import mimeDb from 'mime-db';
import { escapeHtml } from '@/utils/escapeHtml';
import { ehAnexoDeAudio } from '@/utils/anexoAudio';
import {
    Atendente,
    ChatParticipant,
    LastMessage,
    type Chat,
    type Message,
} from '@/mocks/app/mockChatData';
import { AtendimentosService, FiltroAtendimentos, FullInfoAtendimento } from '@/services/atendimentos/atendimentos.service';
import { IdAtendimento, IdBot, IdCanal, IdContato, IdEtiqueta, IdGrupo, IdInbox, IdMensagem, IdQuadro, IdUsuario, makeIdConta, makeIdInbox, makeIdMensagem } from '@/services/common-types';
import { ContasService } from '@/services/contas/contas.service';
import { ContatoEtiquetaService } from '@/services/contatos-etiquetas/contatos-etiquetas.service';
import { GrupoEtiquetaService } from '@/services/grupos-etiquetas/grupos-etiquetas.service';
import { ContatosService, ResponseContato, UpdateContato } from '@/services/contatos/contatos';
import { ResponseEtiqueta } from '@/services/etiquetas/etiquetas.service';
import { DuracaoSilencio, FetchGroupsWrapper, GrupoParticipanteResponseDto, GrupoService } from '@/services/grupos/grupos.service';
import { InboxService, InboxType, InboxWithStatus, ResponseInbox, UpdateInbox, formatoDeAudioDoCanal, type FormatoDeAudioCanal, AUDIO_META_MAX_BYTES } from '@/services/inbox/inbox.service';
import { codecDeAudioMp4, SAMPLE_ENTRY_AAC } from '@/utils/audio/mp4Codec';
import { getDisplayTextForMessageContent, MensagensService, MessageContent, ReceivedReactionContent, RespostaEmParticularEnvio, RespostaEnvioMensagem, WhizMessageAtendimento, WhizMessageSituacao, WhizMessageTipo } from '@/services/mensagens/mensagens.service';
import { IMetaParams, IMetaTemplate } from '@/services/templates-wpp-meta/meta-wpp-template.service';
import { MensagensFixadasService, MensagemFixada } from '@/services/mensagens-fixadas/mensagens-fixadas.service';
import { AnotacoesInternasService, AnotacaoInterna, CriarAnotacaoInternaPayload, ListarAnotacoesInternasParams } from '@/services/anotacoes-internas/anotacoes-internas.service';
import { ResponseTemplate, TipoChavePixTemplate } from '@/services/templates/templates.service';
import { UsuarioAtendimentoService } from '@/services/usuarios-atendimentos/usuarios-atendimentos.service';
import { ResponseUsuario } from '@/services/usuarios/usuarios.service';
import { AtendimentoAtualizadoPayload, AtendimentoCriadoPayload, ConectandoPayload, ConexaoPayload, DesconexaoPayload, GrupoCriadoPayload, GrupoRemovidoPayload, MensagemPayload, PresencaAtualizadaPayload, PresencaChat, QrcodePayload, WebsocketService } from '@/services/websocket/websocket.service';
import { useUiStore } from '@/stores/app/uiStore';
import { PromptToAbrirAtendimento, PromptToAssumirAtendimento, PromptToAssumirAtendimentosEmLote } from '@/components/app/swal/AbrirAtendimento';
import { delay } from '@/utils/delay';
import { ehAtendimentoOcultoPorCampanha, FiltrosAutomacoes, filtrosAutomacoesVazios, normalizarFiltrosAutomacoes, passaFiltroAutomacoes } from '@/utils/campanhaAtendimento';
import { ehAtendimentoEmEspera } from '@/utils/atendimentoEmEspera';
import { excedeTamanhoDeTelefone, formatPhone, normalizarPhone, numeroSemDdiAmbiguo } from '@/utils/phone';
import { nomeExibivel } from '@/utils/nomeExibivel';
import { localizarParticipante, motivoBloqueioRespostaParticular, type RemetenteDeGrupo } from '@/utils/participanteGrupo';
import { defineStore } from 'pinia';
import Swal from 'sweetalert2';
import { converterVideoParaMeta, EtapaDeConversao } from '@/utils/video/converterVideoParaMeta';
import { converterImagemParaMeta, type EtapaDeImagem } from '@/utils/imagem/converterImagemParaMeta';
import { nextTick, watch } from 'vue';
import { toast } from 'vue3-toastify';
import { StorageService } from '../admin/storage';
import { useAuthStoreUser } from './authUser';
import { useLigacoesStore } from './ligacoesStore';
import { useInboxAlertStore } from './inboxAlertStore';
import { useBotsStore } from './botStore';
import { CHAVE_REACAO_PROPRIA, chaveDoReator, DiscriminatedChat, extractInitials, GetReplySnippet, grupoEstaSilenciado, MakeChat, MakeMessage, pastelColorFromString, TextContentUtil, WhizMessagesToMessagesWithReaction } from './chatUtils';
import { useTagStore } from './tagStore';
import { debounce } from '@/utils/debounce';
import { usePermissoesStore } from './permissoesStore';
import { IdReferencia } from '@/shared/perms.config';
import { useSoundStore } from './soundStore';
import { useContatoStore } from './contatoStore';
import { SimpleQueue } from '@/utils/queue';
import { getPromiseWithResolvers } from '@/utils/getPromiseWithResolvers';
import { resolveSelectableMessageIds } from '@/utils/resolveSelectableMessageIds';
import { legendaParaEncaminhar, OpcoesEncaminhamento } from '@/utils/legendaEncaminhamento';
import { ehIdMensagemPersistida } from '@/utils/ehIdMensagemPersistida';
import { idsNaoLidas } from '@/utils/mensagensNaoLidas';
import { reactive } from 'vue';
import { savePendingMessageToDB, deletePendingMessageFromDB, getPendingMessagesFromDB } from '@/utils/pendingMessagesDb'; // Ajuste o caminho conforme onde você salvou
import { GrupoWhitelistService } from '@/services/grupos/grupo-whitelist.service';
import { CanalService } from '@/services/canais/canais.service';
import { FigurinhasService } from '@/services/figurinhas/figurinhas.service';
import { ehCoexistencia, ehPlataformaCompativelComTipoContato, plataformaOcultaMensagemApagada, suportaTemplatesMeta } from '@/utils/plataforma';

/** Uma pessoa digitando numa conversa. `paused` nunca é guardado — apaga a entrada. */
export type PresencaChatEntrada = {
    codigo   : string
    presenca : Exclude<PresencaChat, 'paused'>
    idContato: IdContato | null
    /** Nome vindo do próprio evento (grupo, cache quente no servidor). Ver `resolverNomeQuemDigita`. */
    nome     : string | null
}

/**
 * Rótulo pronto para renderizar, já com a cor pastel do participante em grupo.
 * `texto` é a linha completa (header e prévia da sidebar); `autor` é só o nome, para o
 * balão da conversa, onde a ideia de "escrevendo" já vem dos pontinhos animados.
 * `autor` é `null` em 1:1 — nomear o contato numa conversa de duas pessoas é ruído.
 *
 * `tipo` existe porque o balão da conversa NÃO renderiza `texto` — só o nome e a
 * animação. Sem ele, gravar áudio ficava visualmente idêntico a digitar, já que a
 * distinção só existia dentro da string. Nunca é `'paused'`: esse estado apaga a
 * entrada em vez de virar rótulo.
 */
export type RotuloPresenca = {
    texto: string
    cor  : string | null
    autor: string | null
    tipo : Exclude<PresencaChat, 'paused'>
}

/**
 * Sem `paused` o indicador ficaria preso para sempre: o cliente pode fechar o app,
 * perder rede ou o evento pode morrer num gap do socket. Expiração local é a única
 * garantia — o servidor não mantém timer nenhum.
 */
const PRESENCA_TTL_MS = 8000;

/**
 * Timers de expiração fora do state de propósito: são detalhe de implementação, não
 * estado de UI, e colocá-los no store faria a reatividade disparar a cada rearme.
 */
const presencaTimers = new Map<string, ReturnType<typeof setTimeout>>();

const chavePresenca = (chatId: IdAtendimento|IdGrupo, codigo: string) => `${chatId}|${codigo}`;

/**
 * Quem está digitando, por conversa → por participante.
 *
 * Fora do store de propósito, no mesmo padrão de `globalUploadsState`: é estado
 * puramente efêmero (não persiste, não vai para a API) e manter fora evita empilhar
 * mais acessos a `this` num store cuja inferência de tipo já está degradada.
 */
export const presencaChatState = reactive({
    porChat: new Map<string, Map<string, PresencaChatEntrada>>(),
});

/**
 * Aplica um `PRESENCA_ATUALIZADA`. Nunca cria conversa: presença de chat fora da lista
 * é ruído (o servidor já descarta o que não tem atendimento aberto ou grupo cadastrado).
 * `inboxesDaConta` reproduz a guarda do handler de MENSAGEM — o socket entrega por sala
 * de inbox, e evento de inbox de outra conta não pode pintar esta tela.
 */
export function aplicarPresencaChat(payload: PresencaAtualizadaPayload, inboxesDaConta: { id: string }[]) {
    const chatId = payload.idAtendimento ?? payload.idGrupo;
    if (!chatId || !payload.codigo) return;
    if (!inboxesDaConta.some(inbox => inbox.id === payload.idInbox)) return;

    const chave = chavePresenca(chatId, payload.codigo);
    const timerAnterior = presencaTimers.get(chave);
    if (timerAnterior) {
        clearTimeout(timerAnterior);
        presencaTimers.delete(chave);
    }

    if (payload.presenca === 'paused') {
        limparPresencaChat(chatId, payload.codigo);
        return;
    }

    if (!presencaChatState.porChat.has(chatId)) {
        presencaChatState.porChat.set(chatId, new Map<string, PresencaChatEntrada>());
    }
    // Relê DEPOIS do set: `porChat.get` devolve o Map já embrulhado em proxy pelo
    // Vue, enquanto a referência crua recém-criada não é rastreada. Mutar a crua
    // não dispararia reatividade nenhuma.
    const doChat = presencaChatState.porChat.get(chatId) as Map<string, PresencaChatEntrada>;

    doChat.set(payload.codigo, {
        codigo   : payload.codigo,
        presenca : payload.presenca,
        idContato: payload.idContato ?? null,
        nome     : payload.nome ?? null,
    });

    presencaTimers.set(chave, setTimeout(() => limparPresencaChat(chatId, payload.codigo), PRESENCA_TTL_MS));
}

export function limparPresencaChat(chatId: IdAtendimento|IdGrupo, codigo: string) {
    const chave = chavePresenca(chatId, codigo);
    const timer = presencaTimers.get(chave);
    if (timer) {
        clearTimeout(timer);
        presencaTimers.delete(chave);
    }

    const doChat = presencaChatState.porChat.get(chatId);
    if (!doChat) return;

    doChat.delete(codigo);
    if (doChat.size === 0) presencaChatState.porChat.delete(chatId);
}

/**
 * Apaga a presença de uma conversa inteira. Chamado quando uma MENSAGEM daquela
 * conversa chega: receber a mensagem É o fim do "escrevendo..." — é assim que todo
 * cliente de chat se comporta.
 *
 * Não dá para depender do `paused` aqui. Ele pode se perder num gap de socket (é
 * exatamente por isso que existe o TTL), e mesmo quando chega ele corre contra a
 * mensagem. Sem isto o indicador seguia pulsando ATÉ 8 s embaixo da mensagem que a
 * pessoa acabou de mandar.
 */
export function limparPresencasDoChat(chatId: IdAtendimento|IdGrupo) {
    const doChat = presencaChatState.porChat.get(chatId);
    if (!doChat) return;

    for (const codigo of Array.from(doChat.keys())) {
        const chave = chavePresenca(chatId, codigo);
        const timer = presencaTimers.get(chave);
        if (timer) {
            clearTimeout(timer);
            presencaTimers.delete(chave);
        }
    }

    presencaChatState.porChat.delete(chatId);
}

export function limparTodasPresencasChat() {
    presencaTimers.forEach(timer => clearTimeout(timer));
    presencaTimers.clear();
    presencaChatState.porChat.clear();
}

/**
 * Nome de quem está digitando num grupo. Espelha `resolvedSenderName` de
 * `ChatMessage.vue`: participante por idContato, depois por telefone, e só então o
 * contato global da conta. `null` quando nada resolve — o rótulo cai para o genérico
 * em vez de exibir um número cru.
 */
/**
 * Acrescenta aos parâmetros do operador os valores das variáveis AUTOMÁTICAS que o
 * servidor resolve no envio.
 *
 * Só serve à bolha OTIMISTA: sem isso ela nasceria exibindo `{{nome_contato}}` cru e
 * o texto mudaria debaixo do olho do operador quando o eco chegasse. O eco traz os
 * valores que a Meta realmente recebeu e substitui esta aproximação.
 *
 * Formato POSICIONAL não entra: as variáveis automáticas são nomeadas por definição,
 * então um template posicional nunca as contém.
 */
function paramsComAutomaticas(params: IMetaParams, nomeDoContato?: string | null): IMetaParams {
    if (!params || 'replace' in params) return params;

    // Formato NOMEADO tem pares `[nome, valor]`; o POSICIONAL tem strings soltas. As
    // automáticas são nomeadas por definição, então o posicional sai intocado.
    const corpo = params.body as unknown[];
    const cabecalho = params.header as unknown[];
    if (!corpo.every(v => Array.isArray(v))) return params;

    const nome = nomeExibivel(nomeDoContato);
    if (!nome) return params;

    const pares = corpo as [string, string][];
    const jaTem = (chave: string) => pares.some(([k]) => k === chave);
    const extras: [string, string][] = [];

    for (const chave of ['nome_contato', 'nome_whatsapp'])
        if (!jaTem(chave)) extras.push([chave, nome]);

    if (!extras.length) return params;
    return { header: cabecalho as [string, string][], body: [...pares, ...extras] };
}

function resolverNomeQuemDigita(
    chat: Chat,
    entrada: PresencaChatEntrada,
    contatosByCodigo: Map<string, ResponseContato>,
): string | null {
    const participantes = chat.participants;

    if (participantes?.length) {
        const porId = entrada.idContato
            ? participantes.find(p => p.idContato === entrada.idContato)
            : undefined;
        const doId = nomeExibivel(porId?.name);
        if (doId) return doId;

        const porTelefone = participantes.find(p => p.phone === entrada.codigo);
        const doTelefone = nomeExibivel(porTelefone?.name);
        if (doTelefone) return doTelefone;
    }

    const doStore = nomeExibivel(contatosByCodigo.get(entrada.codigo)?.nome);
    if (doStore) return doStore;

    // Último degrau, análogo ao `message.sender` de `ChatMessage.resolvedSenderName`:
    // o nome que veio no próprio evento. Fica por ÚLTIMO de propósito — com o grupo
    // aberto, `participants` é a mesma fonte que nomeia e colore as bolhas, e trocar
    // a ordem faria o rótulo divergir do que está logo acima na conversa. Serve à
    // linha da lista, onde `participants` sequer foi carregado.
    return nomeExibivel(entrada.nome);
}

/**
 * Rótulo de "escrevendo..." da conversa, ou `null` quando ninguém digita. Em grupo
 * nomeia e colore quem digita com a mesma cor pastel do autor das bolhas, para o
 * operador ligar rótulo e mensagem sem ler.
 */
export function rotuloPresenca(
    chat: Chat | null | undefined,
    contatosByCodigo: Map<string, ResponseContato>,
): RotuloPresenca | null {
    if (!chat) return null;

    const doChat = presencaChatState.porChat.get(chat.id);
    if (!doChat || doChat.size === 0) return null;

    const entradas = Array.from(doChat.values());
    const gravando = (e: PresencaChatEntrada) => e.presenca === 'recording';

    if (!chat.isGroup) {
        const ehAudio = gravando(entradas[0]);
        return {
            texto: ehAudio ? 'gravando áudio...' : 'escrevendo...',
            cor: null,
            autor: null,
            tipo: ehAudio ? 'recording' : 'composing',
        };
    }

    if (entradas.length > 1) {
        // Só vira "gravando" quando TODO mundo está gravando: no lote misto o rótulo
        // de áudio mentiria sobre quem está digitando, e digitar é o caso comum.
        const todosGravando = entradas.every(gravando);
        const quantos = `${entradas.length} pessoas`;
        return {
            texto: `${quantos} estão ${todosGravando ? 'gravando áudio...' : 'escrevendo...'}`,
            cor: null,
            autor: quantos,
            tipo: todosGravando ? 'recording' : 'composing',
        };
    }

    const entrada = entradas[0];
    const ehAudio = gravando(entrada);
    const verbo = ehAudio ? 'gravando áudio...' : 'escrevendo...';
    const tipo: Exclude<PresencaChat, 'paused'> = ehAudio ? 'recording' : 'composing';
    const nome = resolverNomeQuemDigita(chat, entrada, contatosByCodigo);

    if (!nome) return { texto: verbo, cor: null, autor: null, tipo };
    return { texto: `${nome} está ${verbo}`, cor: pastelColorFromString(nome), autor: nome, tipo };
}

export const globalUploadsState = reactive({
    ongoingUploads: [] as { id: string; chatId: string; fileName: string; progress: number }[]
});

/**
 * Uma conversão de mídia (vídeo ou imagem) em andamento, na forma que o cartão
 * flutuante lê.
 *
 * `progress` é 0..100 e vale **0 enquanto não há progresso real** — o core do
 * ffmpeg leva segundos para baixar antes do primeiro quadro, e uma barra parada
 * em 0% parece travada. O cartão trata 0 como "indeterminado" e gira o anel.
 */
export interface ConversaoDeVideoEmAndamento {
    id: string;
    chatId: string | null;
    fileName: string;
    progress: number;
    etapa: EtapaDeConversao | EtapaDeImagem;
    cancelar: () => void;
}

export const globalVideoConversionsState = reactive({
    ongoing: [] as ConversaoDeVideoEmAndamento[]
});

/** Marca que a biblioteca antiga de localStorage já foi enviada ao backend. */
const STICKERS_MIGRATED_KEY = 'whiz_stickers_migrated';

/**
 * Teto de mensagens carregadas na abertura para alcançar a primeira não lida
 * (6 páginas de 50). Grupo com milhares de não lidas não pode travar a abertura
 * baixando o histórico inteiro: passando daqui, o separador fica na mais antiga
 * carregada, com a contagem real.
 */
const LIMITE_MENSAGENS_ATE_PRIMEIRA_NAO_LIDA = 300;

export function makeDummyIdMensagem(): IdMensagem { return makeIdMensagem(`${Date.now()}`) }

export type FiltroModoKanban = {
    inboxIds: IdInbox[]
    agentIds: IdUsuario[]
    tagIds  : IdEtiqueta[]
    botAvaliacao: boolean
};

export type FiltroModoLista = {
    todos:    boolean
    naoLidos: boolean
    /** Só atendimentos em espera (piscando, sem atendente vinculado). */
    emEspera: boolean
    meus:     boolean
    admin:    boolean
    selectedInboxIds  : IdInbox[]
    selectedChannelIds: IdCanal[]
    selectedAgentIds  : IdUsuario[]
    selectedTagIds    : IdEtiqueta[]
    botAvaliacao: boolean
    /** Seção "Automações". Opcional porque filtros persistidos antigos não a têm. */
    automacoes?: FiltrosAutomacoes
};

export type PersistedFilters = { kanban: FiltroModoKanban, list: FiltroModoLista};

/**
 * Mensagem de grupo levada para a conversa particular com quem a enviou
 * ("Responder em particular"), presa ao atendimento de destino.
 *
 * A próxima mensagem enviada nessa conversa a cita via `respostaEmParticular`
 * (ids internos; o servidor monta a citação com o JID do grupo). Quando o canal
 * não consegue anexar a citação, o servidor envia sem ela e devolve
 * `respostaVinculada: false` — ver `handleSendMessage`.
 */
export type RespostaParticular = {
    /** Cópia da mensagem: a lista do grupo é descartada ao trocar de conversa. */
    mensagem: Message;
    nomeRemetente: string;
    idGrupo: IdGrupo;
    nomeGrupo: string;
    idInbox: IdInbox;
};

export type ChatStore = ReturnType<typeof useChatStore>
export function dataURLtoFile(dataurl: string, filename: string): File | null {
    try {
        const arr = dataurl.split(',');
        if (arr.length < 2) return null;

        // Regex segura para pegar o tipo MIME
        const mimeMatch = arr[0].match(/:(.*?);/);
        const mime = mimeMatch ? mimeMatch[1] : 'image/webp';

        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        return new File([u8arr], filename, { type: mime });
    } catch (e) {
        console.error("Erro na conversão dataURLtoFile:", e);
        return null;
    }
}
/**
 * Tentativas de reação em andamento, por mensagem. Fora do `state` de propósito: não é
 * dado de tela, não pode ser reativo nem persistido.
 *
 * `confirmada` é a última reação que o servidor aceitou (ou a que estava na tela quando
 * a primeira tentativa começou); é para ela que uma falha volta. Só a tentativa MAIS
 * NOVA desfaz: com cliques rápidos, a falha de um clique antigo não pode apagar o novo,
 * e a falha do novo não pode devolver um clique antigo que também falhou.
 */
/**
 * Pergunta do `DdiAusenteModal` em aberto. Fora do `state` pelo mesmo motivo acima:
 * função não é dado de tela. `modaisDdiAusenteMontados` evita prometer uma resposta
 * que nenhum modal vai dar (o clique ficaria travado em `inProgressMap`).
 */
let responderDdiPendente: ((escolhido: string | null) => void) | null = null;
let modaisDdiAusenteMontados = 0;

const tentativasDeReacao = new Map<string, { seq: number; seqConfirmada: number; confirmada: string | undefined; pendentes: number }>();

export const useChatStore = defineStore('chat', {
    // 1. STATE: Onde os dados vivem
    state: () => ({
        initialized: false as boolean,
        user: { id: '1', nome: 'Geovana', email: 'geo@whiz.com' } as ResponseUsuario | null,
        contacts: [] as ResponseContato[],
        userAuthStore: useAuthStoreUser(),
        uiStore: useUiStore(),
        soundStore: useSoundStore(),
        localPendingMessages: new Map<string, Message[]>(),
        /** Atendimentos cujo chat está sendo buscado por `_recuperarChatDaMensagem`. */
        _chatsEmRecuperacao: new Set<string>(),

        // Services
        chatService          : new AtendimentosService(),
        groupService         : new GrupoService(),
        groupWhitelistService: new GrupoWhitelistService(),
        contactService       : new ContatosService(),
        contaService         : new ContasService(),
        inboxService         : new InboxService(),
        messageService       : new MensagensService(),
        mensagensFixadasService: new MensagensFixadasService(),
        anotacoesInternasService: new AnotacoesInternasService(),
        storageService       : new StorageService(),
        websocketService     : null as null | WebsocketService,
        isWsConnected        : false as boolean,

        // Maps
        MapContaToUserMap: new Map<string, Map<string, ResponseUsuario>>(), // Mapa que traz os usuário por conta: Conta->UserMap Map
        inboxMap: new Map<string, InboxWithStatus>(),
        inboxesComCanais: new Set<IdInbox>(), // inboxes que possuem ao menos 1 canal cadastrado
        // false enquanto a busca de canais não concluir (ou se falhar): sem esse mapa não é
        // possível saber quais inboxes são "sem canais", então mantemos o comportamento antigo
        // (fila livre) em vez de restringir indevidamente quem trabalha por canal.
        inboxesComCanaisCarregado: false,


        chatList           : [] as Chat[],
        activeChatId       : null as IdGrupo | IdAtendimento | null,
        mensagensFixadas   : [] as MensagemFixada[],
        // Anotações internas ficam FORA de `messages`: aquele array é a fonte de
        // verdade de mensagem (cursor de paginação, não lidas, busca, mapas por id).
        // O merge é só de exibição e acontece no último passo do render.
        anotacoesInternas  : [] as AnotacaoInterna[],
        // Composer de anotação interna. `anotacaoInternaEmEdicao` null = criação.
        isAnotacaoInternaModalOpen: false as boolean,
        anotacaoInternaEmEdicao   : null as { id: string; conteudo: string } | null,
        anotacaoInternaChatAlvo   : null as { id: string; isGroup: boolean } | null,
        messages           : [] as Message[],
        messagePage      : 1 as number,
        messagePageSize  : 50,
        isProfileOpen    : false as boolean,
        isLoadingChats   : false as boolean,
        isLoadingMessages: false as boolean,
        chatToLinkTags   : null as Chat | null,

        kanbanFilters: {
            inboxIds: [] as IdInbox[],
            agentIds: [] as IdUsuario[],
            tagIds  : [] as IdEtiqueta[],
            botAvaliacao: false,
        } as FiltroModoKanban,

        listFilters: {
            todos   : true as boolean,
            naoLidos: false as boolean,
            emEspera: false as boolean,
            meus    : false as boolean,
            admin   : false as boolean,
            botAvaliacao: false as boolean,
            selectedInboxIds  : [] as IdInbox[],
            selectedChannelIds: [] as IdCanal[],
            selectedAgentIds  : [] as IdUsuario[],
            selectedTagIds    : [] as IdEtiqueta[],
            automacoes        : filtrosAutomacoesVazios(),
        } as FiltroModoLista,

        messageToReply      : null as Message | null,
        /**
         * "Responder em particular", indexado pelo atendimento de DESTINO. Por chat, e
         * não um campo único como `messageToReply`, para a referência sobreviver a
         * idas e vindas entre conversas sem aparecer em nenhuma outra.
         */
        respostasParticulares: {} as Record<string, RespostaParticular>,
        messageToEdit       : null as Message | null,
        isForwarding        : false as boolean,
        forwardingMessageIds: [] as string[],
        selectionContext    : null as 'manage' | 'forward' | null,

        isSignatureEnabled  : true as boolean,
        signaturePreferences: {} as Record<string, boolean>,
        chatContext: 'customer' as 'customer' | 'team',

        focusedContactIdForHistory: null as IdContato | null | undefined,
        draftsDict: {} as Record<string, string>,

        // Contatos cujo aviso "número pode não ter WhatsApp" (ChatInput) o
        // operador fechou nesta sessão. Só em memória: recarregar mostra de novo,
        // de propósito — é um aviso real, só não pode bloquear.
        semContaDispensados: {} as Record<string, true>,

        // Texto que outro componente pediu para inserir no ChatInput. O ChatInput
        // observa este campo, acrescenta ao que já estiver digitado e limpa aqui —
        // o input mantém o texto num ref local, então não dá para escrever nele
        // pelo draft.
        textoParaInserirNoInput: null as string | null,

        chatFilter: {
            idsAtendentes: [],
            idsInboxes   : [],
            status       : ['ABERTO', 'INICIADO'],
        } as FiltroAtendimentos,

        initialUnreadCount: 0 as number,

        abortRequestMap: new Map() as Map<string, AbortController>,
        inProgressMap  : new Map() as Map<string, boolean>,

        // Coisinhas para o lendário "clique-em-menção-e-abre-a-conversa-ou-o-modal"
        contactToFocus: null as ResponseContato | null,
        inboxTofocus  : null as IdInbox | null,
        // O modal de novo atendimento não deixa trocar o `inboxTofocus` (ex.: responder
        // em particular tem de sair pelo MESMO inbox do grupo).
        inboxTofocusTravado: false as boolean,
        // Número sem DDI esperando o usuário dizer se é do Brasil (`DdiAusenteModal`)
        ddiAusentePendente: null as { semDdi: string, comDdi: string } | null,

        myStickers           : [] as { id: string; url: string }[],
        recentStickers       : [] as { id: string; url: string }[],
        isStickersInitialized: false,
        isStickersLoading    : false,

        tempProfileChat: null as Chat | null,

        storedNewAtendimentoPromise: null as Promise<boolean>|null,

        selectedInboxIds: [] as string[],
        selectedAgentIds: [] as string[],
        selectedTagIds:   [] as string[],

        chatMapToMsgQueue: new Map() as Map<IdAtendimento|IdGrupo, SimpleQueue>,

        authStore: useAuthStoreUser(),
    }),

    // 2. GETTERS: Computados do store
    getters: {
        /**
         * O usuário pode espiar um atendimento em espera sem assumi-lo?
         *
         * `inbox.ver_previa_atendimentos_em_espera` é opt-in e de escopo inbox:
         * a checagem é por chat, não por conta. Sem ela, o clique num
         * atendimento em espera segue pedindo confirmação antes de abrir.
         */
        podeVerPreviaDeAtendimento(): (chat?: Chat | null) => boolean {
            return (chat) => {
                if (!chat) return false;

                const idConta = this.authStore.currentConta?.id;
                if (!idConta || !chat.idInbox) return false;

                return usePermissoesStore().temPermissao(
                    'inbox.ver_previa_atendimentos_em_espera',
                    idConta,
                    chat.idInbox,
                );
            };
        },

        /**
         * O chat aberto está sendo apenas espiado: em espera, sem atendente
         * vinculado, e o usuário entrou por causa da permissão de prévia.
         *
         * Enquanto isto for verdade o atendimento NÃO pode ser marcado como
         * lido — zerar o badge aqui apagaria o "não lido" da fila para o time
         * inteiro e o atendimento pareceria já atendido.
         */
        estaEmPreviaDeAtendimento(): boolean {
            const chat = this.activeChat;
            if (!chat || chat.isGroup) return false;
            if (chat.status !== 'Criado') return false;

            return this.podeVerPreviaDeAtendimento(chat);
        },

        currentAccountInboxWithOfficialWppIntegration(): ResponseInbox[] {
            return this.authStore.currentAccountInboxWithOfficialWppIntegration
        },

        currentAccountHasOfficialWppIntegration(): boolean {
            return this.authStore.currentAccountHasOfficialWppIntegration
        },

        currentAccountHasAtLeastOneInboxWithAiAgent(): boolean {
            return this.authStore.currentAccountHasAtLeastOneInboxWithAiAgent
        },

        /**
         * O atendimento está sendo CONDUZIDO pelo Agente de IA agora.
         *
         * Mesmo predicado que `AIChatList` usa para montar a aba "Atendimentos
         * com IA": tem chat de bot vinculado E a integração ainda não passou a
         * régua para o humano. `statusIntegracaoIa` preenchido (notadamente
         * 'EM_ATENDIMENTO_HUMANO') significa que o agente já saiu de cena, e aí
         * o atendimento volta a ser um atendimento comum.
         *
         * Centralizado aqui porque as três permissões do Agente de IA e a lista
         * da aba precisam concordar sobre o que é "conduzido pela IA" — quando
         * a definição morava copiada em cada componente, ChatHeader e ChatInput
         * já divergiram entre si.
         */
        chatEhConduzidoPorIa(): (chat?: Chat | null) => boolean {
            return (chat) => !!chat?.idChatBotIa && !chat?.statusIntegracaoIa;
        },

        /**
         * Checagem de permissão de escopo `inbox` para um chat específico.
         *
         * As três permissões do Agente de IA são por inbox, e a lista mistura
         * inboxes: perguntar "tem a permissão?" sem dizer em qual inbox daria a
         * resposta errada para metade dos cards.
         */
        temPermissaoNoInboxDoChat(): (perm: IdReferencia, chat?: Chat | null) => boolean {
            return (perm, chat) => {
                if (!chat) return false;

                const idConta = this.authStore.currentConta?.id;
                if (!idConta || !chat.idInbox) return false;

                return usePermissoesStore().temPermissao(perm, idConta, chat.idInbox);
            };
        },

        /**
         * Pode agir sobre um atendimento conduzido pelo Agente de IA: menu de
         * ações do card na lista, menu do cabeçalho do chat e seleção nas Ações
         * em Lote.
         *
         * Devolve `true` para atendimento que NÃO é da IA — a permissão recorta
         * só o que o agente conduz; o menu de um atendimento comum nunca
         * dependeu dela e não pode passar a depender.
         */
        podeIntervirNoAtendimentoDoAgente(): (chat?: Chat | null) => boolean {
            return (chat) => {
                if (!this.chatEhConduzidoPorIa(chat)) return true;
                return this.temPermissaoNoInboxDoChat('inbox.intervir_atendimentos_agente', chat);
            };
        },

        /** Pode interromper o Agente de IA e assumir a conversa manualmente. */
        podeInterromperFluxoAutomatizado(): (chat?: Chat | null) => boolean {
            return (chat) =>
                this.temPermissaoNoInboxDoChat('inbox.interromper_fluxo_automatizado', chat);
        },

        /**
         * Tem a permissão de acompanhar atendimentos do agente em ALGUM inbox da
         * conta corrente.
         *
         * A entrada "Atendimentos com IA" do menu lateral não é de um inbox
         * específico — ela abre a lista da conta inteira —, então o recorte certo
         * é "em algum", como já faz `podeConfigurarAgenteIa` na NavBarLeftUser.
         */
        podeAcompanharAtendimentosDoAgente(): boolean {
            const idConta = this.authStore.currentConta?.id;
            if (!idConta) return false;

            const permissoesStore = usePermissoesStore();
            return this.authStore.currentAccountInboxes.some((inbox) =>
                permissoesStore.temPermissao(
                    'inbox.acompanhar_atendimentos_agente',
                    idConta,
                    inbox.id,
                ),
            );
        },

        /**
         * Pode ver o que o Agente de IA fez neste inbox.
         *
         * `acompanhar_atendimentos_agente` é a permissão de OBSERVAR o agente — a
         * mesma que libera "Atendimentos com IA" no menu. Quem não a tem não vê o
         * agente em lugar nenhum, e as linhas que ele escreveu na timeline seriam a
         * única exceção: anotação interna roxa e registro de histórico anunciando um
         * Agente de IA que, para aquele operador, não existe.
         *
         * Recebe o inbox porque a permissão é de escopo inbox e nem todo consumidor
         * está olhando o chat aberto — a modal de histórico abre por contexto, com o
         * seu próprio par contato/inbox.
         */
        podeVerRastroDoAgente(): (idInbox?: string | null) => boolean {
            return (idInbox) => {
                const idConta = this.authStore.currentConta?.id;
                if (!idConta || !idInbox) return false;

                return usePermissoesStore().temPermissao(
                    'inbox.acompanhar_atendimentos_agente',
                    idConta,
                    idInbox,
                );
            };
        },

        /**
         * Anotações internas que este operador pode ver.
         *
         * Filtra aqui, e não na renderização: a lista crua também alimenta contagem
         * e navegação, e um item invisível na tela mas presente no array volta a
         * aparecer por outra porta.
         */
        anotacoesInternasVisiveis(): AnotacaoInterna[] {
            if (this.podeVerRastroDoAgente(this.activeChat?.idInbox)) return this.anotacoesInternas;
            return this.anotacoesInternas.filter(a => a.tipo !== 'AGENTE_IA');
        },

        /**
         * Aplica as regras RÍGIDAS da Cloud API ao chat aberto: janela de 24h,
         * composer bloqueado fora dela, sem editar/apagar mensagem.
         *
         * Vale para WHATSAPP_META sempre, e para coexistência apenas quando a
         * metade Evolution está indisponível — com o QR de pé o operador manda
         * texto livre normalmente.
         */
        inboxOfSelectedChatIsOfficialWpp(): boolean {
            if (!this.activeChatId || !this.activeChat?.idInbox) return false;
            const inbox = this.inboxMap.get(this.activeChat.idInbox);
            if (!inbox) return false;
            if (inbox.plataforma == 'WHATSAPP_META') return true;
            if (inbox.plataforma == 'WHATSAPP_HYBRID_COEXISTENCE') return inbox.evolutionConectado !== true;
            return false;
        },

        /**
         * O inbox do chat aberto tem metade Cloud API — logo, templates oficiais
         * estão disponíveis. Diferente de `inboxOfSelectedChatIsOfficialWpp`:
         * aqui a pergunta é sobre CAPACIDADE, não sobre restrição.
         */
        inboxOfSelectedChatSuportaTemplatesMeta(): boolean {
            if (!this.activeChatId || !this.activeChat?.idInbox) return false;
            return suportaTemplatesMeta(this.inboxMap.get(this.activeChat.idInbox)?.plataforma);
        },

        /**
         * Coexistência com a metade Evolution fora do ar: o inbox continua
         * recebendo pela oficial, mas o envio livre deixa de ser garantido.
         */
        inboxOfSelectedChatComQrCaido(): boolean {
            if (!this.activeChatId || !this.activeChat?.idInbox) return false;
            const inbox = this.inboxMap.get(this.activeChat.idInbox);
            return inbox?.plataforma == 'WHATSAPP_HYBRID_COEXISTENCE' && inbox.evolutionConectado !== true;
        },

        /** O inbox do chat aberto é híbrido (as duas conexões no mesmo número). */
        inboxOfSelectedChatEhHibrido(): boolean {
            if (!this.activeChatId || !this.activeChat?.idInbox) return false;
            return this.inboxMap.get(this.activeChat.idInbox)?.plataforma == 'WHATSAPP_HYBRID_COEXISTENCE';
        },

        /**
         * A janela de atendimento de 24h (CSW) está aberta neste chat?
         *
         * Diferente de `currentChatHasOpenInteractionWindow`, este getter NÃO tem
         * atalho por plataforma: ele só olha a última interação do cliente. Isso
         * importa porque num inbox híbrido saudável `inboxOfSelectedChatIsOfficialWpp`
         * é `false` (existe a metade Evolution), então o getter antigo devolvia
         * `true` de saída e a janela nunca era avaliada — e reagir/responder pela
         * Cloud API depende dela.
         */
        cswAberta(): boolean {
            const lastInteractionMs = new Date(this.activeChat?.lastClientInteraction ?? 0).getTime()
            const currentMs = Date.now();
            const _oneMinuteTolerance = 60 * 1000;
            const _interactionWindowMs = 24 * 3600 * 1000;
            const interactionWindowInsideTolerance = _interactionWindowMs - _oneMinuteTolerance;

            return lastInteractionMs + interactionWindowInsideTolerance > currentMs;
        },

        /**
         * Janela para efeito de BLOQUEIO DO COMPOSER: só restringe onde a Cloud
         * API é o único transporte. Onde há metade Evolution o texto livre sai por
         * ela, independentemente da janela.
         */
        currentChatHasOpenInteractionWindow(): boolean {
            if (!this.inboxOfSelectedChatIsOfficialWpp) return true;
            return this.cswAberta;
        },

        /**
         * Que templates o menu rápido ("/" no composer) pode oferecer neste chat.
         *
         * Inbox com metade Cloud API (inclusive coexistência híbrida): com a janela
         * de 24h ABERTA cabem os dois mundos — texto livre sai, então o template
         * Whiz também; com a janela FECHADA só o template oficial aprovado passa.
         * Sem metade Cloud API, só template Whiz.
         *
         * O gate NÃO é `inboxOfSelectedChatIsOfficialWpp` de propósito: num híbrido
         * saudável aquele getter é `false`, e era por isso que o menu ignorava os
         * templates oficiais (e não abria quando a conta só tinha templates Meta).
         */
        modoTemplatesRapidos(): 'SO_WHIZ' | 'SO_META' | 'META_E_WHIZ' {
            if (!this.inboxOfSelectedChatSuportaTemplatesMeta) return 'SO_WHIZ';
            return this.cswAberta ? 'META_E_WHIZ' : 'SO_META';
        },

        connectedInboxesStatus(): InboxWithStatus[] {
            return Array.from(this.inboxMap.values())
        },

        monoUserMap(): Map<string, ResponseUsuario> {
            const userMaps = Array.from(this.MapContaToUserMap.values());
            return new Map(userMaps.flatMap(userMap => Array.from(userMap.entries())))
        },

        activeChat(state): Chat | null {
            if (!state.activeChatId) return null;
            return state.chatList.find(chat => chat.id === state.activeChatId) || null;
        },

        activeChatType(): InboxType | null {
            const idInboxChatAtivo = this.activeChat?.idInbox;
            if (!idInboxChatAtivo) return null;
            return this.userAuthStore.inboxesFull.find(i => i.id == idInboxChatAtivo)?.plataforma as InboxType;
        },

        isGroupChat(): boolean {
            return !!this.activeChat?.isGroup;
        },

        /** Referência de "Responder em particular" da conversa aberta, se houver. */
        respostaParticularAtiva(state): RespostaParticular | null {
            if (!state.activeChatId || state.chatContext === 'team') return null;
            return state.respostasParticulares[state.activeChatId] ?? null;
        },

        // Getter para saber o nome do autor da mensagem (para o 'replyOwner')
        getMessageOwnerName(): (message: Message) => string {
            return (message: Message) => {
                if (message.isSent) {
                    return 'Você'; // Você pode mudar isso para o nome do usuário logado
                }
                if (message.isBot) {
                    return 'Bot';
                }
                // Se for recebida, pega o nome do contato ativo
                if (this.isGroupChat) {
                    return message.sender || 'Contato';
                } else {
                    return this.activeChat?.name || 'Contato';
                }
            }
        },

        favoritedMessages(state): Message[] {
            return state.messages.filter(message => message.isFavorite);
        },

        activePinRecord(): MensagemFixada | null {
            return this.mensagensFixadas[0] || null;
        },

        activePinnedMessage(state): Message | null {
            const firstPin = this.mensagensFixadas[0];
            if (!firstPin) return null;

            const loaded = state.messages.find(m => m.id === firstPin.idMensagem);
            if (loaded) return loaded;

            // Synthetic fallback from pin metadata when message not in window
            return {
                id:         firstPin.idMensagem,
                idExterno:  '',
                text:       firstPin.texto || undefined,
                type:       firstPin.tipo as Message['type'],
                isSent:     false,
                isBot:      false,
                readStatus: null,
                time:       '',
            } as Message;
        },

        isTeamMode(state): boolean {
            return state.chatContext === 'team';
        },

        ongoingAtendimentosByContatoByInboxMap() {
            if (!this.userAuthStore.initialized || this.userAuthStore.inboxesFull.length == 0)
                return new Map() as Map<IdInbox, Map<IdContato, IdAtendimento>>;

            const source: [IdInbox, Map<IdContato, IdAtendimento>][] = this.userAuthStore?.inboxesFull?.map(i => [i.id, new Map() as Map<IdContato, IdAtendimento>]) ?? [];
            const map = new Map(source);
            const ongoingAtendimentos = this.chatList.filter(c => c.status != 'Finalizado');
            for (const atendimento of ongoingAtendimentos) {
                const foundMap = map.get(atendimento.idInbox);
                if (!foundMap) continue;
                if (!atendimento.idContato) continue;
                foundMap.set(atendimento.idContato, atendimento.id as IdAtendimento);
            }
            return map;
        },

        filteredChats(state): Chat[] {
            // 1. Armazenamos o resultado dos filtros
            const filtered = state.chatList.filter(chat => {
                // Filtro de Inbox (Instâncias)
                if (state.listFilters.selectedInboxIds.length > 0 && !state.listFilters.selectedInboxIds.includes(chat.idInbox)) {
                    return false;
                }

                // Filtro de Canal
                if (!chat.isGroup && state.listFilters.selectedChannelIds.length > 0 && (!chat.idCanal || !state.listFilters.selectedChannelIds.includes(chat.idCanal))) {
                    return false;
                }

                // Filtro de Agentes (Equipe)
                // Filtro de Agentes: combina "meus" e "selectedAgentIds" com OR
                if (!chat.isGroup) {
                    const filtroMeus = state.listFilters.meus;
                    const filtroAgentes = state.listFilters.selectedAgentIds.length > 0;

                    if (filtroMeus || filtroAgentes) {
                        const naoIniciado = chat.status === 'Criado' || chat.atendentes.length === 0;
                        const passaMeus = filtroMeus && !!state.user && chat.atendentes.some(a => a.id === state.user?.id);
                        const passaAgentes = filtroAgentes && chat.atendentes.some(a => state.listFilters.selectedAgentIds.includes(a.id));

                        if (!naoIniciado && !passaMeus && !passaAgentes) return false;
                    }
                }

                // Filtro de Etiquetas
                if (state.listFilters.selectedTagIds.length > 0) {
                    const hasTag = chat.tags.some(t => state.listFilters.selectedTagIds.includes(t.id));
                    if (!hasTag) return false;
                }

                // Automações — atendimento criado por campanha fica oculto até que se
                // escolha a campanha dele no seletor. Ver `passaFiltroAutomacoes`.
                if (!passaFiltroAutomacoes(chat, state.listFilters.automacoes)) return false;

                // Filtro de Bot de Avaliação
                // Só considera bots do tipo AVALIACAO; outros tipos de bot não afetam este filtro
                const botStore = useBotsStore();
                const isBotAvaliacao = !!chat.idBotAtivo && botStore.bots.some(b => b.id === chat.idBotAtivo && b.tipo === 'AVALIACAO');
                // - filtro inativo (padrão): oculta atendimentos com bot de avaliação ativo
                // - filtro ativo: exibe todos, incluindo os com bot de avaliação ativo
                if (!state.listFilters.botAvaliacao && isBotAvaliacao) return false;

                // // Filtro "Meus Atendimentos"
                // if (!chat.isGroup && state.listFilters.meus) {
                //     if (!state.user || !chat.atendentes.some(a => a.id === state.user?.id)) return false;
                // }

                // Filtro "Não Lidos" — coalesce contador + marcação manual
                if (state.listFilters.naoLidos && chat.unreadCount === 0 && !chat.isMarkedUnread) {
                    return false;
                }

                // Filtro "Em espera" — só a fila: atendimento que pisca, sem atendente
                // vinculado. Ver `ehAtendimentoEmEspera`.
                if (state.listFilters.emEspera && !ehAtendimentoEmEspera(chat)) {
                    return false;
                }

                return true;
            });

            // 2. Ordenamos o array final
            return filtered.sort((a, b) => {
                // A prioridade #1 é: Fixados vem primeiro
                if (a.isPinned && !b.isPinned) return -1;
                if (!a.isPinned && b.isPinned) return 1;

                // Prioridade #2: Ordenar pelo mais recente se o status "pinado" for igual
                const timeA = a.time ? new Date(a.time).getTime() : 0;
                const timeB = b.time ? new Date(b.time).getTime() : 0;
                return timeB - timeA;
            });
        },

        chatsById(): Map<IdAtendimento|IdGrupo, Chat> {
            return new Map(this.chatList.map(c => [c.id, c]));
        },

        filteredChatsWItemType(): DiscriminatedChat[] {
            return this.filteredChats.map(f => ({ ...f, ...{ itemType: f.isGroup ? 'group' : 'chat' } }))
        },

        messagesById(): Map<string, Message> {
            return new Map(this.messages.map(m => [m.id, m]))
        },

        messagesByIdExterno(): Map<string, Message> {
            return new Map(this.messages.map(m => [m.idExterno, m]))
        },

        contatosById(): Map<string, ResponseContato> {
            return new Map(this.contacts.map(c => [c.id, c]))
        },

        contatosByCodigo(): Map<string, ResponseContato> {
            return new Map(this.currentAccountContatos.map(c => [c.codigo, c]))
        },

        currentAccountContatos(): ResponseContato[] {
          const idConta = this.userAuthStore.currentConta?.id;

          if (!idConta) return [];

          return this.contacts.filter(c => c.idConta == idConta);
        },

        chatForSidebar(state): Chat | null {
            return state.tempProfileChat || this.activeChat;
        },

        async currentAccountUsuarios(): Promise<ResponseUsuario[]> {
            const newAccountId = this.userAuthStore.currentConta?.id
            if (!newAccountId) return [];

            const agentes = await new ContasService().getUsuariosVinculados(newAccountId);
            const currentUserIdx = agentes.findIndex(u => u.id == this.userAuthStore.user?.id);

            if (currentUserIdx >= 0) {
                const [currentUser] = agentes.splice(currentUserIdx, 1);
                agentes.unshift(currentUser);
            }

            return agentes
        }

    },

    actions: {
        //#region 🗃️ Filtros
        addPendingMessage(chatId: string, message: Message) {
            if (!this.localPendingMessages.has(chatId)) {
                this.localPendingMessages.set(chatId, []);
            }
            this.localPendingMessages.get(chatId)?.push(message);

            // Salva no banco offline para sobreviver ao F5
            void savePendingMessageToDB(chatId, message);

            if (this.activeChatId === chatId) {
                this.messages.push(message);
            }
        },

        removePendingMessage(chatId: string, messageId: string) {
            const pending = this.localPendingMessages.get(chatId);
            if (pending) {
                this.localPendingMessages.set(chatId, pending.filter(m => m.id !== messageId));
            }
            // Limpa do banco offline pois já enviou
            void deletePendingMessageFromDB(messageId);
        },

        markPendingMessageAsError(chatId: string, messageId: string) {
            const pending = this.localPendingMessages.get(chatId);
            if (pending) {
                const msg = pending.find(m => m.id === messageId);
                if (msg) {
                    msg.uploadError = true;
                    msg.readStatus = 'error';
                    // Atualiza no banco offline com o status de erro
                    void savePendingMessageToDB(chatId, msg);
                }
            }
            if (this.activeChatId === chatId) {
                const idx = this.messages.findIndex(m => m.id === messageId);
                if (idx !== -1) {
                    this.messages[idx].uploadError = true;
                    this.messages[idx].readStatus = 'error';
                }
            }
        },
        setupFilters() {
            this.recoverFiltros();
            watch(() => this.listFilters, () => this.persistFiltros(), { immediate: true, deep: true });
            watch(() => this.kanbanFilters, () => this.persistFiltros(), { immediate: true, deep: true });
        },

        /**
         * Zera todos os filtros da lista, inclusive a seção Automações.
         *
         * Existe para o caso "quero ver este atendimento agora": ao pular de um envio
         * de campanha para a conversa, selecionar um chat que os filtros escondem
         * abriria a thread com a lista ao lado sem a linha correspondente — parece bug.
         *
         * `idAtendimentoParaRevelar` cobre a armadilha da seção Automações, cuja regra é
         * invertida: um atendimento criado por campanha fica escondido justamente quando
         * NÃO há campanha selecionada. Zerar tudo o esconderia — então, para esse caso,
         * o filtro de campanha é reposto apontando para a campanha dele.
         */
        limparFiltrosDaLista(idAtendimentoParaRevelar?: string) {
            this.listFilters.naoLidos = false;
            this.listFilters.emEspera = false;
            this.listFilters.meus = false;
            this.listFilters.admin = false;
            this.listFilters.botAvaliacao = false;
            this.listFilters.selectedInboxIds = [];
            this.listFilters.selectedChannelIds = [];
            this.listFilters.selectedAgentIds = [];
            this.listFilters.selectedTagIds = [];
            this.listFilters.automacoes = filtrosAutomacoesVazios();

            if (!idAtendimentoParaRevelar) return;
            const alvo = this.chatList.find(c => c.id === idAtendimentoParaRevelar);
            if (alvo && ehAtendimentoOcultoPorCampanha(alvo) && alvo.disparo) {
                this.listFilters.automacoes.idCampanha = alvo.disparo.idDisparo;
            }
        },

        _makePersistedFiltroKey() {
            return `persisted-filter-${this.userAuthStore.currentConta?.id}-${this.userAuthStore.user?.id}`;
        },

        persistFiltros() {
            void debounce(() => {
                const key = this._makePersistedFiltroKey();
                const filters = { list: this.listFilters, kanban: this.kanbanFilters } as PersistedFilters;
                const filterString = JSON.stringify(filters);
                localStorage.setItem(key, filterString)
            }, 100, 'persist-filtros');
        },

        recoverFiltros() {
            const key = this._makePersistedFiltroKey();
            const filterString = localStorage.getItem(key);
            if (!filterString) return;
            const { kanban: kanbanFilters, list: listFilters } = JSON.parse(filterString) as PersistedFilters;
            this.listFilters = listFilters;
            this.kanbanFilters = kanbanFilters;
            this.kanbanFilters.tagIds = [];
            this.listFilters.selectedTagIds = [];
            // Filtros gravados antes da seção Automações não têm o bloco, e os
            // gravados antes de "Todas as campanhas" têm o bloco sem o campo novo.
            // Sem este preenchimento os v-model do ListHeader escreveriam em undefined.
            this.listFilters.automacoes = normalizarFiltrosAutomacoes(this.listFilters.automacoes);
            // Mesmo motivo do bloco acima: filtros gravados antes do "Em espera" não
            // têm o campo, e o v-model do ListHeader escreveria em undefined.
            this.listFilters.emEspera = this.listFilters.emEspera ?? false;
        },

        //#endregion
        // #region 📝 Drafts
        _makePersistedDraftsKey(idUsuario: IdUsuario) {
            return `__drafts-${idUsuario}`
        },

        setupDrafts(idUsuario: IdUsuario) {
            const key = this._makePersistedDraftsKey(idUsuario);
            const maybeJson = localStorage.getItem(key) || "null";
            this.draftsDict = (JSON.parse(maybeJson) as Record<string,string>|null) || {};
        },

        setDraft(chatId: string, text: string | null) {
            if (!chatId) return;

            const userId = this.userAuthStore.user?.id;
            if (!userId) throw new Error('[ChatStore] [setDraft] this.userAuthStore.user?.id was undefined.')
            const key = this._makePersistedDraftsKey(userId);

            if (text && text.trim().length > 0) {
                this.draftsDict[chatId] = text;
            } else {
                delete this.draftsDict[chatId];
            }
            localStorage.setItem(key, JSON.stringify(this.draftsDict))
        },

        /** Pede ao ChatInput que insira este texto no que o atendente já digitou. */
        inserirTextoNoInput(texto: string) {
            this.textoParaInserirNoInput = texto;
        },

        limparTextoParaInserirNoInput() {
            this.textoParaInserirNoInput = null;
        },
        //#endregion
        // #region 🔈 Sons
        setupSounds(idUsuario: IdUsuario) {
            this.soundStore.getSoundPreferences(idUsuario);
        },
        // #endregion
        openParticipantProfile(codigo: string, pushName: string) {
            const contact = this.contatosByCodigo.get(codigo);
            let avatarUrl = undefined;

            // Busca avatar do grupo se disponível
            if (this.activeChat && this.activeChat.participants) {
                const participant = this.activeChat.participants.find(p => p.phone === codigo);
                if (participant && participant.avatar) {
                    avatarUrl = participant.avatar;
                }
            }

            // Busca se já existe chat privado
            let existingPrivateChat: Chat | undefined;
            if (contact) {
                existingPrivateChat = this.chatList.find(c =>
                    !c.isGroup && c.idContato === contact.id
                );
            }

            // CRIA O MOCK
            const mockChat: Chat & { _original?: any } = {
                id: (existingPrivateChat?.id || (contact?.id ? `temp_${contact.id}` : `temp_${codigo}`)) as IdAtendimento,

                idContato: contact?.id, // Pode ser undefined inicialmente
                name: contact?.nome || existingPrivateChat?.name || pushName,
                avatar: avatarUrl || existingPrivateChat?.avatar || contact?.imagem || '',

                // --- CORREÇÃO IMPORTANTE AQUI ---
                // Usa o 'codigo' passado por parâmetro se o contato não existir ainda
                codigoContato: contact?.codigo || codigo,
                // -------------------------------

                unreadCount: existingPrivateChat?.unreadCount || 0,
                tags: existingPrivateChat?.tags || [],
                atendentes: existingPrivateChat?.atendentes || [],

                isMuted: false,
                isPinned: false,
                isFavorite: false,
                isGroup: false, // Força false para a sidebar saber que é perfil de pessoa
                status: existingPrivateChat?.status || 'Aberto',

                idInbox: this.activeChat?.idInbox || makeIdInbox(''),
                lastMessage: { sender: null, text: '', type: 'text', status: 'read' },
                time: new Date().toISOString(),
                _original: contact as ResponseContato | Chat
            };

            this.tempProfileChat = mockChat;
            this.isProfileOpen = true;
        },
        // Carrega biblioteca e recentes do backend. A persistência é por usuário
        // (sobrevive a troca de browser) — antes vivia em localStorage, que
        // estourava a cota de 5MB com figurinhas em base64 e sumia no reload.
        async initStickers() {
            if (this.isStickersInitialized || this.isStickersLoading) return;

            this.isStickersLoading = true;
            try {
                await this.migrateLocalStickers();
                await this.fetchStickers();
                this.isStickersInitialized = true;
            } catch (e) {
                console.error('Erro ao carregar figurinhas', e);
            } finally {
                this.isStickersLoading = false;
            }
        },

        async fetchStickers() {
            const { biblioteca, recentes } = await new FigurinhasService().listar();
            this.myStickers = biblioteca.map(f => ({ id: f.id, url: f.url }));
            this.recentStickers = recentes.map(f => ({ id: f.id, url: f.url }));
        },

        // Envia uma única vez ao backend o que já estava salvo em localStorage.
        // Itens inválidos (blob: expirado, URL morta) são descartados em silêncio.
        async migrateLocalStickers() {
            if (localStorage.getItem(STICKERS_MIGRATED_KEY)) return;

            const savedLibrary = localStorage.getItem('whiz_my_stickers');
            let legacyStickers: string[] = [];

            if (savedLibrary) {
                try {
                    const parsed = JSON.parse(savedLibrary) as unknown;
                    if (Array.isArray(parsed)) legacyStickers = parsed.filter((item): item is string => typeof item === 'string');
                } catch (e) {
                    console.error('Erro ao ler figurinhas antigas para migração', e);
                }
            }

            const service = new FigurinhasService();

            // Sequencial de propósito: são poucos itens e cada um faz upload.
            for (const sticker of legacyStickers.reverse()) {
                try {
                    if (sticker.startsWith('data:')) {
                        const file = dataURLtoFile(sticker, `sticker-${crypto.randomUUID()}.webp`);
                        if (file) await service.criarPorArquivo(file);
                    } else if (/^https?:/.test(sticker)) {
                        await service.adicionarPorUrl(sticker);
                    }
                    // blob: é sempre inválido entre sessões — ignora.
                } catch (e) {
                    console.warn('Figurinha antiga não pôde ser migrada e foi descartada.', e);
                }
            }

            localStorage.setItem(STICKERS_MIGRATED_KEY, '1');
            localStorage.removeItem('whiz_my_stickers');
            localStorage.removeItem('whiz_recent_stickers');
        },

        // Salva uma figurinha recebida na biblioteca. O download do arquivo é
        // feito pelo servidor — o navegador nunca busca a URL de origem, o que
        // evita o bloqueio cross-origin do Chrome.
        async addStickerToLibrary(url: string) {
            if (url.startsWith('blob:')) {
                return { success: false, message: 'Não foi possível salvar esta figurinha (arquivo expirado).' };
            }

            try {
                const figurinha = await new FigurinhasService().adicionarPorUrl(url);
                if (!this.myStickers.some(s => s.id === figurinha.id)) {
                    this.myStickers.unshift({ id: figurinha.id, url: figurinha.url });
                }
                return { success: true, message: 'Figurinha adicionada à biblioteca!' };
            } catch (e) {
                const message = (e as { message?: string })?.message || 'Não foi possível salvar esta figurinha.';
                return { success: false, message };
            }
        },

        // Cria uma figurinha a partir de um arquivo enviado pelo usuário
        async createStickerFromFile(file: File) {
            try {
                const figurinha = await new FigurinhasService().criarPorArquivo(file);
                if (!this.myStickers.some(s => s.id === figurinha.id)) {
                    this.myStickers.unshift({ id: figurinha.id, url: figurinha.url });
                }
                return { success: true, message: 'Figurinha criada!' };
            } catch (e) {
                const message = (e as { message?: string })?.message || 'Erro ao criar figurinha.';
                return { success: false, message };
            }
        },

        // Remove da biblioteca
        async deleteSticker(id: string) {
            const previousLibrary = [...this.myStickers];
            const previousRecent = [...this.recentStickers];

            this.myStickers = this.myStickers.filter(s => s.id !== id);
            this.recentStickers = this.recentStickers.filter(s => s.id !== id);

            try {
                await new FigurinhasService().remover(id);
            } catch (e) {
                console.error('Erro ao remover figurinha', e);
                this.myStickers = previousLibrary;
                this.recentStickers = previousRecent;
                throw e;
            }
        },

        // Registra o uso de uma figurinha (alimenta a aba "Recentes")
        async trackStickerUsage(url: string) {
            if (!url || url.startsWith('blob:') || url.startsWith('data:')) return;

            try {
                const figurinha = await new FigurinhasService().registrarUso(url);
                const listWithoutCurrent = this.recentStickers.filter(s => s.id !== figurinha.id);
                this.recentStickers = [{ id: figurinha.id, url: figurinha.url }, ...listWithoutCurrent].slice(0, 30);
            } catch (e) {
                console.error('Erro ao registrar uso da figurinha', e);
            }
        },
        async retryFailedMessage(message: Message) {
            // Mensagem que chegou ao servidor (id UUID) já tem linha FALHOU no banco: o
            // backend remonta o envio a partir dela e reaproveita a mesma linha — senão
            // cada clique criava outra e a mensagem aparecia duplicada ao recarregar.
            // Id provisório = nunca persistiu, reenvia do zero.
            const idMensagemReenvio = ehIdMensagemPersistida(message.id) ? message.id : undefined;

            // 1. Tipo sem reenvio (figurinha, lista/PIX, contato, template Meta, áudio):
            // avisa e NÃO mexe na bolha — antes ela era removida da tela antes dos `if`
            // e sumia sem nenhuma requisição ter saído.
            if (!TIPOS_REENVIAVEIS.includes(message.type)) {
                toast.warning('Este tipo de mensagem não pode ser reenviado. Envie uma nova mensagem.');
                return;
            }
            // Texto vazio: `handleSendMessage` sairia por `hasContent` sem mandar nada — e a
            // bolha já teria sido apagada. Caso real: a assinatura (`*Nome:*`) é mensagem
            // separada e, sem a assinatura, o texto dela fica vazio.
            if (message.type === 'text' && !message.text?.trim()) {
                toast.warning('Esta mensagem não tem conteúdo para reenviar.');
                return;
            }

            // Arquivo: persistida reenvia pela URL já gravada (o backend ignora o corpo,
            // então não precisa subir de novo nem do arquivo original — vale após recarregar).
            // Só a que nunca persistiu depende do arquivo guardado na RAM.
            const urlGravada = message.fileData?.url && /^https?:/.test(message.fileData.url) ? message.fileData.url : undefined;
            const arquivoParaReenvio = (idMensagemReenvio && urlGravada)
                || message.fileData?.originalFile
                || (idMensagemReenvio ? message.fileData?.url : undefined);
            if (['file', 'imagem', 'video'].includes(message.type) && !arquivoParaReenvio) {
                toast.warning('Não foi possível reenviar. O arquivo original foi perdido.');
                return;
            }

            const chatId = this.activeChatId;
            if (!chatId) return;

            // O composer não pode interferir no reenvio: com uma EDIÇÃO em curso, a
            // estratégia de texto editaria a mensagem em edição com o texto desta e sairia
            // sem reenviar; com uma CITAÇÃO em curso, o reenvio consumiria o rascunho do
            // operador. A edição é descartada (como nas outras ações de envio); a citação
            // é guardada e devolvida ao composer no fim.
            this.clearEditMessage();
            const citacaoEmRascunho = this.messageToReply;
            this.clearReplyMessage();
            // Idem para "Responder em particular" pendente no composer: sem isto o
            // reenvio citaria a mensagem do grupo que o operador ainda vai responder.
            const referenciaDoGrupoEmRascunho = this.respostasParticulares[chatId];
            this.limparRespostaParticular(chatId);
            // A citação do grupo que ESTA mensagem levava volta com ela. Persistida, o
            // servidor a remonta da própria linha; aqui vale para a que nunca persistiu.
            const respostaParticularReenvio = respostaParticularDaBolha(message);

            // 2. Remove a mensagem falha antiga da tela e da fila local
            this.messages = this.messages.filter(m => m.id !== message.id);
            this.removePendingMessage(chatId as string, message.id);

            try {

                if (message.type === 'text') {
                    const prevSignatureEnabledStatus = this.isSignatureEnabled;
                    this.isSignatureEnabled = false; // a assinatura da tentativa anterior é remontada abaixo
                    // A bolha guarda o texto SEM a assinatura (ela vive em `agentName`);
                    // o envio original levava `*Nome:*\n` na frente, e o reenvio também.
                    const textoReenvio = message.agentName ? `*${message.agentName}:*\n${message.text || ''}` : (message.text || '');
                    await handleSendMessage(this, 'TEXT', {
                        text: textoReenvio,
                        mentioned: [],
                        idMensagemReenvio,
                        respostaParticularReenvio,
                        ...this.getCommonPayloadData()
                    });
                    this.isSignatureEnabled = prevSignatureEnabledStatus;
                    // isSignatureEnabled=false faz shouldSign=false, zerando agentName na nova mensagem.
                    // Restaurei a partir da mensagem original para o cabeçalho de assinatura aparecer.
                    if (message.agentName) {
                        // Por id: mensagem recebida durante o await vai para o fim da lista.
                        const newMsg = this.messages.find(m => m.id === (idMensagemReenvio ?? message.id));
                        if (newMsg) newMsg.agentName = message.agentName;
                    }
                } else if (['file', 'imagem', 'video'].includes(message.type)) {
                    // Reenvia arquivo
                    const payload: SendFileMessagePayload = {
                        file: arquivoParaReenvio as File | string,
                        mimeType: typeof arquivoParaReenvio === 'string' ? message.fileData?.mimeType : undefined,
                        caption: message.fileData!.caption || '',
                        mentioned: message.mentions ?? [],
                        idMensagemReenvio,
                        // Se a mensagem persistiu, a assinatura (mensagem separada) já saiu
                        // antes dela — reenviar criaria outra assinatura a cada clique.
                        skipSignature: !!idMensagemReenvio,
                        respostaParticularReenvio,
                        ...this.getCommonPayloadData()
                    };
                    await handleSendMessage(this, 'FILE', payload);
                    // Com URL no lugar do File, a bolha nova tomaria a URL como nome do arquivo.
                    if (typeof payload.file === 'string' && message.fileData?.name) {
                        const newMsg = this.messages.find(m => m.id === (idMensagemReenvio ?? message.id));
                        if (newMsg?.fileData) newMsg.fileData.name = message.fileData.name;
                    }
                }
            } catch (error) {
                console.error("Falha ao tentar reenviar", error);
            } finally {
                if (citacaoEmRascunho && !this.messageToReply) this.messageToReply = citacaoEmRascunho;
                // Só volta se o operador não escolheu outra citação no meio tempo.
                else if (referenciaDoGrupoEmRascunho && !this.messageToReply && !this.respostasParticulares[chatId])
                    this.respostasParticulares[chatId] = referenciaDoGrupoEmRascunho;
            }
        },
        async sendStickerMessage(stickerBlob: Blob | string) {
            if (this.isForwarding) this.cancelForwarding();
            this.clearEditMessage();

            if (!stickerBlob) return;

            console.log("🚀 [Sticker] Iniciando envio...");

            let currentStickerUrl = '';
            let fileToUpload: File | null = null;

            const timestamp = Date.now();

            try {
                if (typeof stickerBlob === 'string') {
                    if (stickerBlob.startsWith('data:')) {
                        const mimeMatch = stickerBlob.match(/:(.*?);/);
                        const mimeType = mimeMatch ? mimeMatch[1] : 'image/webp';
                        const ext = mimeType.split('/')[1] || 'webp';
                        fileToUpload = dataURLtoFile(stickerBlob, `sticker-${timestamp}.${ext}`);
                        if (!fileToUpload) throw new Error("Falha ao converter imagem.");
                        currentStickerUrl = stickerBlob;

                    } else if (stickerBlob.startsWith('blob:')) {
                        // blob: é object URL do próprio documento — fetch aqui não sai
                        // para a rede e portanto não sofre bloqueio cross-origin.
                        const response = await fetch(stickerBlob);
                        const blob = await response.blob();
                        const mimeType = blob.type || 'image/webp';
                        const ext = mimeType.split('/')[1] || 'webp';
                        fileToUpload = new File([blob], `sticker-${timestamp}.${ext}`, { type: mimeType });
                        currentStickerUrl = stickerBlob;
                    } else {
                        // URL remota: enviada como está, sem baixar os bytes no navegador.
                        currentStickerUrl = stickerBlob;
                    }
                }
                else {
                    console.log("📦 [Sticker] Arquivo nativo detectado.");

                    currentStickerUrl = URL.createObjectURL(stickerBlob);

                    const mimeType = stickerBlob.type || 'image/webp';
                    const ext = mimeType.split('/')[1] || 'webp';
                    fileToUpload = new File([stickerBlob], `sticker-${timestamp}.${ext}`, { type: mimeType });
                }
            } catch (e) {
                console.error("❌ [Sticker] Erro ao preparar arquivo:", e);
                toast.error("Erro ao processar a imagem da figurinha.");
                return;
            }

            await handleSendMessage(this, 'STICKER', {fileurl: currentStickerUrl, file: fileToUpload, trackStickerUsage: this.trackStickerUsage.bind(this), ...this.getCommonPayloadData()} as SendStickerMessagePayload)
        },

        // #region Websocket ⚡
        async _initWebhook() {
            await this.userAuthStore.init(true);

            const ws = await this.userAuthStore.initWebsocket();
            this.websocketService = ws;

            this.isWsConnected = ws.ws.connected;
            ws.subscribed.addObservers((v) => this.isWsConnected = v);

            this.registerChatCallbacks();
        },

        registerChatCallbacks() {
            const ws = this.websocketService;
            if (!ws) return;
            this.websocketService = ws;
            this.userAuthStore.wsServiceChat = ws;

            if (!this.websocketService) return;

            this.isWsConnected = this.websocketService.ws.connected;

            this.websocketService.subscribed.addObservers((newVal: boolean) => {this.isWsConnected = newVal})

            // Client-side blip: socket dropped and came back. We may have missed
            // events while reconnecting → resync. (First connect never bumps this.)
            this.websocketService.reconnected.addObservers(() => {
                console.log('[chatStore] websocket reconnected → resync');
                void this.resync();
            });

            // Server-side blip: socket-server told us its NATS link reconnected,
            // so events published during the outage were never delivered → resync.
            this.websocketService.registerCallback('RESYNC', () => {
                console.log('[chatStore] RESYNC recebido → resync');
                void this.resync();
            }, false, true);

            this.websocketService.bindCallbackOnDocVisibilityRestored(() => this.fetchChats(), 1000 * 60 * 10, true);

            this.websocketService.registerCallback('CONEXAO', (_payload) => {
                const payload = _payload as ConexaoPayload;
                const inbox = this.inboxMap.get(payload.idInbox);
                if (!inbox) return;
                this._aplicarEstadoDeConexao(inbox, payload.connected);
                // Chegou resposta definitiva pelo tempo real: o estado deixa de ser
                // indeterminado, mesmo que a última consulta HTTP tenha falhado.
                inbox.statusIndeterminado = false;

                useInboxAlertStore().notifyConnected(payload.idInbox);

                if (this.uiStore.isShowingQrCode && payload.idInbox == this.uiStore.selectedInboxId) {
                    toast.success('Inbox conectado!');
                    Swal.close();
                    this.uiStore.isShowingQrCode = false;
                }
            }, false, true);

            this.websocketService.registerCallback('DESCONEXAO', (_payload) => {
                const payload = _payload as DesconexaoPayload;
                const inbox = this.inboxMap.get(payload.idInbox);
                if (!inbox) return;

                // DESVÍNCULO não é queda de sessão, e o caminho abaixo é todo
                // desenhado para queda de sessão — tratá-lo aqui produziria três
                // erros de uma vez.
                //
                // 1. Em coexistência, `_aplicarEstadoDeConexao` derruba a metade
                //    QR; mas o que se desvincula pela Meta é a metade OFICIAL. O
                //    QR continua de pé e é o que segura o inbox atendendo.
                // 2. O alerta "Inbox desconectado" é um alarme sobre algo que
                //    quebrou sozinho. Aqui alguém acabou de PEDIR a desconexão (ou
                //    removeu nosso app no Business Manager) — a tela já mostra a
                //    mudança, e o alerta seria ruído sobre a própria ação.
                // 3. Nada disso tem a ver com janela de pareamento: zerar o
                //    `qrcode` e clicar no refresh do QR abriria uma sessão nova
                //    sem ninguém pedir.
                if (payload.motivo === 'DESVINCULO') {
                    if (ehCoexistencia(inbox.plataforma)) {
                        inbox.metaConectado = false;
                        // `isConnected` do híbrido SEGUE a metade oficial (ver
                        // `resolveConnectionStatus` no server), então cai junto.
                        inbox.isConnected = false;
                    } else {
                        inbox.isConnected = false;
                    }
                    inbox.statusIndeterminado = false;
                    return;
                }

                // Em coexistência quem cai é a metade QR, e é a queda DELA que
                // decide o alerta — `isConnected` (a metade oficial) não se move,
                // então lê-lo aqui faria o aviso repetir a cada DESCONEXAO.
                const estavaConectado = ehCoexistencia(inbox.plataforma)
                    ? inbox.evolutionConectado === true
                    : inbox.isConnected;
                this._aplicarEstadoDeConexao(inbox, payload.connected);
                inbox.statusIndeterminado = false;
                // Qualquer desconexão mata a janela de pareamento no servidor —
                // o QR em cache passa a ser um código morto. Isso inclui o
                // expirar da janela (~160s sem escanear), que agora chega como
                // DESCONEXAO. Sem limpar aqui, reabrir o modal exibia o cadáver.
                inbox.qrcode = '';

                // Só alerta na transição conectado→desconectado: a expiração da
                // janela de QR de um inbox que nunca conectou também chega como
                // DESCONEXAO, e "inbox caiu" seria um alarme falso.
                if (estavaConectado) useInboxAlertStore().notifyDisconnected(inbox.id, inbox.nome);

                // Modal aberto para este inbox → renova na hora: o clique no
                // botão de refresh reaproveita o fluxo existente do modal (todo
                // ele é DOM cru dentro do Swal) e dispara um novo /conectar,
                // que abre nova janela e gera QR válido.
                if (this.uiStore.isShowingQrCode && payload.idInbox == this.uiStore.selectedInboxId) {
                    document.querySelector<HTMLButtonElement>('#swal-btn-refresh-qr')?.click();
                }
            }, false, true);

            // `supressIfLate` FALSE: 'CONECTANDO' está fora de `InboxEvent` e não
            // tem chave em `latestRecorded` — ligar o dedup descartaria tudo.
            // Mesma razão de PRESENCA_ATUALIZADA, logo abaixo.
            this.websocketService.registerCallback('CONECTANDO', (_payload) => {
                const payload = _payload as ConectandoPayload;
                if (!this.uiStore.isShowingQrCode || payload.idInbox != this.uiStore.selectedInboxId) return;
                // Não toca em `isConnected` nem no `qrcode` em cache: o evento só
                // diz "o QR foi lido, a sessão está abrindo". O estado real segue
                // vindo de CONEXAO/DESCONEXAO.
                this._mostrarConectandoNoQr();
            }, false, true);

            this.websocketService.registerCallback('QR_CODE', (_payload) => {
                const payload = _payload as QrcodePayload;
                const inbox = this.inboxMap.get(payload.idInbox);
                if (!inbox || !payload.base64) return;
                // O WhatsApp rotaciona o QR a cada ~20s (o primeiro dura 60s) e
                // invalida o anterior no ato. Sem aplicar esta atualização, o
                // modal segue exibindo um código morto e o celular falha com
                // "Não foi possível conectar o dispositivo".
                inbox.qrcode = payload.base64;
                if (this.uiStore.isShowingQrCode && payload.idInbox == this.uiStore.selectedInboxId) {
                    // Chegou QR novo = a janela de pareamento continua aberta, logo
                    // ninguém escaneou. Desfaz um "Conectando..." que tenha vindo de
                    // um `connecting` de subida da instância, senão o modal fica
                    // preso no spinner com o QR escondido.
                    this._esconderConectandoNoQr();
                    const img = document.querySelector<HTMLImageElement>('#swal-loading-qrcode img');
                    if (img) img.src = payload.base64;
                }
            }, false, true);

            // `supressIfLate` fica FALSE: o dedup por timestamp de `latestRecorded` é
            // para eventos idempotentes de estado durável; presença é efêmera e o
            // evento nem existe naquele mapa (leitura daria NaN e descartaria tudo).
            this.websocketService.registerCallback('PRESENCA_ATUALIZADA', (_payload) => {
                aplicarPresencaChat(_payload as PresencaAtualizadaPayload, this.authStore.currentAccountInboxes);
            }, false, true);

            // Ligações de voz. Registrado aqui junto dos outros para nascer com a
            // mesma sessão de socket; a store faz o resto e é idempotente.
            useLigacoesStore().registrar(this.websocketService);

            this.websocketService.registerCallback('MENSAGEM', (_payload) => {
                const payload = _payload as MensagemPayload;
                const isGroupMessage = !!payload.idGrupo;

                // Chegou mensagem => quem digitava terminou. Apaga antes de
                // qualquer outra coisa, senão o indicador fica pulsando embaixo da
                // mensagem recém-chegada até o TTL de 8 s: o `paused` pode se
                // perder e, mesmo quando vem, corre contra a própria mensagem.
                const idChatDaMensagem = payload.idGrupo ?? payload.idAtendimento;
                if (idChatDaMensagem) limparPresencasDoChat(idChatDaMensagem);
                const permissoesStore = usePermissoesStore();
                const authStore = useAuthStoreUser();

                // ✅ VALIDAR INBOX PRIMEIRO (antes de procurar chat)
                const idContaAtual = authStore.currentConta?.id;
                const idUsuario = authStore.user?.id; // 🔥 ESTAVA FALTANDO
                if (!idContaAtual || !idUsuario) return;

                const inboxDaMensagemPertenceAContaAtual = authStore.currentAccountInboxes.some(
                    inbox => inbox.id === payload.idInbox
                );
                if (!inboxDaMensagemPertenceAContaAtual) {
                    console.warn("[MENSAGEM] Inbox da mensagem não pertence à conta atual, ignorando");
                    return;
                }

                // 🔥 VALIDAÇÃO DE PERMISSÃO PARA ATENDIMENTOS
                if (payload.idAtendimento && !isGroupMessage) {
                    const temPermissaoVerTodos = permissoesStore.temPermissao(
                        'inbox.ver_todos_atendimentos',
                        idContaAtual,
                        payload.idInbox
                    );

                    // Se NÃO tem permissão de ver todos, verifica se é atendente do chat
                    if (!temPermissaoVerTodos) {
                        const foundChat = this.chatList.find(c => c.id == payload.idAtendimento);

                        if (!foundChat) {
                            console.warn("[MENSAGEM] Atendimento não encontrado na lista de chats, ignorando");
                            return;
                        }

                        // 🔥 VERIFICAR SE EU SOU ATENDENTE
                        // Opção 1: Se o chat tiver array de atendentes (ajuste conforme sua tipagem real)
                        const euSouAtendente = foundChat.atendentes?.some((agent: {id: string}) => agent.id === idUsuario);

                        // Canal com visão global → recebe mensagens de todos os atendimentos do canal
                        const canalComVisaoGlobal = foundChat.idCanal != null && authStore.canaisVisualizacaoGlobal[foundChat.idCanal] === true;

                        // Opção 2: Se não tiver agents no chat, assume que se está na lista, é atendente
                        // (isso pode não ser seguro, dependendo da sua lógica)

                        if (euSouAtendente === false && !canalComVisaoGlobal) { // false explícito, não undefined
                            console.warn("[MENSAGEM] Usuário não é atendente deste chat, ignorando");
                            return;
                        }
                    }
                }

                // ✅ SÓ AGORA procura o chat
                const foundChat = this.chatList.find(c => c.id == payload.idAtendimento || c.id == payload.idGrupo);
                if (!foundChat) {
                    // Descartar em silêncio escondia um caso real: mandar um template
                    // oficial para um contato SEM conversa aberta cria o atendimento no
                    // servidor, e a bolha chegava antes de `ATENDIMENTO_CRIADO` colocar o
                    // chat na lista. Do lado do operador isso é indistinguível de "o envio
                    // não fez nada" — a mensagem só aparecia ao recarregar.
                    console.warn('[MENSAGEM] chat ainda não está na lista, buscando o atendimento', payload.idAtendimento ?? payload.idGrupo);
                    if (payload.idAtendimento && !payload.idGrupo) void this._recuperarChatDaMensagem(payload);
                    return;
                }

                // Unsend do cliente em Instagram/Messenger: a mensagem SAI da
                // conversa, sem bolha "mensagem apagada" (diretriz da Meta — o
                // servidor já apagou o conteúdo da linha e a esconde do histórico).
                // Tratado ANTES de `lastMessage`/`unreadCount`: o evento chega como
                // uma mensagem recebida, então o fluxo normal exibiria a prévia com
                // o texto apagado e ainda somaria uma não-lida.
                const plataformaDoInbox = this.userAuthStore.inboxesFull
                    .find(i => i.id === payload.idInbox)?.plataforma;
                if (payload.apagada && plataformaOcultaMensagemApagada(plataformaDoInbox)) {
                    const indice = this.messages.findIndex(m => m.id === payload.id);
                    const eraDoChatFocado = indice !== -1;
                    if (eraDoChatFocado) this.messages.splice(indice, 1);

                    // Só reconstrói a prévia quando temos a conversa carregada; nos
                    // outros casos o próximo fetch da lista traz a prévia recalculada
                    // pelo servidor.
                    if (eraDoChatFocado) {
                        const restante = this.messages.at(-1);
                        foundChat.lastMessage = restante
                            ? {
                                sender  : restante.sender ?? null,
                                type    : ['text', 'sticker', 'list', 'audio', 'poll'].includes(restante.type) ? restante.type : 'text',
                                text    : restante.text ?? '',
                                isFromMe: restante.isSent,
                                status  : restante.readStatus,
                            }
                            : { sender: null, type: 'text', text: '' };
                    }
                    return;
                }

                const foundContato = isGroupMessage ? this.contacts.find(c => c.id == payload.idContato) : undefined;

                const message = MakeMessage.FromWhizMessage(payload, { thisUserName: this.user?.nome, thisChatName: isGroupMessage ? foundContato?.nome : foundChat.name });

                const tipoRecebido = MakeMessage.Type.FromWhizMsgTipo(payload.tipo);
                foundChat.lastMessage = {
                    sender: message.sender ?? null,
                    type:  ['text', 'sticker', 'list', 'audio', 'poll'].includes(tipoRecebido) ? tipoRecebido : 'text',
                    text: getDisplayTextForMessageContent(payload.conteudo, payload.tipo),
                    ogType: payload.tipo,
                    isFromMe: payload.enviadaPorMim,
                    status: MakeMessage.ReadStatus.FromWhizSituacao(payload.situacao)
                };
                // Atualiza, sempre, a data da última interação
                foundChat.time = payload.dataEnvio;

                const thisUserId = this.userAuthStore.user?.id;
                if (!thisUserId) throw new Error('[chatStore] [MENSAGEM callback] this.userAuthStore.user?.id was undefined.');
                const isMessageFromMyAtendimento = foundChat.isGroup ? false : foundChat.atendentes?.map(a => a.id).includes(thisUserId);

                // Caso em que a mensagem pertence ao chat ativo/focado
                if ((!!payload.idAtendimento && payload.idAtendimento == this.activeChatId) || (!!payload.idGrupo && payload.idGrupo == this.activeChatId)) {
                    if (!message.isSent && !grupoEstaSilenciado(foundChat)) this.soundStore.playSound('onReceiveActive', isMessageFromMyAtendimento);

                    // Se for uma reação, fazemos loucurinhas todo: mudar para dentro do chatConversation
                    if (payload.tipo == 'REACAO') {
                        const reacaoConteudo = payload.conteudo as ReceivedReactionContent;
                        const foundMessage = this.messages.find(m => m.idExterno == reacaoConteudo.idMensagem);
                        if (foundMessage) {
                            // Mesma chave do recarregamento (ver `chaveDoReator`).
                            const chaveReator = chaveDoReator(payload);
                            if (!foundMessage.reactions) foundMessage.reactions = new Map();
                            if (reacaoConteudo.reacao == '')
                                foundMessage.reactions.delete(chaveReator);
                            else
                                foundMessage.reactions.set(chaveReator, reacaoConteudo.reacao);
                        }
                    // Se for qualquer outra coisa, colocamos dentro dele fofinhamente, e atualizamos a data de interação do chat
                    } else {
                        const foundMsg = this.messages.find(msg => msg.id == payload.id);
                        if (!foundMsg) {
                            if (payload.enviadaPorMim && payload.idUsuario === idUsuario) {
                                // Race condition: socket arrived before API response updated the optimistic
                                // message's temp ID to the real server ID. Replace the pending optimistic
                                // message in-place so status updates immediately without creating a duplicate.
                                // Correlaciona por tipo+conteúdo (não só "a primeira pendente"), pois pode
                                // haver mais de uma mensagem pendente ao mesmo tempo (ex: texto + arquivo
                                // enviados em sequência rápida), e a ordem de chegada dos webhooks não é
                                // garantida ser a mesma ordem de envio.
                                const pendingMsg = this.messages.find(m => {
                                    if (!m.isSent || m.readStatus !== 'pending' || m.type !== message.type) return false;
                                    // Template não tem texto nem arquivo para correlacionar: o que
                                    // identifica a tentativa é QUAL template foi mandado. Sem este
                                    // ramo a bolha otimista sobrevivia ao lado da real, e o operador
                                    // via o mesmo template duas vezes.
                                    if (m.type === 'template_meta')
                                        return m.metaTemplateData?.template?.id === message.metaTemplateData?.template?.id;
                                    return m.type === 'text'
                                        ? (m.text ?? '') === (message.text ?? '')
                                        : (m.fileData?.name ?? '') === (message.fileData?.name ?? '') && (m.legenda ?? '') === (message.legenda ?? '');
                                });
                                if (pendingMsg) {
                                    // A mensagem vinda do servidor tem o texto já sem a assinatura (`*NOME:*\n`),
                                    // que passa a viver em `agentName`. Se o operador digitou a assinatura à mão
                                    // (toggle de assinatura desligado), a mensagem otimista não tem `agentName`;
                                    // sobrescrever cegamente apagaria o nome da tela até o próximo carregamento.
                                    // Por isso a otimista só serve de fallback quando o servidor não trouxe assinatura.
                                    // `extractSignature` devolve '' quando não há assinatura, então o fallback usa truthiness.
                                    Object.assign(pendingMsg, message, { agentName: message.agentName || pendingMsg.agentName });
                                } else {
                                    // Own message from another tab/device — no pending optimistic here, push normally
                                    this.messages.push(message);
                                }
                            } else {
                                this.messages.push(message);
                                // Se a mensagem não é minha, incrementa o contador mesmo com o chat aberto.
                                // O componente ChatConversation.vue vai zerar isso se o scroll estiver no fim.
                                if (!payload.enviadaPorMim) foundChat.unreadCount++;
                            }
                        } else {
                            console.log(`[mensagem] msg do chat focado, já existente (atualizada): achada | nova`, foundMsg, message);
                            // Mesmo motivo do bloco acima: preserva a assinatura resolvida pelo servidor
                            // e só cai para a local quando não houver nenhuma.
                            // Recebida: a assinatura saiu do texto e vive só em `agentName` (que a
                            // conversão já zera em grupo) — anulá-la aqui a apagaria da tela.
                            Object.assign(foundMsg, message, {agentName: foundMsg.isSent ? (message.agentName || foundMsg.agentName) : message.agentName});
                        }
                    }
                // Caso em que a mensagem pertence a um chat não focado - atualizar contador de não-lidas
                } else {
                    if (!payload.enviadaPorMim) {
                        foundChat.unreadCount++;
                        if (!grupoEstaSilenciado(foundChat)) this.soundStore.playSound('onReceivePassive', isMessageFromMyAtendimento);
                        // console.log(`[mensagem] msg em um chat não focado, enviada por outrem (aumenta qtd n lidas)`);
                    } else {
                        // console.log(`[mensagem] msg em um chat não focado, enviada por mim (nada acontece feijoada)`);
                    }

                }
            }, false, true);

            this.websocketService.registerCallback('MENSAGEM.ID_EXTERNO', (_payload) => {
                const { idMensagem, idExterno } = _payload as { idMensagem: string, idExterno: string };
                const msg = this.messages.find(m => m.id === idMensagem);
                if (msg) msg.idExterno = idExterno;
            }, false, true);

            /**
             * Situação da mensagem enviada avançou (entregue / lida) — os checks.
             *
             * Evento ESTREITO de propósito, irmão de `MENSAGEM.ID_EXTERNO`. O servidor
             * chegou a republicar a mensagem inteira como `MENSAGEM`, já que o handler
             * acima trata id conhecido como update — mas aquele handler faz muito mais
             * que atualizar: limpa o "digitando" do chat, empilha a mensagem quando ela
             * não está na página carregada (`messages` é paginado) e faz `Object.assign`
             * com um objeto novo cujos `reactions`/`isPinned`/`isFavorite`/`waveform`
             * nascem vazios. Uma confirmação de leitura apagava reação e pin da tela.
             *
             * `ehUltima` vem pronto do servidor. O front não tem como decidir: `chat.time`
             * nasce da `dataCriacao` do atendimento (ou do relógio do BROWSER, no envio) e
             * `dataEnvio` é hora do WhatsApp truncada em segundos — comparar as duas
             * bloqueava a primeira mensagem de todo atendimento novo.
             */
            this.websocketService.registerCallback('MENSAGEM.SITUACAO', (_payload) => {
                const { idMensagem, idAtendimento, idGrupo, situacao, ehUltima } = _payload as {
                    idMensagem: string, idAtendimento: string | null, idGrupo: string | null,
                    situacao: WhizMessageSituacao, ehUltima: boolean
                };

                const novoStatus = MakeMessage.ReadStatus.FromWhizSituacao(situacao);

                // A mensagem só está carregada se o chat dela estiver aberto; fora disso
                // não há nada a redesenhar, e o valor persistido cobre o próximo load.
                const msg = this.messages.find(m => m.id === idMensagem);
                if (msg) msg.readStatus = novoStatus;

                if (!ehUltima) return;
                const chat = this.chatList.find(c => c.id === (idAtendimento ?? idGrupo));
                // Só o status do card. Texto, tipo e `time` ficam como estão: a mensagem
                // não mudou, só o check dela.
                if (chat?.lastMessage) chat.lastMessage.status = novoStatus;
            }, false, true);

            /**
             * Alguém votou numa enquete — as barras do card andam.
             *
             * Estreito pela mesma razão de `MENSAGEM.SITUACAO`: republicar a mensagem
             * como `MENSAGEM` limparia o "digitando", empilharia a mensagem quando ela
             * não está na página carregada e zeraria `reactions`/`isPinned` no
             * `Object.assign`. Um voto não pode apagar uma reação da tela.
             *
             * A contagem vem PRONTA do servidor, por índice de opção: o WhatsApp
             * identifica a opção votada pelo SHA-256 do nome dela, e quem sabe traduzir
             * aquilo é quem guardou a enquete. `totalVotos` é gente, não soma — em
             * múltipla escolha uma pessoa soma em várias opções.
             */
            /**
             * A mídia de uma mensagem que chegou pendente ficou pronta (ou se perdeu).
             *
             * Estreito pela mesma razão de `MENSAGEM.SITUACAO`: o servidor só
             * reconciliou a MÍDIA, então só os campos de mídia mudam — reação, pin,
             * favorito e "digitando" ficam como estão. Mensagem fora da página
             * carregada é ignorada: o estado persistido cobre o próximo load.
             */
            this.websocketService.registerCallback('MENSAGEM.MIDIA', (_payload) => {
                const { idMensagem, url, ehMediaPerdida, tipo, conteudo } = _payload as {
                    idMensagem: string, url?: string, ehMediaPerdida: boolean,
                    tipo?: WhizMessageTipo, conteudo?: MessageContent,
                };
                const msg = this.messages.find(m => m.id === idMensagem);
                if (!msg) return;

                msg.midiaPendente = false;
                msg.miniatura = undefined;

                if (ehMediaPerdida && tipo && conteudo) {
                    // Mesma forma do caminho em linha: vira TEXTO com o aviso.
                    msg.ehMediaPerdida = true;
                    msg.type = MakeMessage.Type.FromWhizMsgTipo(tipo);
                    msg.subtype = MakeMessage.Subtype.FromWhizMsgTipo(tipo);
                    msg.text = (conteudo as { texto?: string }).texto;
                    msg.fileData = undefined;
                    msg.audioUrl = undefined;
                    return;
                }

                if (!url) return;
                const original = msg._original as { tipo: WhizMessageTipo, conteudo: MessageContent, enviadaPorMim: boolean } | undefined;
                if (!original) return;
                const conteudoAtualizado = { ...(original.conteudo as object), url } as MessageContent;
                delete (conteudoAtualizado as { midiaPendente?: unknown }).midiaPendente;
                delete (conteudoAtualizado as { miniatura?: unknown }).miniatura;
                original.conteudo = conteudoAtualizado;
                const tipoResolvido = MakeMessage.Tipo.Resolve(original.tipo, conteudoAtualizado);
                msg.fileData = MakeMessage.Content.ToFileData(conteudoAtualizado, tipoResolvido, original.enviadaPorMim);
                msg.audioUrl = msg.fileData?.url;
            }, false, true);

            this.websocketService.registerCallback('MENSAGEM.ENQUETE_VOTO', (_payload) => {
                const { idMensagem, contagem, totalVotos } = _payload as {
                    idMensagem: string, contagem: number[], totalVotos: number
                };

                // Só está carregada se o chat dela estiver aberto; fora disso não há o
                // que redesenhar, e o `conteudo` persistido cobre o próximo load.
                const msg = this.messages.find(m => m.id === idMensagem);
                if (!msg?.pollData) return;

                msg.pollData.totalVoters = totalVotos;
                msg.pollData.options.forEach((option, index) => {
                    const anterior = option.votes || 0;
                    option.votes = Number(contagem?.[index] ?? 0);

                    // O evento é estreito de propósito: move a contagem sem dizer QUEM
                    // votou. Um evento é a seleção COMPLETA de UM votante, então o delta
                    // de cada opção é exatamente -1, 0 ou +1 — encolher significa que
                    // alguém saiu DESTA opção, e não há como saber quem. Os nomes que o
                    // card tem deixaram de ser confiáveis: zerar joga todos para
                    // "não identificado" (`pollUnnamedVotes`) em vez de deixar na tela um
                    // nome sob a barra que a pessoa abandonou. Perde nome, não mente.
                    //
                    // Comparar com a contagem ANTERIOR, e não com `voters.length`: quando
                    // parte dos votos já era anônima (`voters` menor que a contagem), uma
                    // retirada não derrubava `votes` abaixo de `voters.length` e o nome de
                    // quem saiu continuava listado — a barra caía de 2 para 1 e o popover
                    // seguia mostrando o votante errado.
                    //
                    // Crescer não invalida nada: um votante não adiciona a mesma opção
                    // duas vezes, então os nomes que já estavam continuam valendo e o novo
                    // entra como não identificado. `conteudo.votos` repõe tudo no próximo
                    // carregamento da conversa.
                    if (option.votes < anterior) option.voters = [];
                });
            }, false, true);

            // Falha no envio assíncrono (consumer da fila). O caminho síncrono já
            // é coberto pelo catch de `asyncSendMessage`, mas quando o inbox usa
            // fila o HTTP responde 200 antes do envio real acontecer — sem este
            // evento a bolha ficaria com cara de enviada para sempre.
            this.websocketService.registerCallback('MENSAGEM.FALHA_ENVIO', (_payload) => {
                const { idMensagem, motivo, codigo, detalhe } = _payload as { idMensagem: string, motivo?: string, codigo?: number | null, detalhe?: string | null };
                const msg = this.messages.find(m => m.id === idMensagem);
                if (!msg) return;
                msg.readStatus = 'error';
                msg.uploadError = true;
                // O motivo agora vem traduzido (recusa da Meta) e a bolha o mostra na hora,
                // sem precisar recarregar a conversa — antes ia só para o console.
                if (motivo) msg.motivoFalha = motivo;
                if (codigo != null) msg.erroCodigo = String(codigo);
                if (detalhe && detalhe !== motivo) msg.erroDetalhe = detalhe;
                console.error(`[chatStore] Falha no envio da mensagem ${idMensagem}: ${motivo ?? 'motivo não informado'}`);
            }, false, true);

            this.websocketService.registerCallback('ATENDIMENTO_CRIADO', (_payload) => {
                const payload = _payload as AtendimentoCriadoPayload;

                // 🆕 Validar conta
                const idContaAtual = this.userAuthStore.currentConta?.id;
                if (!idContaAtual || payload.idInbox === undefined) return;

                const inboxPertenceAContaAtual = this.userAuthStore.currentAccountInboxes.some(
                    inbox => inbox.id === payload.idInbox
                );
                if (!inboxPertenceAContaAtual) return;

                console.log('[novo_atendimento event payload]', payload);

                // Verificar visibilidade (mesma regra da carga inicial da lista)
                if (!this._podeVerAtendimento({ ...payload, atendentes: [] })) return;

                const idx = this.chatList.findIndex(c => c.id == payload.id);
                const chat = MakeChat.FromAtendimento({ ...{ ...payload, ...{ idQuadro: null } }, contato: payload.contato, atendentes: [] });
                if (idx < 0) this.chatList.unshift(chat)
            }, false, true);

            this.websocketService.registerCallback('ATENDIMENTO_INICIADO', (_payload: AtendimentoAtualizadoPayload) => {
                console.log('[atendimento_iniciado event payload]', _payload);
                const inboxPertenceAContaAtual = this.userAuthStore.currentAccountInboxes.some(
                    inbox => inbox.id === _payload.idInbox
                );
                if (!inboxPertenceAContaAtual) return;

                // Atendimento iniciado por outro atendente sai da lista de quem não pode
                // mais vê-lo — mesma regra do ATENDIMENTO_ATUALIZADO e da carga inicial
                // (AtendimentosService.aplicarFiltroVisibilidade): enquanto "criado" ele
                // pisca para a fila/canal inteiro e, ao ganhar atendente, só continua
                // visível para o próprio atendente, para quem tem ver_todos_atendimentos
                // ou para canal com visualização global.
                //
                // Antes a checagem valia só para inbox SEM canais. Em inbox COM canais o
                // atendimento seguia na lista dos outros atendentes até o próximo refetch
                // (o clique no chat) — era o "some só depois de clicar".
                //
                // Duas guardas permanecem: sem `atendentes` no payload não dá para saber de
                // quem é o atendimento, e sem o mapa de canais carregado não dá para
                // classificar o inbox (a carga inicial também não recorta nesse caso, ver
                // `aplicarFiltroPermissaoInbox`). Nos dois casos mantém o upsert.
                if (_payload.atendentes && this.inboxesComCanaisCarregado && !this._podeVerAtendimento(_payload)) {
                    this.chatList = this.chatList.filter(c => c.id !== _payload.id);
                    return;
                }

                this._handleUpsertAtendimento(_payload);
            }, false, true)

            this.websocketService.registerCallback('ATENDIMENTO_ATUALIZADO', (_payload: AtendimentoAtualizadoPayload) => {
                console.log('[atendimento_atualizado event payload]', _payload);

                const authStore = useAuthStoreUser();

                const inboxPertenceAContaAtual = authStore.currentAccountInboxes.some(
                    inbox => inbox.id === _payload.idInbox
                );
                if (!inboxPertenceAContaAtual) return;

                if (!this._podeVerAtendimento(_payload)) {
                    // Remove da lista caso estivesse visível antes (ex: canal mudou para outro,
                    // ou o atendimento saiu da fila de criados ao ser atribuído/iniciado)
                    this.chatList = this.chatList.filter(c => c.id !== _payload.id);
                    return;
                }

                this._handleUpsertAtendimento(_payload);
            }, false, true);

            this.websocketService.registerCallback('ATENDIMENTO_FINALIZADO', (_payload: AtendimentoAtualizadoPayload) => {
                console.log('[atendimento_finalizado event payload]', _payload);
                const inboxPertenceAContaAtual = this.userAuthStore.currentAccountInboxes.some(
                    inbox => inbox.id === _payload.idInbox
                );
                if (!inboxPertenceAContaAtual) return;
                this._handleFinishedAtendimento(_payload);
            }, false, true);

            this.websocketService.registerCallback('GRUPO_CRIADO', (_payload: GrupoCriadoPayload) => {
                console.log('[grupo_criado event payload]', _payload);
                const inboxPertenceAContaAtual = this.userAuthStore.currentAccountInboxes.some(
                    inbox => inbox.id === _payload.idInbox
                );
                if (!inboxPertenceAContaAtual) return;
                this._handleUpsertGrupo(_payload);
            }, false, true);

            this.websocketService.registerCallback('GRUPO_ATUALIZADO', (_payload: GrupoCriadoPayload) => {
                console.log('[grupo_atualizado event payload]', _payload);
                const inboxPertenceAContaAtual = this.userAuthStore.currentAccountInboxes.some(
                    inbox => inbox.id === _payload.idInbox
                );
                if (!inboxPertenceAContaAtual) return;
                // Promoção/rebaixamento de administrador e entrada/saída de participante
                // chegam por este evento, mas o payload não traz a lista. Sem descartar
                // o cache local, ela sobrevive até 1 h (PARTICIPANTS_CACHE_TTL_MS) e o
                // inbox recém-promovido continua aparecendo como não-administrador.
                this._invalidarParticipantesDoGrupo(_payload.id);
                this._handleUpsertGrupo(_payload);
            }, false, true);

            this.websocketService.registerCallback('GRUPO_REMOVIDO', (_payload) => {
                return;
                // o motivo desse return é que infelizmente o id do inbox não faz parte do payload que vem no grupo removido,
                // então eu preciso remover o grupo do inbox certo direto ao receber uma resposta positiva do delete group.
                // obs: na real acho que o id do grupo é diferente por inbox, mesmo que seja o mesmo grupo do zap, mas, por segurança, vou deixar assim :)
                console.log('[grupo_criado event payload]', _payload);
                this._handleDeletedGrupo(_payload as GrupoRemovidoPayload);
            }, false, true);

            // Anotações internas — o servidor publica no tópico do INBOX, então cada
            // evento precisa ser filtrado pelo alvo antes de tocar a timeline aberta.
            this.websocketService.registerCallback(['ANOTACAO_INTERNA_CRIADA', 'ANOTACAO_INTERNA_EDITADA'], (_payload) => {
                const anotacao = _payload as unknown as AnotacaoInterna;
                if (!this.anotacaoPertenceAoChatAtivo(anotacao)) return;
                this.aplicarAnotacaoNaTimeline(anotacao);
            }, false, true);

            this.websocketService.registerCallback('ANOTACAO_INTERNA_REMOVIDA', (_payload) => {
                const anotacao = _payload as unknown as AnotacaoInterna;
                if (!this.anotacaoPertenceAoChatAtivo(anotacao)) return;
                const idx = this.anotacoesInternas.findIndex(a => a.id === anotacao.id);
                if (idx >= 0) this.anotacoesInternas.splice(idx, 1);
            }, false, true);

            this.websocketService.registerCallback('CONTATO_ATUALIZADO', (_payload: any) => {
                const payload = _payload as import('@/services/websocket/websocket.service').ContatoAtualizadoEventPayload;
                const local = this.contacts.find(c => c.id === payload.id);
                if (local?.updatedAt && new Date(payload.updatedAt) <= new Date(local.updatedAt)) return;
                if (local) Object.assign(local, payload);
                for (const chat of this.chatList.filter(c => c.idContato === payload.id)) {
                    if (!chat.isGroup) chat.name = payload.nome;
                    chat.codigoContato      = payload.codigo;
                    chat.obsContato         = payload.observacao;
                    chat.idClienteAxion     = payload.idClienteAxion ?? undefined;
                    chat.idResponsavelAxion = payload.idResponsavelAxion ?? undefined;
                }
                if (this.tempProfileChat?.idContato === payload.id) {
                    this.tempProfileChat.name          = payload.nome;
                    this.tempProfileChat.codigoContato = payload.codigo;
                    this.tempProfileChat.obsContato    = payload.observacao;
                }
                if (this.activeChat?.participants) {
                    for (const p of this.activeChat.participants) {
                        if (p.idContato === payload.id || p.phone === payload.codigo) {
                            p.name = payload.nome;
                        }
                    }
                }
            });
        },

        __upsertChat(chat: Chat) {
            const idx = this.chatList.findIndex(c => c.id == chat.id);
            if (idx < 0) this.chatList.unshift(chat);
            else {
                const foundChat = this.chatList.at(idx);
                if (!foundChat) return;

                const tags           = foundChat.tags;
                // max: eventos sem incluirNaoLidas chegam com naoLidas 0 e não podem zerar o local
                const unreadCount    = Math.max(foundChat.unreadCount ?? 0, chat.unreadCount ?? 0);
                // tri-state: undefined = payload sem info (preserva local); boolean = servidor mandou (propaga entre clientes)
                const isMarkedUnread = chat.isMarkedUnread === undefined ? foundChat.isMarkedUnread : chat.isMarkedUnread;
                const lastMessage    = foundChat.lastMessage;
                const isPinned       = foundChat.isPinned;        // ← preservar
                const isMuted        = foundChat.isMuted;         // ← preservar (mesmo motivo)
                const silenciadoAte  = foundChat.silenciadoAte;   // ← preservar (mesmo motivo)
                const isFavorite     = foundChat.isFavorite;      // ← preservar (mesmo motivo)
                const grupoWhitelist = foundChat.grupoWhitelist;  // ← preservar (mesmo motivo)
                const participants = foundChat.participants;  // ← preservar (mesmo motivo)
                // Mesma regra tri-state das não lidas: nem todo payload que chega aqui
                // carrega o status cru e o vínculo de campanha. Sem esta guarda, um
                // evento sem esses campos zeraria `statusServidor` e faria um
                // atendimento oculto de campanha reaparecer na lista sem motivo.
                const statusServidor = chat.statusServidor === undefined ? foundChat.statusServidor : chat.statusServidor;
                const disparo        = chat.disparo === undefined ? foundChat.disparo : chat.disparo;
                // Mesma regra tri-state: `AtendimentoAtualizadoPayload.contato` não traz
                // `nomeWhatsapp`, e sem esta guarda um evento de atualização apagaria o
                // valor que a carga da lista trouxe — `{nome_whatsapp}` num template
                // passaria a cair para o nome cadastrado sem motivo.
                const nomeWhatsappContato = chat.nomeWhatsappContato === undefined ? foundChat.nomeWhatsappContato : chat.nomeWhatsappContato;
                // `time` só anda para frente. O carimbo ao vivo (mensagem que
                // chega, ligação que encerra) precede a gravação do servidor —
                // que é assíncrona, via fila —, então um evento de atendimento
                // qualquer (assumir, mover de quadro, etiquetar) chegando nessa
                // janela traz `ultimaInteracao` AINDA ANTIGO e derrubava a
                // conversa de volta para o meio da lista.
                const tempoNovo  = chat.time ? new Date(chat.time).getTime() : 0;
                const tempoLocal = foundChat.time ? new Date(foundChat.time).getTime() : 0;
                const time       = tempoNovo >= tempoLocal ? chat.time : foundChat.time;

                Object.assign(foundChat, chat, { unreadCount, isMarkedUnread, tags, lastMessage, isPinned, isMuted, silenciadoAte, isFavorite, grupoWhitelist, participants, statusServidor, disparo, nomeWhatsappContato, time });
            }
        },

        /**
         * Descobre quais inboxes têm canais cadastrados — insumo do recorte de fila.
         * Tenta novamente com backoff porque o mapa vazio desliga o recorte: uma falha
         * transitória faria a fila de inbox sem canais aparecer inteira para quem não tem
         * ver_todos_atendimentos. Esgotadas as tentativas, `inboxesComCanaisCarregado` fica
         * false e o comportamento antigo (fila livre) é mantido em vez de restringir errado.
         */
        async _carregarInboxesComCanais(idsInboxes: IdInbox[], tentativas = 3) {
            for (let tentativa = 1; tentativa <= tentativas; tentativa++) {
                try {
                    const results = await new CanalService().getAllByManyInbox(idsInboxes);
                    this.inboxesComCanais.clear();
                    for (const { idInbox, canais } of results) {
                        if (canais.length > 0) this.inboxesComCanais.add(idInbox);
                    }
                    this.inboxesComCanaisCarregado = true;
                    return;
                } catch (erro) {
                    console.warn(`[chatStore] falha ao buscar canais das inboxes (tentativa ${tentativa}/${tentativas})`, erro);
                    if (tentativa < tentativas) {
                        await new Promise(resolve => setTimeout(resolve, 500 * 2 ** (tentativa - 1)));
                    }
                }
            }
            // Sem o mapa não é possível classificar as inboxes: zera para não usar dados de
            // outra conta e desliga o recorte.
            this.inboxesComCanais.clear();
            this.inboxesComCanaisCarregado = false;
        },

        /**
         * Regra de visibilidade de um atendimento para o usuário atual.
         * Espelha AtendimentosService.aplicarFiltroPermissaoInbox/aplicarFiltroVisibilidade
         * (carga inicial da lista) para que os eventos de websocket não reexibam nem
         * mantenham na lista atendimentos que o usuário não pode ver:
         *   - com ver_todos_atendimentos → vê tudo
         *   - é atendente OU canal com visualização global → vê
         *   - sem permissão + inbox COM canais → sem atendentes E canal é do usuário
         *   - sem permissão + inbox SEM canais → apenas os "criados" (piscando, sem atendentes)
         */
        _podeVerAtendimento(payload: {
            idInbox: IdInbox,
            idCanal: IdCanal|null,
            dataAbertura?: string|null,
            atendentes?: { id: string }[]|null,
        }): boolean {
            const permissoesStore = usePermissoesStore();
            const authStore = useAuthStoreUser();

            const idConta = authStore.currentConta?.id;
            const idUsuario = authStore.user?.id;
            if (!idConta || !idUsuario) return false;

            if (permissoesStore.temPermissao('inbox.ver_todos_atendimentos', idConta, payload.idInbox)) return true;

            if (payload.atendentes?.some(atendente => atendente.id === idUsuario)) return true;
            // Canal com visão global → vê todos os atendimentos do canal, mesmo atribuídos a outros
            if (payload.idCanal != null && authStore.canaisVisualizacaoGlobal[payload.idCanal] === true) return true;

            const semAtendentes = !payload.atendentes || payload.atendentes.length === 0;

            if (this.inboxesComCanais.has(payload.idInbox)) {
                // Inbox com canais: fila visível só para quem pertence ao canal atribuído
                // (e somente após o canal ser definido)
                return semAtendentes && payload.idCanal != null && authStore.canais[payload.idCanal] === true;
            }

            // Mapa de canais indisponível → não é possível afirmar que a inbox é sem canais;
            // mantém o comportamento antigo (fila geral visível para todos)
            if (!this.inboxesComCanaisCarregado) return semAtendentes;

            // Inbox sem canais: fila geral limitada aos atendimentos ainda não iniciados
            return semAtendentes && !payload.dataAbertura;
        },

        /**
         * Bolha provisória do template Meta, enquanto o envio está em voo.
         *
         * O envio de template é o único caminho de mensagem do chat sem eco otimista: o
         * operador clicava, a lista fechava e a tela não mudava até o webhook da Meta
         * voltar (ou até recarregar). A bolha nasce `pending` e é substituída pela real
         * quando o evento chega — a correlação é por id do template, no handler de
         * `MENSAGEM`.
         *
         * Devolve o id provisório para o chamador poder removê-la se o envio falhar.
         */
        adicionarTemplateMetaOtimista(template: IMetaTemplate, params: IMetaParams): IdMensagem | null {
            const chat = this.chatList.find(c => c.id == this.activeChatId);
            const idProvisorio = makeDummyIdMensagem();

            const message: Message = {
                idExterno: '',
                id: idProvisorio,
                time: new Date().toISOString(),
                isSent: true,
                readStatus: 'pending',
                type: 'template_meta',
                isBot: false,
                text: '',
                metaTemplateData: {
                    tipo: 'TEMPLATE_META',
                    // Otimista: assume aceite. Recusa chega pelo evento real e reescreve a
                    // bolha com `sendSuccess:false` + motivo.
                    sendSuccess: true,
                    parametersToReplace: paramsComAutomaticas(params, chat?.name),
                    template: template as unknown as NonNullable<Message['metaTemplateData']>['template'],
                },
            };

            this.messages.push(message);

            if (chat) {
                chat.lastMessage = {
                    sender: this.user?.nome ?? null,
                    text: getDisplayTextForMessageContent({ template: JSON.stringify(template) } as unknown as MessageContent, 'TEMPLATE_META'),
                    type: 'text',
                    ogType: 'TEMPLATE_META',
                    status: 'pending',
                    isFromMe: true,
                };
            }

            return idProvisorio;
        },

        /** Remove a bolha provisória quando o envio nem chegou a acontecer. */
        removerMensagemOtimista(idMensagem: IdMensagem | null) {
            if (!idMensagem) return;
            this.messages = this.messages.filter(m => m.id !== idMensagem);
        },

        /**
         * Traz para a lista o chat de uma mensagem cujo atendimento ainda não está nela.
         *
         * Acontece de verdade quando o operador manda um template oficial para um contato
         * sem conversa aberta: o servidor cria o atendimento e publica a bolha, e a bolha
         * pode chegar antes de `ATENDIMENTO_CRIADO`. Sem esta recuperação, o evento era
         * descartado e a mensagem só aparecia ao recarregar o chat.
         *
         * `__upsertChat` faz o trabalho de inserir/atualizar, então uma corrida com o
         * `ATENDIMENTO_CRIADO` não duplica a linha. Nunca lança: é melhor seguir sem o
         * chat do que derrubar o handler de mensagens.
         */
        async _recuperarChatDaMensagem(payload: MensagemPayload) {
            const idAtendimento = payload.idAtendimento;
            if (!idAtendimento || !payload.idContato) return;
            // Uma tentativa por atendimento: mensagem que continua sem chat (sem
            // permissão, por exemplo) não pode virar uma chamada por evento recebido.
            if (this._chatsEmRecuperacao.has(idAtendimento)) return;
            this._chatsEmRecuperacao.add(idAtendimento);

            try {
                const abertos = await this.chatService.findAbertosByContato(payload.idContato);
                const atendimento = abertos?.find(a => a.id === idAtendimento);
                if (!atendimento) return;

                if (!this._podeVerAtendimento({
                    idInbox: atendimento.idInbox,
                    idCanal: atendimento.idCanal ?? null,
                    atendentes: (atendimento.atendentes ?? []) as { id: string }[],
                })) return;

                this.__upsertChat(MakeChat.FromAtendimento(atendimento as Parameters<typeof MakeChat.FromAtendimento>[0]));
            } catch (e) {
                console.warn('[MENSAGEM] falha ao recuperar o chat do atendimento', idAtendimento, e);
            } finally {
                this._chatsEmRecuperacao.delete(idAtendimento);
            }
        },

        /**
         * Sobe a conversa na lista quando uma ligação termina.
         *
         * O servidor já carimbou `ultimaInteracao` no atendimento (é ele quem
         * manda em quem recarrega a página); isto é o equivalente AO VIVO, para
         * a conversa não ficar parada no meio da lista até o próximo fetch.
         *
         * Só o `time` muda: a prévia do card continua sendo a última mensagem
         * real — ligação não é mensagem, e aparece na timeline como anotação.
         *
         * A guarda de "só para frente" é a mesma do consumidor `atendimento-wbc`:
         * a fila offline do WhatsApp reentrega ligações antigas depois de toda
         * reconexão, e um terminate de ontem não pode puxar a conversa para o
         * topo.
         *
         * O instante vai CRU, como todo carimbo ao vivo de `chat.time` (a
         * mensagem que chega grava `payload.dataEnvio` direto). Passar pelo
         * ajuste de −3 h que o `MakeChat.FromAtendimento` aplica em browser UTC
         * jogaria a ligação 3 h para trás e a guarda acima rejeitaria o próprio
         * bump — no ambiente em que aquele ajuste age, a conversa simplesmente
         * não subiria.
         */
        marcarInteracaoDeLigacao(
            idAtendimento: IdAtendimento | string | null,
            quando: string | null,
            previa?: { texto: string; isFromMe: boolean } | null,
        ) {
            if (!idAtendimento) return;

            const chat = this.chatList.find(c => c.id === idAtendimento);
            if (!chat) return;

            const instante = quando ? new Date(quando) : new Date();
            if (isNaN(instante.getTime())) return;

            const atual = chat.time ? new Date(chat.time).getTime() : 0;
            if (instante.getTime() <= atual) return;

            chat.time = instante.toISOString();

            // A prévia vem DEPOIS da guarda de propósito: a fila offline do
            // WhatsApp reentrega o `terminate` de uma ligação antiga depois de
            // toda reconexão, e escrever a prévia antes fazia o card voltar a
            // exibir a ligação de ontem por cima de uma mensagem recebida
            // ontem à noite — com a hora, corretamente, recusada.
            //
            // `__upsertChat` preserva `lastMessage`, então nenhum evento de
            // atendimento desfaz isto até o próximo `fetchChats`; por isso ela
            // não pode entrar quando o carimbo não entrou.
            if (previa) {
                chat.lastMessage = {
                    sender: null,
                    text: previa.texto,
                    type: 'text',
                    ogType: 'LIGACAO',
                    isFromMe: previa.isFromMe,
                    status: 'sent',
                } as typeof chat.lastMessage;
            }
        },

        _handleUpsertAtendimento(payload: AtendimentoAtualizadoPayload) {
            const updatedAtendimento = payload;

            const inboxPertenceAContaAtual = this.userAuthStore.currentAccountInboxes.some(
                inbox => inbox.id === payload.idInbox
            );
            if (!inboxPertenceAContaAtual) return;

            if (payload.dataFinalizacao !== null) return;

            const chat = MakeChat.FromAtendimento({
                ...updatedAtendimento,
                atendentes: payload.atendentes as ResponseUsuario[],
                contato: payload.contato,
            })

            this.__upsertChat(chat);

            // `marcadoNaoLido` só vem quando o servidor leu o contador real (confirmação
            // de leitura ou marcação manual). Aí o `naoLidas` vale mais que o `max` do
            // upsert — sem isto, a leitura feita em outro cliente nunca zeraria aqui.
            if (payload.marcadoNaoLido !== undefined) {
                const foundChat = this.chatList.find(c => c.id === chat.id);
                if (foundChat) foundChat.unreadCount = payload.naoLidas ?? 0;
            }
        },

        /**
         * Marca a lista de participantes de um grupo como vencida.
         *
         * O evento de grupo atualizado não carrega participantes, então mudança de
         * papel (admin) ou de composição só aparece se o cache local vencer — o
         * servidor já invalida o dele ao receber o mesmo evento.
         *
         * NÃO esvazia a lista, só zera o carimbo. Esvaziar era pior que o problema:
         * `GRUPO_ATUALIZADO` chega como eco do próprio `PATCH /leitura` que o
         * `selectChat` dispara, então abrir um grupo apagava a lista recém-buscada — e
         * como só `selectChat` e o preview de arquivo consultam
         * `participantsNeedRefresh`, nada a repunha. A barra lateral passava a dizer
         * "Nenhum participante carregado", o autocomplete de menção esvaziava e as
         * tarjas que dependem da lista sumiam. Sendo broadcast por inbox, um usuário
         * lendo o grupo fazia isso para todos os outros.
         *
         * Lista vencida ainda é utilizável; lista vazia não é.
         */
        _invalidarParticipantesDoGrupo(idGrupo: string) {
            const chat = this.chatList.find(c => c.id === idGrupo);
            if (!chat?.isGroup) return;

            chat.participantsFetchedAt = undefined;

            // No chat aberto ninguém mais vai perguntar se precisa recarregar — o
            // `selectChat` já passou. Recarrega aqui, mantendo a lista antiga visível
            // enquanto a nova não chega.
            if (this.activeChatId === idGrupo && this.chatContext != 'team') {
                void MakeChat.FetchAndPopulateWithParticipants(chat);
            }
        },

        _handleFinishedAtendimento(payload: AtendimentoAtualizadoPayload) {
            const inboxPertenceAContaAtual = this.userAuthStore.currentAccountInboxes.some(
                inbox => inbox.id === payload.idInbox
            );
            if (!inboxPertenceAContaAtual) return;
            this.chatList = this.chatList.filter(a => a.id != payload.id)
        },

        _handleUpsertGrupo(payload: GrupoCriadoPayload) {
            const updatedGrupo = payload;

            const chat = MakeChat.FromGrupo(updatedGrupo);
            const permissoesStore = usePermissoesStore();
            const authStore = useAuthStoreUser();

            const idConta = authStore.currentConta?.id;
            if (!idConta) return;

            const temPermissao = permissoesStore.temPermissao(
                'inbox.ver_grupos',
                idConta,
                payload.idInbox
            );

            if (!temPermissao) return;

            // Grupo restrito (whitelist ativa): só é visível para quem tem a permissão
            // de conta ver_grupos_restritos ou está na whitelist do grupo. Espelha o
            // filtro do backend em GruposService.buscarGrupos. Sem isto, o broadcast por
            // inbox (GRUPO_ATUALIZADO/GRUPO_CRIADO) reexibia o grupo oculto após qualquer
            // atualização (ex.: envio de mensagem no inbox). Mesma regra do load inicial
            // (populateChat) via _podeVerGrupoRestrito.
            if (!this._podeVerGrupoRestrito(payload)) {
                // Também remove da lista caso já esteja presente (ex.: grupo acabou de virar restrito).
                this.chatList = this.chatList.filter(c => c.id !== payload.id);
                return;
            }

            const inboxPertenceAContaAtual = this.userAuthStore.currentAccountInboxes.some(
                inbox => inbox.id === payload.idInbox
            );
            if (!inboxPertenceAContaAtual) return;

            this.__upsertChat(chat);
        },

        _handleDeletedGrupo(payload: GrupoRemovidoPayload) {
            console.log('[_webhookDeletedGrupo] (todo)', payload);
            this.chatList = this.chatList.filter(c => c.id != payload.id);
        },
        // #endregion

        async _fetchAndPopulateUsuariosContas(rawIdContas: Record<string, boolean>) {

            const idContas = Array.from(Object.keys(rawIdContas));

            const getAllUsersConta = async (idConta: string) => {
                const usuariosDessaConta = await this.contaService.getUsuariosVinculados(idConta);
                const userMapForConta = usuariosDessaConta.reduce((map, user) => map.set(user.id, user), new Map<string, ResponseUsuario>());
                return { idConta, userMapForConta }
            }

            const allUserMapForContas = await Promise.all(idContas.map(idConta => getAllUsersConta(idConta)));
            for (const userMapSet of allUserMapForContas) {
                this.MapContaToUserMap.set(userMapSet.idConta, userMapSet.userMapForConta)
            }
        },

        /**
         * @param podarAusentes Remove do `inboxMap` os inboxes que NÃO vieram nesta
         * chamada. Só pode ser ligado por quem passa a lista COMPLETA do usuário
         * (hoje: `chatStore.init`) — as outras chamadas recarregam um inbox só, e
         * podar nelas esvaziaria o mapa inteiro.
         *
         * Existe porque `inboxMap` só crescia: nada nunca removia uma entrada. Ao
         * desvincular um usuário de um inbox, o token novo já não o traz, mas a
         * entrada antiga sobrevivia na memória — e `InboxModal` lê exatamente daí
         * (`connectedInboxesStatus`), então continuava listando um inbox a que o
         * usuário perdeu acesso, até recarregar a página.
         */
        async _fetchAndPopulateInboxes(rawIdInboxes: Record<string, unknown> | string[], idsNaoConectadas: string[] = [], podarAusentes = false) {
            const idInboxes = Array.isArray(rawIdInboxes) ? rawIdInboxes : Array.from(Object.keys(rawIdInboxes));
            // Inclui as inboxes Meta criadas-porém-não-conectadas (admin) para que
            // apareçam no InboxModal com o botão "Conectar" — o getById as retorna
            // sem sub-registro de integração, e o modal já trata esse caso.
            const naoConectadasSet = new Set(idsNaoConectadas);
            const todosIds = Array.from(new Set([...idInboxes, ...idsNaoConectadas]));

            // Poda ANTES de buscar: o desvínculo tem que sumir da tela mesmo que
            // alguma das consultas abaixo falhe. Guarda explícita contra chamada
            // parcial — podar com a lista de um inbox só zeraria o mapa.
            if (podarAusentes) {
                const permitidos = new Set(todosIds);
                for (const idExistente of Array.from(this.inboxMap.keys())) {
                    if (!permitidos.has(idExistente)) {
                        console.debug(`[chatStore] inbox ${idExistente} saiu do escopo do usuário — removido do inboxMap`);
                        this.inboxMap.delete(idExistente);
                    }
                }
            }

            const inboxesWithStatus = await Promise.all(todosIds.map(
                async idInbox => {
                    // Um único GET /status: `connected` é o agregado e, em
                    // coexistência, os dois campos extras dizem qual metade caiu.
                    const status = await this.inboxService.getStatusConexaoOuIndeterminado(idInbox);
                    return {
                        inbox: await this.inboxService.getById(idInbox),
                        // `null` = status indeterminado (a consulta falhou). Ver abaixo.
                        isConnected: status?.connected ?? null,
                        metaConectado: status?.metaConectado,
                        evolutionConectado: status?.evolutionConectado,
                        // `status === null` é a consulta que NÃO respondeu — diferente de
                        // uma resposta dizendo "desconectado". Preservado porque
                        // `isConnected` colapsa os dois em `false`.
                        statusIndeterminado: status === null,
                        dados: await this.inboxService.getDadosDetalhados(idInbox),
                    };
                }
            ));
            inboxesWithStatus.forEach(group => {
                if (group.inbox == null) return;
                const anterior = this.inboxMap.get(group.inbox.id);

                // Status INDETERMINADO (a consulta não respondeu) não é "desconectado".
                // Colapsar os dois em `false` fazia a recarga logo depois de conectar
                // uma inbox Meta — quando o GET /status ainda dá 404, porque o token do
                // usuário só passa a carregar a inbox quando o cache de permissões
                // expira — REBAIXAR para "desconectado" uma conexão que acabara de ser
                // confirmada. A tela então oferecia "Reconectar" para algo já conectado.
                // Sem resposta, o certo é MANTER o que já se sabia; `statusIndeterminado`
                // segue marcado para quem precisa distinguir os dois casos.
                const isConnected = group.statusIndeterminado
                    ? (anterior?.isConnected ?? false)
                    : group.isConnected === true;

                this.inboxMap.set(group.inbox.id, {
                    ...{ id: makeIdInbox(''), nome: '', ausente: false, comercial: false, idConta: makeIdConta('') },
                    ...group.inbox,
                    ...{ isConnected },
                    // Mesma regra para as metades da coexistência: sem resposta, o que
                    // valia antes continua valendo (`undefined` some da UI e apagaria
                    // o estado das duas metades de uma vez).
                    metaConectado: group.statusIndeterminado ? anterior?.metaConectado : group.metaConectado,
                    evolutionConectado: group.statusIndeterminado ? anterior?.evolutionConectado : group.evolutionConectado,
                    statusIndeterminado: group.statusIndeterminado,
                    dados: group.dados,
                });
            });

            // Semeia avisos para inboxes já desconectados no login, sem depender
            // de o operador entrar no atendimento para perceber a queda. Inboxes
            // NUNCA conectadas (aguardando vínculo Meta) não são "quedas" — não alertar.
            // Status indeterminado (`null`) também não: acontece quando GET /status
            // falha, p.ex. no 404 logo após conectar uma inbox Meta (o token do
            // usuário ainda não a carrega) — antes isso virava "false" e disparava
            // o alerta "Inbox desconectado" numa inbox que acabara de conectar.
            const inboxAlertStore = useInboxAlertStore();
            inboxesWithStatus.forEach(group => {
                if (group.inbox == null || group.isConnected !== false) return;
                if (naoConectadasSet.has(group.inbox.id)) return;
                inboxAlertStore.notifyDisconnected(group.inbox.id, group.inbox.nome);
            });
        },

        // Espelha o filtro de grupos restritos do backend (GruposService.buscarPorUsuario)
        // e da via websocket (_handleUpsertGrupo). Grupo restrito (whitelistEnabled) só é
        // visível para quem tem conta.ver_grupos_restritos ou está na whitelist. Defesa em
        // profundidade: se o backend regredir e devolver um grupo restrito indevido no load,
        // o front não o exibe.
        _podeVerGrupoRestrito(grupo: { whitelistEnabled?: boolean; whitelist?: { id: string }[] }): boolean {
            if (!grupo.whitelistEnabled) return true;

            const permissoesStore = usePermissoesStore();
            const authStore = useAuthStoreUser();

            const idConta = authStore.currentConta?.id;
            if (!idConta) return false;

            const temAcessoRestritos = permissoesStore.temPermissao('conta.ver_grupos_restritos', idConta);
            const idUsuario = authStore.user?.id;
            const estaNaWhitelist = !!idUsuario && (grupo.whitelist?.some(u => u.id === idUsuario) ?? false);

            return temAcessoRestritos || estaNaWhitelist;
        },

        populateChat(atendimentosInput: FullInfoAtendimento | FullInfoAtendimento[], gruposInput: FetchGroupsWrapper): Chat[] {
            if (!Array.isArray(atendimentosInput)) atendimentosInput = [atendimentosInput];

            const mappedAtendimentos = atendimentosInput.map(wrappedAtendimento => MakeChat.FromAtendimento(wrappedAtendimento));
            const mappedGroups = gruposInput.dados
                .filter(wrappedGrupo => this._podeVerGrupoRestrito(wrappedGrupo))
                .map(wrappedGrupo => MakeChat.FromGrupo(wrappedGrupo));

            const chats = [...mappedAtendimentos, ...mappedGroups];
            return chats;
        },

        async fetchContatoIfNotPresent(idContato?: IdContato): Promise<ResponseContato | undefined> {
            if (!idContato) return undefined;

            const found = this.contacts.find(c => c.id == idContato);
            if (found) return found;

            const fetched = await this.contactService.getById(idContato);
            if (!fetched) return undefined;

            this.contacts.push(fetched);
            return fetched;
        },

        async init(force?: boolean) {
            if (this.initialized && !force) return;
            console.log(`[init|chatStore]`)

            if (!this.userAuthStore.initialized) {
                await this.userAuthStore.init();
            }

            const contas = this.userAuthStore.contas;
            const inboxes = this.userAuthStore.inboxes;
            const idsNaoConectadas = (this.userAuthStore.inboxesNaoConectadas ?? []).map(i => i.id);
            const idInboxesList = Array.isArray(inboxes) ? inboxes : Object.keys(inboxes);
            await Promise.all([
                this._fetchAndPopulateUsuariosContas(contas),
                // ÚNICO ponto que poda: aqui `inboxes` é a lista completa do token.
                // Num `init(true)` disparado por desvínculo, é o que tira do mapa o
                // inbox que o usuário deixou de ter.
                this._fetchAndPopulateInboxes(inboxes, idsNaoConectadas, true),
                this._initWebhook(),
                this._carregarInboxesComCanais(idInboxesList as IdInbox[]),
            ])

            if (this.userAuthStore.user) {
                this.user = {
                    id: this.userAuthStore.user.id,
                    nome: this.userAuthStore.user.nome,
                    email: this.userAuthStore.user.email
                } as ResponseUsuario;
            } else {
                console.log(this.userAuthStore);
                throw new Error('Store inicializada sem um usuário autenticado.')
            }

            // 🆕 Filtra inboxes da conta atual
            const idConta = this.userAuthStore.currentConta?.id;
            const inboxesDaConta = idConta
                ? this.userAuthStore.currentAccountInboxes.map(i => i.id)
                : [];

            // 🆕 Cria filtro com inboxes da conta
            const filtroComConta = {
                ...this.chatFilter,
                idsInboxes: inboxesDaConta.length > 0 ? inboxesDaConta : [],
            } as FiltroAtendimentos;

            const [rawChat, rawGroups] = await Promise.all([
                this.chatService.getAllFullInfo(filtroComConta, this.inboxesComCanaisCarregado ? this.inboxesComCanais : undefined),
                this.groupService.getByUsuario(1000, 1, inboxesDaConta)  // 🆕 Passa inboxes
            ]);
            this.chatList = this.populateChat(rawChat, rawGroups);

            if (idConta) {
                const result = await this.contactService.getByContaPaginated(idConta, 1, 100);
                this.contacts = result.dados;
            } else {
                this.contacts = [];
            }

            this.setupFilters();

            const idUsuario = this.userAuthStore.user?.id;
            if (!idUsuario) throw new Error(`[chatStore|init] this.userAuthStore.user?.id was undefined`)
            this.setupSounds(idUsuario);
            this.setupDrafts(idUsuario);

            this.initialized = true;
        },

        async focusAtendimento(idAtendimento: IdAtendimento, msgOnOpen?: string) {
            // 1. Close ContactsModal
            this.uiStore.closeContactsModal();
            // 2. Goto list mode
            this.uiStore.setActiveTab('Lista');
            // 3. Wait a bit
            await nextTick();
            // 4. Execute selectAtendimento action
            await this.selectChat(idAtendimento);
            // 5. gg
            if (msgOnOpen) toast.success(msgOnOpen);
        },

        async criarAtendimento(idContato: IdContato, idInbox: IdInbox, idCanal?: IdCanal) {
            try {
                const res = await this.chatService.create({ idContato, idInbox, idCanal, iniciar: true });
                await this.refreshAtendimentos();
                toast.success('Atendimento iniciado!');
                return res;
            } catch (e) {
                console.error(e);
                toast.error((e as Error).message || 'Não foi possível abrir o atendimento.')
            }
        },

        async batchCriarAtendimento(itens: { idContato: IdContato; idInbox: IdInbox }[], supressRefresh: boolean = false) {
            const CHUNK_SIZE = 10;
            let falhas = 0;
            for (let i = 0; i < itens.length; i += CHUNK_SIZE) {
                const chunk = itens.slice(i, i + CHUNK_SIZE);
                const results = await Promise.allSettled(chunk.map(({ idContato, idInbox }) => this.chatService.create({ idContato, idInbox, iniciar: true })));
                results.forEach((r, idx) => {
                    if (r.status === 'rejected') {
                        falhas++;
                        console.error('[batchCriarAtendimento] Falha ao criar atendimento', chunk[idx], r.reason);
                    }
                });
            }
            if (!supressRefresh) await this.refreshAtendimentos();
            if (falhas > 0) toast.error(`${falhas} ${falhas === 1 ? 'atendimento não pôde ser criado' : 'atendimentos não puderam ser criados'}.`);
        },

        /**
         * Atendente de OUTRA pessoa num atendimento em espera — o "transferir e
         * finalizar" cria o atendimento novo em `Criado` já no nome do receptor, e
         * o root o enxerga. Abrir um desses é tomá-lo: o servidor grava uma
         * transferência para quem abriu, então a UI confirma antes.
         */
        atendenteDeOutroEmEspera(idAtendimento: IdAtendimento): Atendente | null {
            const chat = this.chatList.find(c => c.id == idAtendimento);
            if (!chat || chat.status != 'Criado') return null;
            const idUsuario = this.userAuthStore.user?.id;
            return chat.atendentes?.find(a => a.id != idUsuario) ?? null;
        },

        /** `null` quando o usuário cancela a confirmação de assumir o atendimento. */
        async abrirAtendimento(idAtendimento: IdAtendimento, supressRefresh: boolean = false, body?: { emLote?: boolean }) {
            try {
                const atendenteAtual = this.atendenteDeOutroEmEspera(idAtendimento);
                const chat = this.chatList.find(c => c.id == idAtendimento);
                if (atendenteAtual && chat && !await PromptToAssumirAtendimento(chat, atendenteAtual)) {
                    toast.info('Ação cancelada.');
                    return null;
                }

                const res = await this.chatService.iniciarAtendimento(idAtendimento, body);
                if (!supressRefresh) await this.refreshAtendimentos();
                toast.success('Atendimento aberto!');
                return res;
            } catch (e) {
                console.error(e);
                toast.error((e as Error).message || 'Não foi possível abrir o atendimento.')
            }
        },

        async restaurarAtendimento(idAtendimento: IdAtendimento, supressRefresh: boolean = false) {
            try {
                const res = await this.chatService.restaurarAtendimento(idAtendimento);
                if (!supressRefresh) await this.refreshAtendimentos();
                toast.success('Atendimento devolvido à fila.');
                return res;
            } catch (e) {
                console.error(e);
                toast.error((e as Error).message || 'Não foi possível devolver o atendimento à fila.')
            }
        },

        async transferirAtendimento(idAtendimento: IdAtendimento, idUsuarioReceptor: IdUsuario, idContato: IdContato, idInbox: IdInbox, options: { enviarMensagemTransferencia: boolean, mensagemTransferencia?: string, finalizarAtendimento: boolean, idCanal: IdCanal|null, supressToast?: boolean, emLote?: boolean, tornarAtendenteFixo?: boolean } = { enviarMensagemTransferencia: false, finalizarAtendimento: false, idCanal: null }) {
            try {
                let res;
                if (options.enviarMensagemTransferencia) {
                    if (!options.mensagemTransferencia)
                        return toast.warning('Insira uma mensagem de transferência ou desmarque a opção correspondente.')

                    const contato = await this.contactService.getById(idContato);
                    if (!contato)
                        return toast.error("Contato não encontrado.");

                    const prevSignatureEnabledStatus = this.isSignatureEnabled;
                    this.isSignatureEnabled = false;
                    await this.messageService.enviarTexto({ codigo: contato.codigo, grupo: false, idInbox, mensagem: options.mensagemTransferencia })
                    this.isSignatureEnabled = prevSignatureEnabledStatus;
                }
                if (options.finalizarAtendimento) {
                    // Finalizar + transferir: o atendimento antigo é encerrado e a
                    // transferência tem de recair sobre o NOVO atendimento, não sobre o
                    // que acabou de ser finalizado.
                    //
                    // `POST /atendimentos` com `iniciar: true` sempre vincula o CRIADOR
                    // (`iniciarAtendimentoFromIdUsuario(id, usuario.id)` no servidor), então
                    // o destino escolhido só era respeitado quando ele mesmo era o usuário
                    // logado — em qualquer outro caso o atendimento nascia no nome de quem
                    // clicou. Sem esta transferência o ramo também descartava `idCanal` e
                    // `tornarAtendenteFixo`.
                    //
                    // `iniciar: false` mantém o novo atendimento em "Criado" — é esse status
                    // que faz o item piscar (`ChatItem.shouldPulsate`). Com `iniciar: true` o
                    // servidor o passava a "Em andamento" no nome de quem transferiu, e o
                    // receptor recebia uma conversa já iniciada, sem sinal de novo atendimento.
                    // A visibilidade não muda: ela é decidida por `atendentes`, e a
                    // transferência abaixo vincula o receptor.
                    await this.chatService.finalizarAtendimento(idAtendimento);
                    const novoAtendimento = await this.chatService.create({ iniciar: false, idContato, idInbox });
                    res = await this.chatService.transferirAtendimento(novoAtendimento.id, idUsuarioReceptor, options.idCanal, { emLote: options.emLote, tornarAtendenteFixo: options.tornarAtendenteFixo });
                } else res = await this.chatService.transferirAtendimento(idAtendimento, idUsuarioReceptor, options.idCanal, { emLote: options.emLote, tornarAtendenteFixo: options.tornarAtendenteFixo });

                await this.refreshAtendimentos();
                if (options.supressToast !== true) toast.success('Atendimento transferido!');
                return res;
            } catch (e) {
                console.error(e);
                toast.error((e as Error).message || 'Não foi possível transferir o atendimento.')
            }
        },

        async finalizarAtendimentoAtivo(idAtendimento: IdAtendimento, conversaoPayload?: { conversao?: boolean; conversaoValor?: number }) {
            try {
                // Lendário finalizar atendimento
                const res = await this.chatService.finalizarAtendimento(idAtendimento, conversaoPayload);
                this.chatList = this.chatList.filter(c => c.id !== idAtendimento);
                if (this.activeChatId === idAtendimento) {
                    this.activeChatId = null;
                    this.messages = [];
                    this.isProfileOpen = false;
                }
                toast.success('Atendimento finalizado!');
                return res;
            } catch (e) {
                console.error(e);
                toast.error((e as Error).message || 'Não foi possível finalizar o atendimento.')
            }
        },

        async batchTransferirAtendimento(idAtendimento: IdAtendimento|IdAtendimento[], idUsuarioReceptor: IdUsuario, options?: { tornarAtendenteFixo?: boolean }) {
            if (!Array.isArray(idAtendimento)) idAtendimento = [idAtendimento];
            try {
                for (const idChat of idAtendimento) {
                    const foundChat = this.chatsById.get(idChat);
                    if (!foundChat || !foundChat.idContato) {
                        console.error('[batchTransferirAtendimento] Atendimento não encontrado ou sem idContato', idChat, foundChat)
                        toast.error(`O atendimento de ID #${idChat} não foi encontrado ou não tem um contato vinculado.`);
                        continue;
                    }

                    await this.transferirAtendimento(
                        foundChat.id as IdAtendimento,
                        idUsuarioReceptor,
                        foundChat.idContato,
                        foundChat.idInbox,
                        {
                            enviarMensagemTransferencia: false,
                            finalizarAtendimento: false,
                            idCanal: foundChat.idCanal ?? null,
                            supressToast: true,
                            emLote: true,
                            tornarAtendenteFixo: options?.tornarAtendenteFixo,
                        }
                    );
                }
            } catch (e) {
                console.error(e);
                toast.error((e as Error).message || `Não foi possível transferir ${idAtendimento.length == 1 ? 'o atendimento' : 'os atendimentos'}.`);
            }
        },

        /** `false` quando o usuário cancela a confirmação de assumir os atendimentos de outros. */
        async batchAbrirAtendimento(idAtendimentos: IdAtendimento[], supressRefresh: boolean = false): Promise<boolean> {
            const deOutros = idAtendimentos.filter(id => this.atendenteDeOutroEmEspera(id)).length;
            if (deOutros > 0 && !await PromptToAssumirAtendimentosEmLote(deOutros)) {
                toast.info('Ação cancelada.');
                return false;
            }

            const CHUNK_SIZE = 10;
            let falhas = 0;
            for (let i = 0; i < idAtendimentos.length; i += CHUNK_SIZE) {
                const chunk = idAtendimentos.slice(i, i + CHUNK_SIZE);
                const results = await Promise.allSettled(chunk.map(id => this.chatService.iniciarAtendimento(id, { emLote: true })));
                results.forEach((r, idx) => {
                    if (r.status === 'rejected') {
                        falhas++;
                        console.error('[batchAbrirAtendimento] Falha ao abrir atendimento', chunk[idx], r.reason);
                    }
                });
            }
            if (!supressRefresh) await this.refreshAtendimentos();
            if (falhas > 0) toast.error(`${falhas} ${falhas === 1 ? 'atendimento não pôde ser aberto' : 'atendimentos não puderam ser abertos'}.`);
            return true;
        },

        async batchFinalizarAtendimento(idAtendimento: IdAtendimento|IdAtendimento[], options: { cleanupChatStore: boolean, mensagemFinalizacao?: string, avaliacao: boolean, assunto?: string } = {cleanupChatStore: true, avaliacao: false}) {
            if (!Array.isArray(idAtendimento)) idAtendimento = [idAtendimento];
            try {
                console.log(`[batchFinalizarAtendimento] todo: avaliacao e vincular assunto (??????)`);
                const { handleSendMessage } = getSendMessageStrategy('TEXT');

                const currContaId = this.userAuthStore.currentConta?.id;
                if (!currContaId) throw new Error(`[batchFinalizarAtendimento] this.userAuthStore.currentConta?.id was undefined`);
                const botStore = useBotsStore()
                await botStore.init(currContaId);

                const prevSignatureEnabledStatus = this.isSignatureEnabled;
                this.isSignatureEnabled = false;
                const res = options.mensagemFinalizacao
                    ? await Promise.all(idAtendimento.map(async id => {
                        const foundChat = this.chatsById.get(id);
                        if (!foundChat) {
                            return toast.error(`Atendimento de ID #${id} não encontrado.`);
                        }

                        await handleSendMessage({text: options.mensagemFinalizacao ?? '', mentioned: [], substituirVariaveis: true, ...this.getCommonPayloadData(foundChat)} as SendTextMessagePayload);

                        if (!options.avaliacao)
                            return await this.chatService.finalizarAtendimento(id, { emLote: true });

                        const botsDisponiveis = botStore.bots.filter(b =>
                            !b.del &&
                            b.tipo === 'AVALIACAO' &&
                            b.inbox.id === foundChat.idInbox
                        );
                        const botIdSelecionado = botsDisponiveis[0]?.id;

                        if (botIdSelecionado)
                            await this.dispararBot(foundChat.id as IdAtendimento, botIdSelecionado);
                        else
                            await this.chatService.finalizarAtendimento(id, { emLote: true })

                    }))
                    : await Promise.all(idAtendimento.map(async id => await this.chatService.finalizarAtendimento(id, { emLote: true })));
                this.isSignatureEnabled = prevSignatureEnabledStatus;

                if (!options.cleanupChatStore) return res;

                this.chatList = this.chatList.filter(c => !idAtendimento.includes(c.id as IdAtendimento));

                if (this.activeChatId && idAtendimento.includes(this.activeChatId as IdAtendimento)) {
                    this.activeChatId = null;
                    this.messages = [];
                    this.isProfileOpen = false;
                }

                return res;
            } catch (e) {
                console.error(e);
                toast.error((e as Error).message || `Não foi possível finalizar ${idAtendimento.length == 1 ? 'o atendimento' : 'os atendimentos'}`);
            }
        },

        async batchSendMessage(idAtendimento: IdAtendimento|IdAtendimento[], templatedMessage: string, options: { sign: boolean } = { sign: true }) {
            if (!Array.isArray(idAtendimento)) idAtendimento = [idAtendimento];
            try {
                const prevSignatureEnabledStatus = this.isSignatureEnabled;
                this.isSignatureEnabled = options.sign;

                const { getLastMessage, getNewMessage, handleSendMessage } = getSendMessageStrategy('TEXT');

                for (const idChat of idAtendimento) {
                    const foundChat = this.chatsById.get(idChat);
                    if (!foundChat) {
                        toast.error(`Atendimento de id #${idChat} não encontrado.`);
                        continue;
                    }

                    const payload: SendTextMessagePayload = { text: templatedMessage.replace('{nome}', foundChat.name), mentioned: [], ...this.getCommonPayloadData(foundChat) };

                    const newMessage: Message = getNewMessage(options.sign, payload);
                    // 5. Atualizar a lastMessage do chat em questão
                    if (foundChat) {
                        foundChat.time = new Date().toISOString();
                        foundChat.lastMessage = getLastMessage(payload);
                    }

                    // 6. Envio especifico
                    const { idMensagem } = await handleSendMessage(payload)

                    // 7. Atualização da Mensagem em thisCtx.messages (🕒 => ✔️)
                    if (foundChat?.lastMessage)
                        Object.assign(foundChat.lastMessage, { status: 'sent' });
                    Object.assign(newMessage, { id: idMensagem, readStatus: 'sent' });

                }
                this.isSignatureEnabled = prevSignatureEnabledStatus;
            } catch (e) {
                console.error(e);
                toast.error((e as Error).message || `Não foi possível enviar a mensagem.`);
            }
        },

        async dispararBot(idAtendimento: IdAtendimento, idBot: IdBot) {
            return await this.chatService.dispararBotNoAtendimento(idAtendimento, idBot);
        },

        async interromperBot(idAtendimento: IdAtendimento) {
          return await this.chatService.interromperBot(idAtendimento);
        },

        async refreshAtendimentos() {
            return await this.fetchChats();
        },

        async updateInbox(inbox: UpdateInbox) {
            const res = await this.inboxService.update(inbox);
            await this._fetchAndPopulateInboxes([String(inbox.id)])
            return res;
        },

        async removerAtendentesAtendimento(atendimento: Chat) {
            return await this.updateAtendentesChat(atendimento, [])
        },

        async updateAtendentesChat(chat: Chat, newAtendenteList: ResponseUsuario[]) {
            const usuarioAtendimentoService = new UsuarioAtendimentoService()
            const currentAtendentesBound    = await usuarioAtendimentoService.getByAtendimento(chat.id as IdAtendimento);
            const currentAtendentesIdMap    = currentAtendentesBound.reduce((map, el) => map.set(el.idUsuario, el.id), new Map())
            const newAtendentesIdMap        = newAtendenteList.reduce((map, el) => map.set(el.id, true), new Map())

            for (const newOrKeptAtt of newAtendenteList) {
                if (currentAtendentesIdMap.has(newOrKeptAtt.id)) continue;
                else {
                    await usuarioAtendimentoService.create({ idAtendimento: chat.id as IdAtendimento, idUsuario: newOrKeptAtt.id })
                    chat.atendentes.push({ id: newOrKeptAtt.id, name: newOrKeptAtt.nome, initials: extractInitials(newOrKeptAtt.nome) })
                }
            }
            for (const removedAtt of currentAtendentesBound) {
                if (newAtendentesIdMap.has(removedAtt.idUsuario)) continue;
                else {
                    await usuarioAtendimentoService.delete(removedAtt.id);
                    const idx = chat.atendentes.findIndex(c => c.id == removedAtt.idUsuario)
                    if (idx >= 0) chat.atendentes.splice(idx, 1)
                }
            }
            // console.log('final =>', chat.atendentes)
        },

        async updateQuadroChat(idChat: IdAtendimento|IdGrupo, idQuadro: IdQuadro | null) {
            const res = await this.chatService.update({ id: idChat as IdAtendimento, idQuadro });
            const foundChat = this.chatList.find(el => el.id == idChat);
            if (foundChat) foundChat.idQuadro = idQuadro;
            return res;
        },

        async updateQuadroEAtendentesChat(idChat: string, idQuadro: string | null, atendentes: null | (Atendente[]), enviarMensagemMovimentacao: boolean = false) {
            const idsAtendentes = atendentes ? atendentes.map(a => a.id) : null;
            await this.chatService.patchIdQuadroEAtendentes(idChat, idQuadro, idsAtendentes, enviarMensagemMovimentacao);
            const foundChat = this.chatList.find(el => el.id == idChat);
            if (foundChat) Object.assign(foundChat, { idQuadro, atendentes: atendentes && atendentes.length > 0 ? atendentes : null })
        },

        async fetchChats() {
            this.isLoadingChats = true;

            if (!this.initialized) {
                await this.init();
            }

            const idConta = this.userAuthStore.currentConta?.id;
            if (!idConta) {
                console.warn("[ChatStore] Nenhuma conta selecionada");
                this.isLoadingChats = false;
                return;
            }

            // 🆕 FILTRA os inboxes para apenas da conta atual
            const inboxesDaConta = this.userAuthStore.currentAccountInboxes.map(i => i.id);

            // Atualiza quais inboxes têm canais (pode ter mudado desde o init)
            await this._carregarInboxesComCanais(inboxesDaConta as IdInbox[]);

            // 🆕 Cria um filtro com apenas os inboxes da conta
            const filtroComConta = {
                ...this.chatFilter,
                idsInboxes: inboxesDaConta.length > 0 ? inboxesDaConta : [],
            } as FiltroAtendimentos;

            const [rawChat, rawGroups] = await Promise.all([
                this.chatService.getAllFullInfo(filtroComConta, this.inboxesComCanaisCarregado ? this.inboxesComCanais : undefined),
                this.groupService.getByUsuario(1000, 1, inboxesDaConta)  // 🆕 Passa inboxes
            ]);
            this.chatList = this.populateChat(rawChat, rawGroups);

            this.isLoadingChats = false;
        },

        async reloadForConta() {
            // Resetar estado
            this.chatList = [];
            this.activeChatId = null;
            this.messages = [];
            this.initialized = true;

            // Recarregar
            await this.fetchChats();
        },

        // Recovery after a websocket blip: reload the chat LIST and, crucially,
        // the OPEN conversation's thread. fetchChats alone only fixes the list —
        // messages missed inside the chat the user is staring at would otherwise
        // stay invisible until they leave and reopen it.
        async resync() {
            // Um gap de socket engole eventos, e o `paused` engolido deixaria
            // "escrevendo..." preso na tela até o TTL. Zera antes de recarregar.
            limparTodasPresencasChat();

            await Promise.all([
                this.fetchChats(),
                this.resyncActiveChat(),
            ]);
        },

        async resyncActiveChat() {
            const chatId = this.activeChatId;
            if (!chatId) return;

            try {
                this.messagePage = 1;
                const whizMessages = await this.resolveFetchMessages(false, 'resyncActiveChat');
                const messages = WhizMessagesToMessagesWithReaction(whizMessages);
                messages.reverse(); // API vem desc; alinha com a ordem da tela

                // Preserva mensagens pendentes/erro (RAM + HD), como em selectChat
                const pendingRam = this.localPendingMessages.get(chatId as string) || [];
                const pendingDB = await getPendingMessagesFromDB(chatId as string);
                const mapUnicos: Map<IdMensagem, Message> = new Map();
                pendingRam.forEach(m => mapUnicos.set(m.id, m));
                pendingDB.forEach(m => mapUnicos.set(m.id, m));
                const mensagensPendentes = semDuplicataDaApi(Array.from(mapUnicos.values()), messages);

                // Usuário pode ter trocado de chat durante o await — não sobrescreve
                if (this.activeChatId !== chatId) return;

                this.messages = [...messages, ...mensagensPendentes].sort((a, b) => {
                    return new Date(a.time).getTime() - new Date(b.time).getTime();
                });

                void this.carregarMensagensFixadas();
                void this.carregarAnotacoesInternas();
            } catch (e) {
                console.warn('[chatStore] resyncActiveChat falhou', e);
            }
        },

        async resolveMarkChatAsRead(chatId: IdAtendimento|IdGrupo, isGroup?: boolean) {
            const chat = this.chatList.find(c => c.id === chatId);
            if (chat) {
                chat.unreadCount = 0;
                chat.isMarkedUnread = false; // leitura limpa a marcação manual (backend deleta a flag)
            }

            // if (this.activeChatId === chatId) {
            //     this.initialUnreadCount = 0;
            // }

            if (isGroup === undefined) isGroup = this.isGroupChat;
            if (isGroup) {
                await this.groupService.marcarGrupoComoLido(chatId as IdGrupo);
            } else {
                await this.chatService.marcarAtendimentoComoLido(chatId as IdAtendimento);
            }
        },

        async selectChat(chatId: IdAtendimento|IdGrupo) {
            const uiStore = useUiStore();

            if (this.activeChatId === chatId) {

                if (uiStore.activeTab === 'Kanban' || uiStore.activeTab === 'Radar') {
                    uiStore.openChatModal();
                }
                else if (this.chatContext !== 'team') {
                    this.isProfileOpen = true;
                }

                // Reclicar o chat que já está em prévia não pode marcá-lo como
                // lido: a fila continua devendo esse atendimento a alguém.
                if (!this.estaEmPreviaDeAtendimento) await this.resolveMarkChatAsRead(chatId)
                return;
            }

            this.initialUnreadCount = 0;

            const foundChat = this.chatList.find(c => c.id == chatId);
            // `emEspera`: atendimento criado e ainda sem atendente vinculado — o
            // item que pisca na lista. É o único caso que exige uma decisão
            // antes de entrar no chat.
            const emEspera = !!foundChat
                && foundChat.status == 'Criado'
                && !foundChat.isGroup
                && (!foundChat.idChatBotIa || foundChat.statusIntegracaoIa == 'EM_ATENDIMENTO_HUMANO');

            // Entrar em prévia é o que este `flag` marca; ele sobrevive até o
            // fim de `selectChat` para suprimir o "marcar como lido" do final.
            let entrouEmPrevia = false;

            if (foundChat?.isBotActive && !foundChat.isGroup) {
                // sem dialog — overlay no input gerencia a interação
            } else if (emEspera) {
                if (this.podeVerPreviaDeAtendimento(foundChat)) {
                    // Com a permissão, o clique entra direto no atendimento sem
                    // vincular atendente. O composer segue bloqueado pelo estado
                    // `chatIsOpenButAtendimentoIsNotOpen` do ChatInput (mesmo
                    // estado de quando se interrompe um Agente de IA), e é de lá
                    // que sai a ação de abrir de fato.
                    entrouEmPrevia = true;
                } else {
                    const atendenteAtual = this.atendenteDeOutroEmEspera(foundChat!.id as IdAtendimento);
                    const theyWantoToOpen = atendenteAtual
                        ? await PromptToAssumirAtendimento(foundChat!, atendenteAtual)
                        : await PromptToAbrirAtendimento(foundChat!);
                    if (!theyWantoToOpen) return;
                    await this.chatService.iniciarAtendimento(foundChat!.id);
                }
            }

            if (foundChat?.isGroup && this.chatContext != 'team' && MakeChat.participantsNeedRefresh(foundChat)) { // Evita recarregar participantes se já estão atualizados
                void MakeChat.FetchAndPopulateWithParticipants(foundChat);
            }

            if (uiStore.activeTab == 'Kanban' || uiStore.activeTab == 'Radar') {
                uiStore.openChatModal();
            }

            uiStore.closeFilePreviewModal();
            this.cancelForwarding();
            this.clearReplyMessage();
            this.clearEditMessage();

            this.activeChatId = chatId;
            this.mensagensFixadas = [];
            this.anotacoesInternas = [];
            this.isProfileOpen = false;

            if (this.chatContext === 'team') {
                this.isSignatureEnabled = false;
            } else {
                const savedPref = this.signaturePreferences[chatId];
                this.isSignatureEnabled = (savedPref !== undefined) ? savedPref : true;
            }

            if (foundChat && foundChat.unreadCount > 0) {
                this.initialUnreadCount = foundChat.unreadCount;
            }

            this.isLoadingMessages = true;
            this.messages = [];

            // Trocou de conversa enquanto esta carregava: a seleção nova já zerou
            // `messages` e tem a própria busca em andamento. Escrever aqui punha as
            // mensagens desta sob o cabeçalho da outra (e zerava o badge dela). Sem
            // conversa nova (o chat foi fechado), ninguém mais desligaria o spinner.
            const trocouDeConversa = () => {
                if (this.activeChatId === chatId) return false;
                if (!this.activeChatId) this.isLoadingMessages = false;
                return true;
            };

            this.messagePage = 1;
            const whizMessages = await this.resolveFetchMessages(false);
            const temMaisPaginas = !!whizMessages.pagingState;
            const messages = WhizMessagesToMessagesWithReaction(whizMessages);
            messages.reverse(); // Inverte as da API primeiro

            // 1. Pega os que estão na memória RAM
            const pendingRam = this.localPendingMessages.get(chatId as string) || [];

            // 2. Pega os que "sobreviveram" no HD após o F5
            const pendingDB = await getPendingMessagesFromDB(chatId as string);

            // 3. Mescla tudo, evitando duplicados
            const mapUnicos: Map<IdMensagem, Message> = new Map();
            pendingRam.forEach(m => mapUnicos.set(m.id, m));
            pendingDB.forEach(m => mapUnicos.set(m.id, m)); // DB sobrescreve a RAM se houver conflito (garante que venha como erro)
            const mensagensPendentes = semDuplicataDaApi(Array.from(mapUnicos.values()), messages);

            if (trocouDeConversa()) return;

            // 4. Joga pra tela: API + Pendentes/Erros
            this.messages = [...messages, ...mensagensPendentes].sort((a, b) => {
                const tA = new Date(a.time).getTime();
                const tB = new Date(b.time).getTime();
                return tA - tB;
            });

            if (temMaisPaginas) await this.carregarAtePrimeiraNaoLida(chatId);
            if (trocouDeConversa()) return;

            if (this.activeChat) this.activeChat.unreadCount = 0;
            this.isLoadingMessages = false;

            if (!this.isGroupChat) this.focusedContactIdForHistory = foundChat?.idContato;
            // Prévia é leitura sem posse: marcar como lido zeraria o badge do
            // atendimento para todo o time e ele pareceria já atendido.
            if (!entrouEmPrevia) await this.resolveMarkChatAsRead(chatId);
            void this.carregarMensagensFixadas();
            void this.carregarAnotacoesInternas();
        },

        _getSetAbtKey(key: string) {
            const abtCtrl = new AbortController();
            this.abortRequestMap.set(key, abtCtrl);
            return { ctrl: abtCtrl, signal: abtCtrl.signal }
        },

        async resolveFetchMessages(getNextPage: boolean = false, abtKey: string = 'fetchMessages') {
            console.debug(abtKey);

            if (this.activeChat?.isGroup) {
                if (!this.activeChatId) throw new Error('[chatStore] [resolveFetchMessages] `this.activeChatId` was `null`');
                return await this.messageService.getMensagensByGrupo(
                    this.activeChatId as IdGrupo,
                    getNextPage ? this.messages.at(0)?.cursor : undefined,
                    this.messagePageSize
                );
            } else {
                const idContato = this.activeChat?.idContato;
                const idInbox = this.activeChat?.idInbox;
                const idConta = this.authStore.currentConta?.id;

                if (!idContato || !idInbox || !idConta) throw new Error('[chatStore] [resolveFetchMessages] idContato, idInbox ou idConta ausente');
                const permissoesStore = usePermissoesStore();
                // ✅ Verifica permissão específica para este inbox
                const temPermissaoVerInteracoes = permissoesStore.temPermissao(
                    'inbox.ver_interacoes_atendimento',
                    idConta,
                    idInbox
                );

                // ✅ SEM permissão: busca apenas mensagens do atendimento atual
                if (!temPermissaoVerInteracoes) {
                    if (!this.activeChatId) throw new Error('[chatStore] activeChatId necessário para getMensagensByAtendimento');

                    // ⚠️ IMPORTANTE: Este método usa paginação por NÚMERO, não cursor
                    return await this.messageService.getMensagensByAtendimento(
                        this.activeChatId as IdAtendimento,
                        getNextPage ? this.messagePage : 1,  // ← Usa número de página
                        this.messagePageSize
                    );
                }

                // ✅ COM permissão: busca todas as interações (cross-atendimento)
                return await this.messageService.getMensagensCrossAtendimentoByInboxContato(
                    idContato,
                    idInbox,
                    getNextPage ? this.messages.at(0)?.cursor : undefined,  // ← Usa cursor
                    this.messagePageSize
                );
            }
        },

        async fetchNextPage() {
            const chatIdOnFetch = this.activeChatId;
            this.messagePage++;
            const whizMessages = await this.resolveFetchMessages(true);
            const hasMore = !!whizMessages.pagingState;
            const parsedMessages = WhizMessagesToMessagesWithReaction(whizMessages);
            parsedMessages.reverse();
            if (chatIdOnFetch == this.activeChatId) {
                this.messages = [...parsedMessages.filter(msg => !this.messagesById.has(msg.id)), ...this.messages];
                this.mensagensFixadas.forEach(pin => {
                    const msg = this.messages.find(m => m.id === pin.idMensagem);
                    if (msg) msg.isPinned = true;
                });
            }
            return { messages: parsedMessages, hasMore };
        },

        /**
         * A primeira página da abertura não alcança a primeira não lida (ex.: 80 não
         * lidas, página de 50): busca as anteriores AINDA sob o spinner, para o
         * ChatConversation abrir direto no separador em vez de desenhar, posicionar e
         * ver o conteúdo pular a cada página que chega.
         */
        async carregarAtePrimeiraNaoLida(chatId: IdAtendimento|IdGrupo) {
            try {
                while (this.activeChatId === chatId
                    && idsNaoLidas(this.messages, this.initialUnreadCount).size < this.initialUnreadCount
                    && this.messages.length < LIMITE_MENSAGENS_ATE_PRIMEIRA_NAO_LIDA) {
                    const carregadas = this.messages.length;
                    const { hasMore } = await this.fetchNextPage();
                    // Página sem nada novo (cursor ausente na mais antiga, p.ex.) repetiria para sempre.
                    if (!hasMore || this.messages.length === carregadas) break;
                }
            } catch (e) {
                // Página extra é conforto, não requisito: a conversa abre com o que já veio.
                console.warn('[chatStore] carregarAtePrimeiraNaoLida falhou', e);
            }
        },

        setReplyMessage(message: Message) {
            this.messageToEdit = null;
            this.messageToReply = message;
            // Uma referência por vez: a citação real desta conversa substitui a do grupo.
            if (this.activeChatId) this.limparRespostaParticular(this.activeChatId);
        },
        clearReplyMessage() {
            this.messageToReply = null;
        },

        limparRespostaParticular(idChat: string) {
            delete this.respostasParticulares[idChat];
        },

        toggleSignature() {
            this.isSignatureEnabled = !this.isSignatureEnabled;

            if (this.activeChatId && this.chatContext === 'customer') {
                this.signaturePreferences[this.activeChatId] = this.isSignatureEnabled;
            }
        },

        // #region 📨 Envio de msg

        async sendForwardedMessage(msg: Message, codigoRecipiente: string, idInbox: IdInbox, isGroup: boolean, opcoes: OpcoesEncaminhamento = {}) {
            const { currentUserName } = this.getCommonPayloadData();
            const codigoDestino = codigoRecipiente;
            const idInboxOrigem = idInbox;

            switch (msg.type) {
                case 'file':
                case 'video':
                case 'imagem':
                    return await getSendMessageStrategy('FILE').handleSendMessage({
                        codigoDestino, idInboxOrigem, isGroup, currentUserName,
                        ehEncaminhamento: true,
                        file: msg.fileData?.url,
                        caption: legendaParaEncaminhar(msg, opcoes),
                        mimeType: msg.fileData?.mimeType,
                    } as SendFileMessagePayload);
                case 'audio':
                    return await getSendMessageStrategy('AUDIO').handleSendMessage({
                        codigoDestino, idInboxOrigem, isGroup, currentUserName,
                        ehEncaminhamento: true,
                        audioUrl: msg.fileData?.url
                    } as SendAudioMessagePayload);
                case 'multi_contact':
                case 'contact':
                    return await getSendMessageStrategy('CONTACT').handleSendMessage({
                        codigoDestino, idInboxOrigem, isGroup, currentUserName,
                        ehEncaminhamento: true,
                        contacts: msg.contactData
                    } as SendContactMessagePayload);
                case 'sticker':
                    return await getSendMessageStrategy('STICKER').handleSendMessage({
                        codigoDestino, idInboxOrigem, isGroup, currentUserName,
                        ehEncaminhamento: true,
                        fileurl: msg.fileData?.url,
                        // Encaminhamento não deve mexer nas "Recentes" do usuário.
                        trackStickerUsage: () => { /* no-op */ },
                    } as SendStickerMessagePayload);
                case 'list':
                case 'poll':
                    return toast.error('Forwarding unsupported for contact, list or poll messages.'); // todo: implementar esses e depois o forwarding
                case 'text':
                case 'system':
                default:
                    return await this.messageService.enviarTexto({
                        codigo: codigoRecipiente, grupo: isGroup, idInbox: idInbox,
                        mensagem: msg.text ?? '', ehEncaminhamento: true
                    });
            }
        },

        async sendMessage(text: string, mentioned: ChatParticipant[] = []) {
            await handleSendMessage(this, 'TEXT', {text, mentioned, ...this.getCommonPayloadData()})
        },

        async sendAudioMessage(audioBlob: Blob, duration: string, waveform: number[]) {
            const formatoAudio = formatoDeAudioDoCanal(this.activeChatType);
            await handleSendMessage(this, 'AUDIO', {audioBlob, duration, waveform, formatoAudio, ...this.getCommonPayloadData()} as SendAudioMessagePayload)
        },

        async sendPollMessage(question: string, options: string[], allowMultiple: boolean) {
            await handleSendMessage(this, 'POLL', {question, options, allowMultiple, ...this.getCommonPayloadData()})
        },

        async sendFileMessage(file: File, caption: string, mentioned: ChatParticipant[] = [], skipSignature = false) {
            await handleSendMessage(this, 'FILE', {file, caption, mentioned, skipSignature, ...this.getCommonPayloadData()})
        },

        async sendContactMessage(contacts: { name: string, phone: string, initials: string, avatar?: string }[]) {
            await handleSendMessage(this, 'CONTACT', {contacts, ...this.getCommonPayloadData()})
        },

        async sendTemplateMessage(template: ResponseTemplate) {
            await handleSendMessage(this, 'TEMPLATE', {template, ...this.getCommonPayloadData()})
        },

        // #endregion

        async toBase64(file: File): Promise<string> {
            const result = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
            })
            return result as string;
        },

        getCommonPayloadData(chat?: Chat): SendMessagePayload {
            if (!chat) chat = this.activeChat ?? undefined;
            if (!chat) throw new Error('[getCodigoDestinoAndInboxOrigem] `this.activeChat` was `undefined`');

            const codigoDestino = chat.isGroup ? chat.codigoGrupo : chat.codigoContato;
            if (!codigoDestino) throw new Error('[getCodigoDestinoAndInboxOrigem] `codigoDestino` was `undefined`');

            return {
                codigoDestino,
                idInboxOrigem: chat.idInbox,
                isGroup: !!chat.isGroup,
                currentUserName: this.userAuthStore.user?.nome,
                chatId: chat.id as string
            }
        },

        ifReplyGetProperData() {
            if (!this.messageToReply) return {
                idExternoResposta: undefined,
                replyToId: undefined,
                replyText: undefined,
                replyOwner: undefined,
            };

            return {
                idExternoResposta: this.messages.find(m => m.id == this.messageToReply?.id)?.idExterno as string,
                replyToId: this.messageToReply.id,
                replyText: this.messageToReply.text || (this.messageToReply.type === 'audio' ? 'Áudio' : 'Mensagem'),
                replyOwner: this.getMessageOwnerName(this.messageToReply),
            }
        },

        toggleProfile() { this.isProfileOpen = !this.isProfileOpen; },
        closeProfile() {
            this.isProfileOpen = false;
            // Limpa o temporário após a animação de fechamento
            setTimeout(() => {
                this.tempProfileChat = null;
            }, 300);
        },
        closeChat() {
            this.activeChatId = null;
            this.messages = [];
            this.isProfileOpen = false;
        },
        setChatToLinkTags(chat: Chat) { this.chatToLinkTags = chat; },
        toggleMute(chatId: string) {
            const chat = this.chatList.find(c => c.id === chatId);
            if (chat) chat.isMuted = !chat.isMuted;
        },
        /**
         * Silencia o grupo (sem som nem push). O servidor grava pelo `codigo`, então
         * o mesmo grupo nos outros inboxes também fica silenciado — espelhado aqui.
         */
        async silenciarGrupo(chatId: IdGrupo, duracao: DuracaoSilencio) {
            const chat = this.chatList.find(c => c.id === chatId);
            if (!chat) return;

            try {
                const { silenciadoAte } = await this.groupService.silenciarGrupo(chatId, duracao);
                this._aplicarSilencioNoGrupo(chat.codigoGrupo, true, silenciadoAte);
            } catch (e: any) {
                toast.error((e as Error).message || 'Não foi possível silenciar o grupo.');
            }
        },
        async reativarNotificacoesGrupo(chatId: IdGrupo) {
            const chat = this.chatList.find(c => c.id === chatId);
            if (!chat) return;

            try {
                await this.groupService.reativarNotificacoesDoGrupo(chatId);
                this._aplicarSilencioNoGrupo(chat.codigoGrupo, false, null);
            } catch (e: any) {
                toast.error((e as Error).message || 'Não foi possível reativar as notificações.');
            }
        },
        _aplicarSilencioNoGrupo(codigoGrupo: string | undefined, silenciado: boolean, silenciadoAte: string | null) {
            if (!codigoGrupo) return;
            for (const c of this.chatList) {
                if (c.isGroup && c.codigoGrupo === codigoGrupo) {
                    c.isMuted = silenciado;
                    c.silenciadoAte = silenciadoAte;
                }
            }
        },
        async togglePin(chatId: string) {
            const chat = this.chatList.find(c => c.id === chatId);
            if (!chat) return;

            const isPinning = !chat.isPinned;
            chat.isPinned = isPinning; // optimistic update

            try {
                if (chat.isGroup) {
                    if (isPinning) await this.groupService.pinGrupo(chatId as IdGrupo);
                    else           await this.groupService.unpinGrupo(chatId as IdGrupo);
                } else {
                    if (isPinning) await this.chatService.pinAtendimento(chatId as IdAtendimento);
                    else           await this.chatService.unpinAtendimento(chatId as IdAtendimento);
                }
            } catch (e: any) {
                chat.isPinned = !isPinning; // rollback
                toast.error((e as Error).message || 'Não foi possível fixar o chat.');
            }
        },
        toggleFavorite(chatId: string) {
            const chat = this.chatList.find(c => c.id === chatId);
            if (chat) chat.isFavorite = !chat.isFavorite;
        },
        async markAsUnread(chatId: string) {
            const chat = this.chatList.find(c => c.id === chatId);
            if (!chat) return;

            // chat aberto re-marcaria como lido ao rolar/selecionar — fecha antes
            if (this.activeChatId === chatId) this.closeChat();

            const previousMarked = chat.isMarkedUnread;
            chat.isMarkedUnread = true; // optimistic update (contador intocado — badge vira dot)

            try {
                if (chat.isGroup) await this.groupService.marcarGrupoComoNaoLido(chatId as IdGrupo);
                else             await this.chatService.marcarAtendimentoComoNaoLido(chatId as IdAtendimento);
            } catch (e: any) {
                chat.isMarkedUnread = previousMarked; // rollback
                toast.error((e as Error).message || 'Não foi possível marcar como não lida.');
            }
        },

        async linkTagsToChat(idChat: IdGrupo|IdAtendimento, idContato: IdContato|undefined, tagIds: IdEtiqueta[], isGroup = false) {
            const tagStore = useTagStore();
            const tagContatoService = new ContatoEtiquetaService();
            const tagGrupoService   = new GrupoEtiquetaService();

            const selectedTagObjects: ResponseEtiqueta[] = tagStore.availableTags.filter(tag =>
                tagIds.includes(tag.id)
            );
            const chat = this.chatList.find(c => c.id === idChat);
            if (!chat) return toast.error('Chat não encontrado.');

            const existingTagsMap = chat.tags.reduce((map, t) => map.set(t.id, true), new Map<IdEtiqueta, boolean>());
            const existingTagsArr = Array.from(existingTagsMap.keys());
            const incomingTagsMap = tagIds.reduce((map, t) => map.set(t, true), new Map<string, boolean>());

            const newTags      = tagIds.filter(newTag => !existingTagsMap.has(newTag));
            const tagsToRemove = existingTagsArr.filter(tag => !incomingTagsMap.has(tag));

            const idUsuario = this.user?.id;
            const idInbox   = chat.idInbox;

            let addPromises: Promise<unknown>[];
            let removePromises: Promise<unknown>[];

            if (isGroup) {
                const idGrupo = idChat as IdGrupo;
                addPromises    = newTags.map(t => tagGrupoService.create({ idGrupo, idEtiqueta: t, idUsuario, idInbox }));
                removePromises = tagsToRemove.map(tagId => tagGrupoService.remove(idGrupo, tagId as IdEtiqueta));
            } else {
                if (!idContato) return toast.error('Contato não encontrado.');
                addPromises    = newTags.map(t => tagContatoService.create({ idEtiqueta: t, idContato, idAtendimento: idChat as string, idUsuario, idInbox }));
                removePromises = tagsToRemove.map(tagId => tagContatoService.remove(idContato, tagId as IdEtiqueta));
            }

            chat.tags = selectedTagObjects;

            await Promise.all([...addPromises, ...removePromises]);
        },
        /**
         * Reflete na lista de atendimentos as etiquetas de um contato alteradas fora do chat
         * (ex: formulário de contato). Um contato pode ter vários atendimentos abertos,
         * por isso o filtro é por `idContato` e não pelo id do chat.
         */
        updateTagsDoContato(idContato: IdContato, tags: ResponseEtiqueta[]) {
            for (const chat of this.chatList.filter(c => !c.isGroup && c.idContato === idContato)) {
                chat.tags = [...tags];
            }

            if (this.tempProfileChat?.idContato === idContato) {
                this.tempProfileChat.tags = [...tags];
            }
        },

        removeTagFromAllChats(tagId: string) {
            this.chatList.forEach(chat => {
                chat.tags = chat.tags.filter(tag => tag.id !== tagId);
            });
        },

        toggleReaction(message: Message, emoji: string) {
            console.log('[toggleReaction] nope', message, emoji);
            // // Garante que o array exista
            // if (!message.reactions) {
            //     message.reactions = [];
            // }

            // const reactionIndex = message.reactions.indexOf(emoji);

            // if (reactionIndex > -1) {
            //     // Já reagiu com este emoji, então remove (un-react)
            //     message.reactions.splice(reactionIndex, 1);
            // } else {
            //     // Adiciona a reação
            //     message.reactions.push(emoji);
            // }
        },

        setEditMessage(messageToEdit: Message) {
            this.messageToReply = null; // Cancela qualquer resposta pendente
            this.messageToEdit = messageToEdit;
            if (['video','imagem','file','audio'].includes(messageToEdit.type) && messageToEdit.fileData?.caption) {
                messageToEdit.text = messageToEdit.fileData?.caption
            }
        },
        clearEditMessage() {
            this.messageToEdit = null;
        },

        startForwarding(message: Message) {
            this.isForwarding = true;
            this.selectionContext = 'forward';
            // Um balão de álbum (`media_grid`) tem ID sintético: resolve nas mensagens reais.
            this.forwardingMessageIds = resolveSelectableMessageIds(message);
        },

        startSelectionMode() {
            this.isForwarding = true;
            this.selectionContext = 'manage';
            this.forwardingMessageIds = [];
        },

        startMassForwarding() {
            this.isForwarding = true;
            this.selectionContext = 'forward';
            this.forwardingMessageIds = [];
        },
        /**
         * Alterna a seleção de uma ou várias mensagens de uma vez. Um balão de álbum
         * (`media_grid`) representa N mídias, então o toggle é atômico: ou o grupo inteiro
         * entra na seleção, ou o grupo inteiro sai dela.
         */
        toggleForwardMessage(target: string | string[]) {
            const ids = Array.isArray(target) ? target : [target];
            if (ids.length === 0) return;

            const allSelected = ids.every(id => this.forwardingMessageIds.includes(id));

            if (allSelected) {
                this.forwardingMessageIds = this.forwardingMessageIds.filter(id => !ids.includes(id));
                if (this.forwardingMessageIds.length === 0 && this.selectionContext === 'forward') {
                    this.cancelForwarding();
                }
            } else {
                for (const id of ids) {
                    if (!this.forwardingMessageIds.includes(id)) this.forwardingMessageIds.push(id);
                }
            }
        },
        cancelForwarding() {
            this.isForwarding = false;
            this.forwardingMessageIds = [];
            this.selectionContext = null;
        },
        /** Retorna os ids das conversas que receberam o encaminhamento. */
        async commitForwarding(chatsToForwardTo: Chat[], opcoes: OpcoesEncaminhamento = {}): Promise<Chat['id'][]> {
            const messagesToForward = this.messages.filter(m =>
                this.forwardingMessageIds.includes(m.id)
            );

            // Sem isso um ID que não resolve para mensagem real falha em silêncio,
            // e o toast de sucesso no fim ainda dispara.
            if (messagesToForward.length === 0) {
                toast.error('Nenhuma mensagem selecionada para encaminhar.');
                return [];
            }

            toast.info('Encaminhando...');

            for (const chat of chatsToForwardTo) {
                for (const currentMessage of messagesToForward) {
                    const codigoDestino = chat.isGroup ? chat.codigoGrupo : chat.codigoContato;
                    if (!codigoDestino) continue;
                    await this.sendForwardedMessage(currentMessage, codigoDestino, chat.idInbox, !!chat.isGroup, opcoes);

                    const foundChat = this.chatList.find(c => c.id === chat.id);
                    if (foundChat) {
                        foundChat.time = new Date().toISOString();
                        const lm = this._makeLastMessageFromForwarded(currentMessage);
                        lm.status = 'sent';
                        foundChat.lastMessage = lm;
                    }
                }
            }

            toast.success(chatsToForwardTo.length == 1 ? `Mensagem encaminhada!` : `${chatsToForwardTo.length} mensagens encaminhadas!`);
            return chatsToForwardTo.map(c => c.id);
        },

        /**
         * Encaminha as mensagens selecionadas para contatos escolhidos na aba "Contatos":
         * cria (e inicia) um atendimento para cada contato antes de enviar. Se o contato já
         * tiver atendimento aberto/na fila no inbox, o backend reaproveita (dedupe) em vez de duplicar.
         * Se a criação do atendimento falhar, a mensagem NÃO é enviada para aquele contato.
         * Retorna os ids dos atendimentos que receberam o encaminhamento.
         */
        async commitForwardingToContatos(contatos: { id: IdContato; nome: string; codigo: string }[], idInbox: IdInbox, idCanal?: IdCanal, opcoes: OpcoesEncaminhamento = {}): Promise<IdAtendimento[]> {
            const messagesToForward = this.messages.filter(m =>
                this.forwardingMessageIds.includes(m.id)
            );

            // Evita criar atendimentos para os contatos sem ter o que enviar.
            if (messagesToForward.length === 0) {
                toast.error('Nenhuma mensagem selecionada para encaminhar.');
                return [];
            }

            toast.info('Encaminhando...');

            const falhas: string[] = [];
            const encaminhados: IdAtendimento[] = [];
            try {
                for (const contato of contatos) {
                    let idAtendimento: IdAtendimento;
                    try {
                        idAtendimento = (await this.chatService.create({ idContato: contato.id, idInbox, idCanal, iniciar: true })).id;
                    } catch (e) {
                        console.error(`[commitForwardingToContatos] Falha ao criar atendimento para ${contato.nome}`, e);
                        falhas.push(contato.nome);
                        continue;
                    }

                    for (const currentMessage of messagesToForward) {
                        await this.sendForwardedMessage(currentMessage, contato.codigo, idInbox, false, opcoes);
                    }
                    encaminhados.push(idAtendimento);
                }
            } finally {
                await this.refreshAtendimentos();
            }

            if (falhas.length > 0) {
                toast.error(`Falha ao criar atendimento para: ${falhas.join(', ')}. Mensagem não encaminhada.`);
            }
            if (encaminhados.length > 0) {
                toast.success(encaminhados.length == 1 ? `Mensagem encaminhada!` : `Mensagens encaminhadas para ${encaminhados.length} contatos!`);
            }
            return encaminhados;
        },

        _makeLastMessageFromForwarded(msg: Message): LastMessage {
            const sender = this.userAuthStore.user?.nome ?? null;

            let resolvedType = msg.type;
            if (msg.fileData?.mimeType?.includes('image')) resolvedType = 'imagem';

            const typeToOgType: Partial<Record<typeof resolvedType, string>> = {
                imagem       : 'IMAGEM',
                video        : 'VIDEO',
                audio        : 'AUDIO',
                sticker      : 'FIGURINHA',
                file         : 'DOCUMENTO',
                contact      : 'CONTATO',
                multi_contact: 'CONTATOS',
                poll         : 'ENQUETE',
                list         : 'INTERATIVA',
                location     : 'LOCALIZACAO',
                'group-invite': 'CONVITE_GRUPO',
            };

            const textMap: Partial<Record<typeof resolvedType, string>> = {
                imagem       : 'Imagem',
                video        : 'Vídeo',
                audio        : 'Áudio',
                sticker      : 'Figurinha',
                file         : 'Arquivo',
                contact      : 'Contato',
                multi_contact: 'Contatos',
                poll         : 'Enquete',
                list         : 'Lista',
                location      : 'Localização',
                'group-invite': 'Convite para grupo',
            };

            const lastMsgType: LastMessage['type'] = resolvedType === 'audio' ? 'audio'
                : resolvedType === 'sticker' ? 'sticker'
                : 'text';

            return {
                sender,
                text   : textMap[resolvedType] ?? msg.text ?? '',
                type   : lastMsgType,
                ogType: (typeToOgType[resolvedType] ?? undefined) as LastMessage['ogType'],
                status : 'pending',
                isFromMe: true
            };
        },

        toggleFavoriteMessage(messageId: string) {
            const message = this.messages.find(m => m.id === messageId);
            if (message) {
                message.isFavorite = !message.isFavorite;
            }
        },

        /** @returns `true` se a mensagem realmente saiu — quem chama não deve supor sucesso. */
        async deleteMessage(message: Message): Promise<boolean> {
            // O balão de álbum (`media_grid`) é virtual: o ID dele não existe na store nem
            // no servidor. Apagar esse balão significa apagar as N mídias que ele agrupa —
            // é como o usuário o enxerga, e é o mesmo critério já usado na seleção.
            const idsReais = resolveSelectableMessageIds(message);
            if (idsReais.length > 1 || idsReais[0] !== message.id) {
                const reais = this.messages.filter(m => idsReais.includes(m.id));
                if (reais.length === 0) {
                    console.error('[deleteMessage] balão sem mensagens reais correspondentes', { id: message.id, idsReais });
                    return false;
                }
                const resultados = await Promise.all(reais.map(m => this.deleteMessage(m)));
                return resultados.every(Boolean);
            }

            // Mensagem com ID provisório (timestamp, não UUID) nunca chegou ao servidor:
            // o backend estouraria no driver do Cassandra. Some dela só localmente — RAM,
            // IndexedDB e tela. Bolha de reenvio recusado tem ID real e cai no DELETE
            // normal, que é o certo: a linha existe no servidor.
            if (!ehIdMensagemPersistida(message.id)) {
                const chatId = this.activeChatId;
                this.messages = this.messages.filter(m => m.id !== message.id);
                if (chatId) this.removePendingMessage(chatId as string, message.id);
                return true;
            }

            try {
                const currChatCodigoContato = (this.isGroupChat) ? this.activeChat?.codigoGrupo?.replaceAll(/\D/g, '') : this.activeChat?.codigoContato;
                if (!currChatCodigoContato) {
                    // Saída silenciosa aqui fazia a exclusão em massa anunciar sucesso sem
                    // ter mandado requisição nenhuma.
                    console.error('[deleteMessage] sem código de destino', { chatId: this.activeChatId, isGroupChat: this.isGroupChat });
                    toast.error('Não foi possível identificar o destino da conversa.');
                    return false;
                }
                await this.messageService.deletarMensagem(message.id, currChatCodigoContato);
                message.isDeleted = true;
                return true;
            } catch (e) {
                console.error(e);
                toast.error((e as Error).message || 'Não foi possível excluir a mensagem.')
                return false;
            }
        },

        /**
         * Aplica a reação no clique e só depois pergunta ao servidor.
         *
         * Antes o `set` vinha DEPOIS do `await`, e o POST só volta quando o provedor
         * terminou o envio: a barra de emoji fechava e a reação só aparecia com o
         * retorno — parecia que o clique tinha falhado.
         *
         * A chave é `CHAVE_REACAO_PROPRIA`, a mesma que o evento `MENSAGEM` e o
         * recarregamento usam para reação nossa, então o retorno cai em cima desta entrada.
         */
        async reactToMessage(message: Message, emoji: string) {
            if (!message.reactions) message.reactions = new Map();
            const reacoes = message.reactions;

            const tentativa = tentativasDeReacao.get(message.id)
                ?? { seq: 0, seqConfirmada: 0, confirmada: reacoes.get(CHAVE_REACAO_PROPRIA), pendentes: 0 };
            tentativasDeReacao.set(message.id, tentativa);
            const seq = ++tentativa.seq;
            tentativa.pendentes++;

            reacoes.set(CHAVE_REACAO_PROPRIA, emoji);
            try {
                await new MensagensService().enviarReacao({ idMensagem: message.id, reacao: emoji });
                if (seq > tentativa.seqConfirmada) {
                    tentativa.seqConfirmada = seq;
                    tentativa.confirmada = emoji;
                }
            } catch (e) {
                console.error(e);
                if (seq === tentativa.seq) {
                    if (tentativa.confirmada) reacoes.set(CHAVE_REACAO_PROPRIA, tentativa.confirmada);
                    else reacoes.delete(CHAVE_REACAO_PROPRIA);
                }
                toast.error('Não foi possível enviar a reação.');
            } finally {
                if (--tentativa.pendentes === 0) tentativasDeReacao.delete(message.id);
            }
        },

        // #region Anotações internas 📝

        abrirModalAnotacaoInterna(anotacao: { id: string; conteudo: string } | null = null) {
            this.anotacaoInternaEmEdicao = anotacao;
            // Fixa o alvo na abertura: clique em notificação PWA, dock do kanban ou
            // openChatModal podem trocar `activeChatId` com o modal aberto.
            this.anotacaoInternaChatAlvo = this.activeChatId
                ? { id: this.activeChatId as string, isGroup: this.isGroupChat }
                : null;
            this.isAnotacaoInternaModalOpen = true;
        },

        fecharModalAnotacaoInterna() {
            this.isAnotacaoInternaModalOpen = false;
            this.anotacaoInternaEmEdicao = null;
            this.anotacaoInternaChatAlvo = null;
        },

        //
        // Anotação NÃO é mensagem: vive em PostgreSQL, nunca vai para WhatsApp/Meta.
        // Entra no mesmo array `messages` apenas para ser ordenada junto na timeline,
        // com `type: 'internal-note'` — o que a exclui de todo caminho de envio,
        // encaminhamento, resposta, reação e fixação, que filtram por tipo.

        /** Projeção somente-exibição. Nada aqui volta ao pipeline de mensagem. */
        anotacaoToMessage(anotacao: AnotacaoInterna): Message {
            return {
                id: anotacao.id,
                idExterno: '',
                type: 'internal-note',
                text: anotacao.conteudo,
                time: anotacao.dataCriacao,
                isSent: anotacao.idUsuario === this.userAuthStore.user?.id,
                readStatus: null,
                isBot: false,
                // Evento de sistema não tem remetente: `undefined`, não string
                // vazia — é o que faz o menu de ações sumir para todo mundo.
                idUsuarioSender: anotacao.idUsuario ?? undefined,
                noteAuthorName: anotacao.autor?.nome ?? '',
                noteEdited: anotacao.editada,
                noteTipo: anotacao.tipo,
                noteMetadados: anotacao.metadados,
            } as Message;
        },

        /**
         * Espelha a bifurcação de `carregarMensagensFixadas`: sem
         * `inbox.ver_interacoes_atendimento` lê só o atendimento corrente; com a
         * permissão lê tudo do contato naquele inbox. É a mesma fronteira que o
         * backend valida — pedir o modo errado devolve 403.
         */
        async carregarAnotacoesInternas() {
            if (!this.activeChatId) return;
            const chatIdOnFetch = this.activeChatId;

            try {
                let params: ListarAnotacoesInternasParams;

                if (this.isGroupChat) {
                    params = { idGrupo: this.activeChatId as string };
                } else {
                    const idInbox = this.activeChat?.idInbox;
                    const idContato = this.activeChat?.idContato;
                    const idConta = this.authStore.currentConta?.id;
                    const permissoesStore = usePermissoesStore();
                    const temPermissaoVerInteracoes = idConta && idInbox
                        ? permissoesStore.temPermissao('inbox.ver_interacoes_atendimento', idConta, idInbox)
                        : false;

                    params = temPermissaoVerInteracoes && idInbox && idContato
                        ? { idInbox, idContato }
                        : { idAtendimento: this.activeChatId as string };
                }

                const anotacoes = await this.anotacoesInternasService.listar({ ...params, limite: 200 });

                // Usuário pode ter trocado de chat durante o await
                if (this.activeChatId !== chatIdOnFetch) return;

                this.mesclarAnotacoesNaTimeline(anotacoes);
            } catch (e) {
                console.error('[carregarAnotacoesInternas]', e);
            }
        },

        /**
         * Substitui a lista pelo resultado da listagem, PRESERVANDO anotações que
         * chegaram por realtime durante o voo da requisição — a resposta é um
         * retrato anterior a elas e não pode apagá-las.
         */
        mesclarAnotacoesNaTimeline(anotacoes: AnotacaoInterna[]) {
            const idsDaListagem = new Set(anotacoes.map(a => a.id));
            const recebidasDurante = this.anotacoesInternas.filter(a => !idsDaListagem.has(a.id));

            this.anotacoesInternas = [...anotacoes.filter(a => !a.del), ...recebidasDurante];
        },

        /** Insere/atualiza uma anotação isolada sem refazer a listagem inteira. */
        aplicarAnotacaoNaTimeline(anotacao: AnotacaoInterna) {
            const idx = this.anotacoesInternas.findIndex(a => a.id === anotacao.id);

            if (anotacao.del) {
                if (idx >= 0) this.anotacoesInternas.splice(idx, 1);
                return;
            }

            if (idx >= 0) this.anotacoesInternas[idx] = anotacao;
            else this.anotacoesInternas.push(anotacao);
        },

        /**
         * O evento chega no tópico do INBOX, não do chat: antes de tocar a
         * timeline é preciso confirmar que a anotação pertence ao chat aberto.
         */
        anotacaoPertenceAoChatAtivo(anotacao: AnotacaoInterna): boolean {
            if (!this.activeChatId) return false;

            if (this.isGroupChat) return anotacao.idGrupo === this.activeChatId;

            if (anotacao.idAtendimento === this.activeChatId) return true;

            // Modo cross-atendimento: anotação de outro atendimento do mesmo
            // contato/inbox também pertence à timeline aberta.
            const idInbox = this.activeChat?.idInbox;
            const idContato = this.activeChat?.idContato;
            const idConta = this.authStore.currentConta?.id;
            if (!idInbox || !idContato || !idConta) return false;

            const permissoesStore = usePermissoesStore();
            if (!permissoesStore.temPermissao('inbox.ver_interacoes_atendimento', idConta, idInbox)) return false;

            return anotacao.idInbox === idInbox && anotacao.idContato === idContato;
        },

        async criarAnotacaoInterna(conteudo: string) {
            const alvo = this.anotacaoInternaChatAlvo;
            // Sem alvo fixado a nota iria para `activeChatId`, que no chat interno /
            // modo equipe ainda aponta para o último atendimento de cliente aberto.
            if (!alvo) {
                toast.error('Abra uma conversa para adicionar uma anotação.');
                return;
            }

            const payload: CriarAnotacaoInternaPayload = alvo.isGroup
                ? { conteudo, idGrupo: alvo.id }
                : { conteudo, idAtendimento: alvo.id };

            try {
                const anotacao = await this.anotacoesInternasService.criar(payload);
                this.aplicarAnotacaoNaTimeline(anotacao);
                return anotacao;
            } catch (e) {
                console.error('[criarAnotacaoInterna]', e);
                toast.error('Não foi possível salvar a anotação.');
                throw e;
            }
        },

        async editarAnotacaoInterna(id: string, conteudo: string) {
            try {
                const anotacao = await this.anotacoesInternasService.editar(id, conteudo);
                this.aplicarAnotacaoNaTimeline(anotacao);
                return anotacao;
            } catch (e) {
                console.error('[editarAnotacaoInterna]', e);
                toast.error('Não foi possível editar a anotação.');
                throw e;
            }
        },

        async removerAnotacaoInterna(id: string) {
            try {
                await this.anotacoesInternasService.remover(id);
                const idx = this.anotacoesInternas.findIndex(a => a.id === id);
                if (idx >= 0) this.anotacoesInternas.splice(idx, 1);
            } catch (e) {
                console.error('[removerAnotacaoInterna]', e);
                toast.error('Não foi possível excluir a anotação.');
                throw e;
            }
        },
        // #endregion

        async carregarMensagensFixadas() {
            if (!this.activeChatId) return;
            try {
                let pins: MensagemFixada[];
                if (this.isGroupChat) {
                    pins = await this.mensagensFixadasService.listarFixadasGrupo(this.activeChatId as string);
                } else {
                    const idInbox = this.activeChat?.idInbox;
                    const idContato = this.activeChat?.idContato;
                    const idConta = this.authStore.currentConta?.id;
                    const permissoesStore = usePermissoesStore();
                    const temPermissaoVerInteracoes = idConta && idInbox
                        ? permissoesStore.temPermissao('inbox.ver_interacoes_atendimento', idConta, idInbox)
                        : false;

                    pins = await this.mensagensFixadasService.listarFixadasContato(
                        temPermissaoVerInteracoes && idInbox && idContato
                            ? { idInbox, idContato }
                            : { idAtendimento: this.activeChatId as string }
                    );
                }
                this.mensagensFixadas = pins;
                pins.forEach(pin => {
                    const msg = this.messages.find(m => m.id === pin.idMensagem);
                    if (msg) msg.isPinned = true;
                });
            } catch (e) {
                console.error('[carregarMensagensFixadas]', e);
            }
        },

        async setPinMessage(duration: '24h' | '7d' | '30d', global = false) {
            const uiStore = useUiStore();
            const messageToPin = uiStore.messageToPin;
            if (!messageToPin || !this.activeChat || !this.activeChatId) return;

            try {
                let pin: MensagemFixada;
                if (this.isGroupChat) {
                    pin = await this.mensagensFixadasService.fixarGrupo(
                        this.activeChatId as string,
                        { idMensagem: messageToPin.id, duracao: duration, global },
                    );
                } else {
                    const chat = this.chatList.find(c => c.id === this.activeChatId);
                    pin = await this.mensagensFixadasService.fixarContato({
                        idInbox:       chat?.idInbox as string,
                        idContato:     chat?.idContato as string,
                        idAtendimento: this.activeChatId as string,
                        idMensagem:    messageToPin.id,
                        duracao:       duration,
                        global,
                    });
                }

                this.mensagensFixadas.push(pin);
                this.mensagensFixadas.sort((a, b) => new Date(a.dataMensagem).getTime() - new Date(b.dataMensagem).getTime());

                const newPinned = this.messages.find(m => m.id === messageToPin.id);
                if (newPinned) newPinned.isPinned = true;
            } catch (e) {
                console.error(e);
                toast.error('Não foi possível fixar a mensagem.');
            }
        },

        async unpinMessage(pinId: string) {
            const pin = this.mensagensFixadas.find(p => p.id === pinId);
            if (!pin) return;

            try {
                if (this.isGroupChat) {
                    await this.mensagensFixadasService.desafixarGrupo(pinId);
                } else {
                    await this.mensagensFixadasService.desafixarContato(pinId);
                }

                this.mensagensFixadas = this.mensagensFixadas.filter(p => p.id !== pinId);

                const oldPinned = this.messages.find(m => m.id === pin.idMensagem);
                if (oldPinned) oldPinned.isPinned = false;
            } catch (e) {
                console.error(e);
                toast.error('Não foi possível desafixar a mensagem.');
            }
        },

        async updateContactDetails(updateContato: UpdateContato, idChat: string) {
            try {
                await this.contactService.update(updateContato);

                // Atualiza chatList (chats privados reais)
                const chat = this.chatList.find(c => c.id === idChat);
                if (chat) {
                    if (updateContato.nome) chat.name = String(updateContato.nome);
                    if (updateContato.codigo) chat.codigoContato = updateContato.codigo;
                    if (updateContato.agenteIaAtivado !== undefined) chat.agenteIaAtivado = updateContato.agenteIaAtivado;
                }

                // Atualiza contacts cache
                const localContact = this.contacts.find(c => c.id === updateContato.id);
                if (localContact) {
                    if (updateContato.nome) localContact.nome = String(updateContato.nome);
                    if (updateContato.codigo) localContact.codigo = updateContato.codigo;
                    if (updateContato.observacao !== undefined) localContact.observacao = updateContato.observacao;
                    if (updateContato.agenteIaAtivado !== undefined) localContact.agenteIaAtivado = updateContato.agenteIaAtivado;
                }

                // Sincroniza o cache do contatoStore (fonte lida pelo badge "IA desativada" no ChatHeader)
                const contatoStore = useContatoStore();
                const cachedContato = contatoStore.contatos.find(c => c.id === updateContato.id);
                if (cachedContato) {
                    contatoStore._patchContato({
                        ...cachedContato,
                        ...(updateContato.nome !== undefined ? { nome: String(updateContato.nome) } : {}),
                        ...(updateContato.codigo !== undefined ? { codigo: updateContato.codigo } : {}),
                        ...(updateContato.observacao !== undefined ? { observacao: updateContato.observacao } : {}),
                        ...(updateContato.agenteIaAtivado !== undefined ? { agenteIaAtivado: updateContato.agenteIaAtivado } : {}),
                    });
                }

                // Atualiza tempProfileChat (perfil de participante de grupo)
                if (this.tempProfileChat?.idContato === updateContato.id) {
                    if (updateContato.nome) this.tempProfileChat.name = String(updateContato.nome);
                    if (updateContato.codigo) this.tempProfileChat.codigoContato = updateContato.codigo;
                    if (updateContato.observacao !== undefined) this.tempProfileChat.obsContato = updateContato.observacao;
                    if (updateContato.agenteIaAtivado !== undefined) this.tempProfileChat.agenteIaAtivado = updateContato.agenteIaAtivado;
                }

                // Atualiza nome do participante no grupo ativo (reflete nas mensagens via resolvedSenderName)
                if (this.activeChat?.participants && updateContato.nome) {
                    for (const p of this.activeChat.participants) {
                        if (p.idContato === updateContato.id || (updateContato.codigo && p.phone === updateContato.codigo)) {
                            p.name = String(updateContato.nome);
                        }
                    }
                }
            } catch (e) {
                console.error(e);
                toast.error((e as Error).message || 'Não foi possível atualizar o contato.')
                throw e;
            }
        },

        // A `chatList` de atendimentos fica intacta no modo equipe: a tela do Chat da
        // Equipe lê o `internalChatStore`, e o websocket segue atualizando a lista
        // (é ela que alimenta o contador de não lidas do menu lateral). Por isso a
        // troca de contexto não recarrega nem esvazia a lista.
        async setChatContext(mode: 'customer' | 'team') {
            this.chatContext = mode;
            this.activeChatId = null;
            this.messages = [];
            this.isProfileOpen = false;
            this.isLoadingChats = true;

            if (mode === 'team') {
                this.isSignatureEnabled = false;
            } else {
                this.isSignatureEnabled = true;
            }

            this.isLoadingChats = false;
        },

        /**
         * Aplica um CONEXAO/DESCONEXAO ao inbox, na metade CERTA.
         *
         * Um inbox de coexistência tem duas conexões e dois campos: `isConnected`
         * segue a metade OFICIAL — é literalmente o que o `GET /status` devolve em
         * `connected` para essa plataforma (`connected: metaConectado`) — e
         * `evolutionConectado` segue a metade QR.
         *
         * CONEXAO/DESCONEXAO só nascem do webhook `connection.update`, que é da
         * Evolution: o transformer da Cloud API não implementa `transformarConexao`,
         * e a queda da metade oficial chega por outro caminho (`account_update` →
         * INBOX_ATUALIZADO). Ou seja, num inbox híbrido este evento é SEMPRE sobre
         * a metade QR.
         *
         * Escrever `isConnected` aqui, como se fazia, errava duas vezes: carimbava
         * o estado da Evolution no campo da metade oficial e deixava
         * `evolutionConectado` congelado no valor do último `GET /status`. Por isso
         * o modal continuava dizendo "QR desconectado" depois de conectar, e o
         * atendimento seguia no modo "enviar template"
         * (`inboxOfSelectedChatIsOfficialWpp` lê `evolutionConectado !== true`) até
         * alguém recarregar a página.
         */
        _aplicarEstadoDeConexao(inbox: InboxWithStatus, conectado: boolean) {
            if (ehCoexistencia(inbox.plataforma)) {
                inbox.evolutionConectado = conectado;
                return;
            }
            inbox.isConnected = conectado;
        },

        /**
         * Troca o quadro do QR pelo aviso "Conectando...". Os filhos atuais são
         * apenas ESCONDIDOS, nunca removidos: o `#swal-btn-refresh-qr` que o
         * handler de DESCONEXAO clica e o `img` que o handler de QR_CODE atualiza
         * precisam continuar no DOM, senão o modal trava neste estado.
         */
        _mostrarConectandoNoQr() {
            const el = document.querySelector('#swal-loading-qrcode');
            if (!el || el.querySelector('#swal-qr-conectando')) return;

            el.querySelectorAll<HTMLElement>(':scope > *').forEach(filho => { filho.hidden = true; });

            const aviso = document.createElement('div');
            aviso.id = 'swal-qr-conectando';
            aviso.className = 'text-center';
            aviso.innerHTML = `
                <div class="spinner-border text-success mb-2" role="status"></div>
                <p class="mb-0 fw-bold">QR Code lido. Conectando...</p>
                <p class="mb-0 font-size-12 text-muted">Aguarde a confirmação do WhatsApp.</p>
            `;
            el.appendChild(aviso);
        },

        /** Desfaz `_mostrarConectandoNoQr` e devolve o QR à vista. */
        _esconderConectandoNoQr() {
            const el = document.querySelector('#swal-loading-qrcode');
            const aviso = el?.querySelector('#swal-qr-conectando');
            if (!el || !aviso) return;

            aviso.remove();
            el.querySelectorAll<HTMLElement>(':scope > *').forEach(filho => { filho.hidden = false; });
        },

        async showQrCode(idInbox: string) {
            const inbox = this.inboxMap.get(idInbox);
            if (!inbox) return toast.error('Inbox não encontrada.');
            if (inbox.isConnected && inbox.plataforma != 'WHATSAPP_HYBRID_COEXISTENCE') return toast.info('Este dispositivo já está conectado.');

            this.uiStore.isShowingQrCode = true;
            this.uiStore.selectedInboxId = idInbox;

            const renderQr = async (forceRefresh = false) => {
                const el = document.querySelector('#swal-loading-qrcode');
                if (!el) return;

                el.className = 'text-center py-3 border rounded-3 bg-light w-100';
                (el as HTMLElement).style.cssText = 'min-height: 250px; display: flex; flex-direction: column; justify-content: center; align-items: center;';
                el.innerHTML = `
                    <div class="spinner-border text-primary mb-2" role="status"></div>
                    <p class="mb-0 fw-bold">Gerando QR Code...</p>
                `;

                try {
                    if (forceRefresh || !inbox.qrcode || inbox.qrcode.length < 1) {
                        const res = await this.inboxService.conectar(idInbox);
                        inbox.qrcode = res.base64;
                    }

                    const el2 = document.querySelector('#swal-loading-qrcode');
                    if (el2) {
                        el2.classList.remove('bg-light');
                        el2.innerHTML = `
                            <img src="${escapeHtml(inbox.qrcode)}" class="img-fluid rounded-3 shadow-sm" style="max-width: 240px; border: 10px solid #fff;" />
                            <div class="mt-2 text-primary font-size-11 fw-bold">
                                <i class="ri-refresh-line"></i> QR Code atualizado em tempo real
                            </div>
                            <button id="swal-btn-refresh-qr" class="btn btn-primary mt-3 px-4 py-2 fw-semibold d-flex align-items-center gap-2" style="border-radius: 8px; font-size: 14px;">
                                <i class="ri-refresh-line fs-5"></i> Gerar novo QR Code
                            </button>
                        `;
                        document.querySelector('#swal-btn-refresh-qr')?.addEventListener('click', () => void renderQr(true));
                    }
                } catch (e) {
                    console.error(e);
                    const el2 = document.querySelector('#swal-loading-qrcode');
                    if (el2) {
                        el2.innerHTML = `
                            <p class="text-danger mb-3"><i class="ri-error-warning-line"></i> Erro ao gerar QR Code.</p>
                            <button id="swal-btn-refresh-qr" class="btn btn-primary px-4 py-2 fw-semibold d-flex align-items-center gap-2" style="border-radius: 8px; font-size: 14px;">
                                <i class="ri-refresh-line fs-5"></i> Tentar novamente
                            </button>
                        `;
                        document.querySelector('#swal-btn-refresh-qr')?.addEventListener('click', () => void renderQr(true));
                    }
                }
            };

            void Swal.fire({
                title: 'Conectar WhatsApp',
                width: '550px',
                html: `
                    <div id="swal-qr-container" class="text-start px-2">
                        <div class="d-flex flex-column align-items-center mb-4">
                            <div id="swal-loading-qrcode" class="text-center py-3 border rounded-3 bg-light w-100" style="min-height: 250px; display: flex; flex-direction: column; justify-content: center; align-items: center;">
                                <div class="spinner-border text-primary mb-2" role="status"></div>
                                <p class="mb-0 fw-bold">Gerando QR Code...</p>
                            </div>
                        </div>

                        <div class="instructions-section">
                            <h6 class="fw-bold mb-3 d-flex align-items-center gap-2">
                                <i class="ri-qr-scan-2-line text-primary"></i> Para conectar o WhatsApp:
                            </h6>
                            <ol class="font-size-13 text-muted ps-3 mb-2">
                                <li class="mb-1">Abra o <b>WhatsApp</b> no seu celular.</li>
                                <li class="mb-1">Acesse o <b>Menu</b> (três pontos ou configurações).</li>
                                <li class="mb-1">Clique em <b>Aparelhos conectados</b>.</li>
                                <li class="mb-1">Selecione o botão <b>"Conectar um aparelho"</b>.</li>
                                <li>Posicione a câmera sobre o <b>QR Code acima</b>.</li>
                            </ol>

                            <div class="d-flex align-items-start gap-2 font-size-12 text-muted mb-4">
                                <i class="ri-time-line text-primary" style="margin-top: 2px;"></i>
                                <span>
                                    Assim que o celular mostrar <b>conectado</b>, a conexão ainda pode
                                    levar alguns segundos para valer no Whiz. Deixe esta janela aberta:
                                    ela fecha sozinha quando a conexão for confirmada.
                                </span>
                            </div>

                            <div class="troubleshoot-box p-3 rounded-3 border bg-light-subtle">
                                <h6 class="font-size-12 fw-bold text-dark mb-2 d-flex align-items-center gap-2">
                                    <i class="ri-question-line text-warning"></i> Não conseguiu conectar?
                                </h6>
                                <ul class="font-size-12 text-muted ps-3 mb-0">
                                    <li>Feche e limpe o histórico do WhatsApp no celular.</li>
                                    <li>Acesse Menu > Aparelhos conectados e tente novamente.</li>
                                    <li>No computador, force a atualização: <b>Ctrl + Shift + R</b>.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                `,
                showConfirmButton: false,
                showCloseButton: true,
                customClass: {
                    title: 'fs-5 fw-bold pt-4',
                    popup: 'rounded-4'
                },
                willClose: () => {
                    this.uiStore.isShowingQrCode = false;
                },
            });

            await delay(50);
            // Sempre busca um código fresco ao abrir: o cache pode conter um QR
            // de uma janela já expirada (códigos duram 20-60s). As rotações
            // seguintes chegam ao vivo pelo evento QR_CODE do socket.
            await renderQr(true);
        },
        addTagsToChat(idAtendimento: string, tags: ResponseEtiqueta[]) {
            const foundChat = this.chatList.find(c => c.id == idAtendimento);
            if (!foundChat) return;
            else foundChat.tags = tags;
        },

        updateDraft(chatId: string, draftText: string | null) {
            const chat = this.chatList.find(c => c.id === chatId);
            if (chat) {
                // @ts-expect-error Propriedade dinâmica adicionada em tempo de execução
                chat.draft = draftText;
            }
        },

        async findOrMakeContatoByPhone(phone: string, nome?: string): Promise<ResponseContato|false> {
            // const standardizedPhone = standardizePhone(phone, {returnErrorObject: false, returnToastError: false, throwError: false});
            // if (!standardizedPhone)  {
            //     toast.warning('Número inválido.');
            //     return false;
            // }

            const standardizedPhone = normalizarPhone(phone);

            // Buscar o contato vinculado ao número fornecido pós normalização
            let foundContato = this.contatosByCodigo.get(standardizedPhone);
            if (!foundContato) {
                const currContaId = this.userAuthStore.currentConta?.id;
                if (!currContaId) {
                    toast.error(`Nenhuma conta logada..?`);
                    return false;
                }
                const exato = await this.contactService.getByCodigo(currContaId, standardizedPhone);
                if (exato) {
                    foundContato = exato;
                    this.contacts.push(foundContato);
                } else {
                    // Não cria contato com "número" maior que qualquer telefone (E.164):
                    // o WhatsApp não responde por ele, o servidor não barra (fica
                    // "desconhecido") e sobraria um contato inválido. Contato que JÁ
                    // existe com um código assim continua abrindo (busca acima).
                    if (excedeTamanhoDeTelefone(standardizedPhone)) {
                        toast.warning('Número inválido: tem mais dígitos do que um telefone pode ter.');
                        return false;
                    }
                    foundContato = await this.contactService.create({ codigo: standardizedPhone, nome: nome ?? standardizedPhone, idConta: currContaId, tipo: 'WHATSAPP' })
                    this.contacts.push(foundContato);
                }
            }

            console.log(foundContato);

            return foundContato;
        },

        async findContatoByNameOrFail(nome: string, possibilities?: GrupoParticipanteResponseDto[]): Promise<ResponseContato|false> {
            throw new Error('findContatoByNameOrFail não implementada 😔');
            console.log(nome, possibilities);
            await delay(0);
        },

        /**
         * Número sem DDI que poderia ser brasileiro: pergunta se é para prefixar o 55.
         * Devolve o número escolhido, ou `null` se o usuário cancelou ou não há número.
         *
         * Pergunta sempre, mesmo que uma das formas já seja contato: quem escolheu antes
         * pode ter escolhido errado, e o mesmo texto pode ser de pessoas diferentes.
         */
        async resolverDdiAusente(phone: string | null | undefined): Promise<string|null> {
            // Cartão de contato sem TEL/waid no vCard chega sem `telefone`.
            if (!phone) return null;
            const semDdi = phone.replace(/\D/g, '');
            if (!semDdi) return null;
            const comDdi = numeroSemDdiAmbiguo(semDdi);
            if (!comDdi) return phone;

            if (!modaisDdiAusenteMontados) {
                console.warn('[resolverDdiAusente] DdiAusenteModal não montado nesta tela; seguindo sem o 55.');
                return phone;
            }

            responderDdiPendente?.(null);
            return new Promise(resolve => {
                responderDdiPendente = resolve;
                this.ddiAusentePendente = { semDdi, comDdi };
            });
        },

        /** Resposta do `DdiAusenteModal`. Sem pergunta em aberto é no-op. */
        responderDdiAusente(escolha: 'com' | 'sem' | null) {
            const pendente = this.ddiAusentePendente;
            const responder = responderDdiPendente;
            this.ddiAusentePendente = null;
            responderDdiPendente = null;
            if (!pendente || !responder) return;
            responder(escolha == 'com' ? pendente.comDdi : escolha == 'sem' ? pendente.semDdi : null);
        },

        registrarModalDdiAusente(montado: boolean) {
            modaisDdiAusenteMontados += montado ? 1 : -1;
        },

        async handleClickOnTextWithAction(type: 'phone' | 'mention', content: string | null | undefined, nome?: string) {
            const methodName = 'handleClickOnTextWithAction';
            try {
                if (this.inProgressMap.get(methodName)) return;
                this.inProgressMap.set(methodName, true);

                if (!content?.replace(/\D/g, '') && type == 'phone')
                    return toast.error('Este contato não possui número de telefone.');
                if (!content) return;

                if (type == 'phone') {
                    const escolhido = await this.resolverDdiAusente(content);
                    if (escolhido === null) return;
                    content = escolhido;
                }

                const foundContato = type == 'phone'
                    ? await this.findOrMakeContatoByPhone(content, nome)
                    : await this.findContatoByNameOrFail(content); // type == 'mention'
                // `findOrMakeContatoByPhone` já avisa o motivo de cada `false`.
                if (!foundContato) return type == 'phone' ? undefined : toast.error(`Não foi possível encontrar o contato.`);

                // Buscar o chat ativo, e o inbox vinculado; then, buscar os atendimentos abertos com aquele inbox;
                const relevantInbox = this.activeChat?.idInbox;
                //if (!relevantInbox) return toast.error(`Nenhum inbox vinculado..?`);
                const relevantAtendimentoId = relevantInbox ? this.ongoingAtendimentosByContatoByInboxMap.get(relevantInbox)?.get(foundContato.id) : null;

                if (relevantAtendimentoId) // se há atendimento, focar o fofo
                    return this.focusAtendimento(relevantAtendimentoId);

                // Sem inbox ativo: procurar em todos os inboxes se há atendimento aberto com este contato
                let inboxToFocus: IdInbox | null = relevantInbox ?? null;
                let inboxHasExistingAtendimento = false;

                if (!relevantInbox) {
                    for (const [inboxId, contatoMap] of this.ongoingAtendimentosByContatoByInboxMap) {
                        if (contatoMap.has(foundContato.id)) {
                            inboxToFocus = inboxId;
                            inboxHasExistingAtendimento = true;
                            break;
                        }
                    }
                }

                this.contactToFocus = foundContato;
                this.inboxTofocus = inboxToFocus;
                return () => this.clearContactInboxToFocus();
            } catch (e) {
                console.error(e);
                toast.error((e as Error).message || `Não foi possível processar o número.`);
            } finally {
                this.inProgressMap.set(methodName, false);
            }
        },

        async suggestToOpenNovoAtendimento(options: { contactToFocus?: ResponseContato, inboxToFocus?: IdInbox, travarInbox?: boolean, waitForResponse: boolean } = {waitForResponse: false} ) {
            // Se não encontrado, exibir modal propondo abrir atendimento com este inbox relevante (ou outro qualquer :3)
            this.contactToFocus = options.contactToFocus ?? null;
            this.inboxTofocus = options.inboxToFocus ?? null;
            this.inboxTofocusTravado = !!options.travarInbox && !!options.inboxToFocus;
            if (options.waitForResponse) await this.storedNewAtendimentoPromise;
            else void this.storedNewAtendimentoPromise;
            return () => this.clearContactInboxToFocus();
        },

        clearContactInboxToFocus() {
            this.contactToFocus = null;
            this.inboxTofocus = null;
            this.inboxTofocusTravado = false;
        },

        /**
         * "Responder em particular": abre a conversa 1:1 com quem enviou `mensagem` no
         * grupo aberto, NO INBOX DO GRUPO, e deixa a mensagem como referência acima do
         * campo de digitação. Não envia nada.
         *
         * Conversa existente é reaproveitada; sem ela, o modal de novo atendimento de
         * sempre é aberto com o inbox travado. A referência só é presa se a tela
         * terminar na conversa certa (contato e inbox conferidos) — um prompt de
         * assumir atendimento cancelado, ou um modal fechado, não deixa nada pendurado.
         */
        async responderEmParticular(mensagem: Message) {
            const methodName = 'responderEmParticular';
            if (this.inProgressMap.get(methodName)) return;
            this.inProgressMap.set(methodName, true);

            // Aviso só se a resolução demorar: em rede boa a conversa abre antes dele, e
            // um toast que pisca e some é ruído.
            let idAviso: string | number | null = null;
            const timerAviso = setTimeout(() => { idAviso = toast.loading('Abrindo conversa particular...'); }, 600);
            const encerrarAviso = () => {
                clearTimeout(timerAviso);
                if (idAviso !== null) toast.remove(idAviso);
                idAviso = null;
            };

            try {
                const grupo = this.activeChat;
                if (!grupo?.isGroup || this.chatContext === 'team') return;

                // Fotografados agora: a navegação abaixo troca `activeChat` e `messages`.
                const idGrupo = grupo.id as IdGrupo;
                const idInbox = grupo.idInbox;
                const nomeGrupo = grupo.name;
                const copiaMensagem: Message = { ...mensagem };
                const remetente: RemetenteDeGrupo = { idContato: mensagem.groupSenderId, codigo: mensagem.groupSenderCodigo };

                // Sem a lista não dá para saber se o remetente é LID (número oculto).
                if (!grupo.participantsFetchedAt) await MakeChat.FetchAndPopulateWithParticipants(grupo);

                const plataforma = this.userAuthStore.inboxesFull.find(i => i.id == idInbox)?.plataforma;
                const bloqueio = motivoBloqueioRespostaParticular({ participantes: grupo.participants ?? [], remetente, plataforma });
                if (bloqueio) return void toast.warning(bloqueio);

                const participante = localizarParticipante(grupo.participants, remetente);
                const contato = await this._resolverContatoDoRemetente(remetente, participante?.idContato, nomeExibivel(participante?.name) ?? undefined);
                if (!contato) return void toast.error('Não foi possível localizar o contato deste participante.');

                if (!ehPlataformaCompativelComTipoContato(plataforma, contato.tipo))
                    return void toast.warning('O inbox deste grupo não atende o canal deste contato.');

                const existente = await this._localizarConversaParticular(contato, idInbox);
                encerrarAviso();
                if (existente === false) return;

                let idAtendimento: IdAtendimento | null = existente;
                if (idAtendimento) {
                    await this.focusAtendimento(idAtendimento);
                } else {
                    // Sem conversa: o modal de sempre, com o inbox do grupo travado.
                    await this.suggestToOpenNovoAtendimento({ contactToFocus: contato, inboxToFocus: idInbox, travarInbox: true, waitForResponse: true });
                    // `criarAtendimento` recarrega a lista; o atendimento novo já está no mapa.
                    idAtendimento = this.ongoingAtendimentosByContatoByInboxMap.get(idInbox)?.get(contato.id) ?? null;
                }
                if (!idAtendimento) return;

                const destino = this.activeChat;
                const chegouNaConversaCerta = !!destino
                    && destino.id === idAtendimento
                    && !destino.isGroup
                    && destino.idContato === contato.id
                    && destino.idInbox === idInbox;
                if (!chegouNaConversaCerta) return;

                this.respostasParticulares[idAtendimento] = {
                    mensagem: copiaMensagem,
                    nomeRemetente: nomeExibivel(participante?.name)
                        ?? nomeExibivel(mensagem.sender)
                        ?? nomeExibivel(contato.nome)
                        ?? formatPhone(contato.codigo),
                    idGrupo,
                    nomeGrupo,
                    idInbox,
                };
            } catch (e) {
                console.error(e);
                toast.error((e as Error).message || 'Não foi possível abrir a conversa particular.');
            } finally {
                encerrarAviso();
                this.inProgressMap.set(methodName, false);
            }
        },

        /**
         * Contato de quem enviou a mensagem no grupo.
         *
         * Pelo ID primeiro: é a mesma linha que o servidor gravou como remetente. Ir pelo
         * telefone normalizado erraria contato antigo gravado com o número legado (12
         * dígitos) e CRIARIA um segundo contato para a mesma pessoa. O telefone só entra
         * quando a mensagem veio sem contato — e aí pelo fluxo de sempre, que cria o
         * contato se ele não existir.
         */
        async _resolverContatoDoRemetente(remetente: RemetenteDeGrupo, idContatoParticipante?: string, nome?: string): Promise<ResponseContato | null> {
            const idContato = (remetente.idContato || idContatoParticipante) as IdContato | undefined;
            if (idContato) {
                const contato = this.contatosById.get(idContato) ?? await useContatoStore().findByIdCached(idContato);
                if (contato) return contato;
            }
            if (!remetente.codigo) return null;
            return (await this.findOrMakeContatoByPhone(remetente.codigo, nome)) || null;
        },

        /**
         * Atendimento em curso do contato NESTE inbox: o id, `null` quando não há, ou
         * `false` quando há mas este usuário não o enxerga (já avisado).
         *
         * A lista local só tem o que este usuário enxerga. Antes de propor um atendimento
         * novo, o servidor é consultado: um atendimento em curso com outro atendente
         * faria o "abrir" duplicar a conversa (ou falhar sem explicação).
         */
        async _localizarConversaParticular(contato: ResponseContato, idInbox: IdInbox): Promise<IdAtendimento | null | false> {
            const local = this.ongoingAtendimentosByContatoByInboxMap.get(idInbox)?.get(contato.id);
            if (local) return local;

            const abertos = await this.chatService.findAbertosByContato(contato.id);
            const noInbox = abertos.find(a => a.idInbox == idInbox);
            if (!noInbox) return null;
            if (this.chatsById.has(noInbox.id)) return noInbox.id;

            const atendente = noInbox.atendentes?.at(0)?.nome;
            toast.info(atendente
                ? `Este contato já está em atendimento com ${atendente} neste inbox.`
                : 'Este contato já tem um atendimento em aberto neste inbox.');
            return false;
        },

        async updateGroupWhitelistStatus(idGrupo: IdGrupo, statusWhitelist: boolean) {
            return await this.groupService.setWhitelistStatus(idGrupo, statusWhitelist);
        },

        async addUserToGroupWhitelist(idGrupo: IdGrupo, idUsuario: IdUsuario, user: ResponseUsuario) {
            const res = await this.groupWhitelistService.whitelistUser(idGrupo, idUsuario);
            this.activeChat?.grupoWhitelist?.push(user);
            return res;
        },

        async removeUserFromGroupWhitelist(idGrupo: IdGrupo, idUsuario: IdUsuario) {
            const res = await this.groupWhitelistService.unwhitelistUser(idGrupo, idUsuario);
            const removeAt = this.activeChat?.grupoWhitelist?.findIndex(u => u.id == idUsuario);
            if (removeAt !== undefined && removeAt !== -1) this.activeChat?.grupoWhitelist?.splice(removeAt, 1);
            return res;
        },

        async deleteGroup(idGrupo: IdGrupo, idInbox: IdInbox) {
            try {
                // 👇 Importante pra não ocupar o slot de fixamento de grupo com um id fantasma 🙏
                await this.groupService.unpinGrupo(idGrupo);
                const delGroupResponse = await this.groupService.deletarGrupo(idGrupo);
                console.log(`[deleteGroup] delete group response`, delGroupResponse);
                this.chatList = this.chatList.filter(c => !(c.id == idGrupo && c.idInbox == idInbox))
                toast.success(`Grupo excluído!`);
            } catch (e) {
                toast.error((e as Error).message || `Não foi possível excluir o grupo.`);
            }
        },

        destroy() {
            void this.$state.websocketService?.disconnect();
            this.$state.websocketService = null;
            this.$state.isWsConnected = false;
            this.$state.initialized = false;
            this.$state.user = null;
            this.$state.contacts = [];
            this.$state.chatList = [];
            this.$state.messages = [];
            this.$state.listFilters.selectedInboxIds = [];
            this.$state.listFilters.selectedAgentIds = [];
            this.$state.listFilters.selectedTagIds = [];
            this.$state.listFilters.automacoes = filtrosAutomacoesVazios();
        },

    },
});

type SendMessagePayload = { currentUserName?: string, shouldSign?: boolean, skipSignature?: boolean, isGroup: boolean, codigoDestino: string, idInboxOrigem: IdInbox, chatId?: string, ehEncaminhamento?: boolean, respostaParticularReenvio?: RespostaParticularDoEnvio }

/** Campos de citação da bolha (os mesmos que `chatUtils` monta do `dadosResposta`). */
type CitacaoNaBolha = Pick<Message, 'replyToId'|'replyText'|'replyOwner'|'replyType'|'replyUrl'|'replyMiniatura'|'replyGrupoNome'>;

/**
 * "Responder em particular" de UMA mensagem enviada: o que vai à API e a citação
 * da bolha otimista. Um envio novo o tira da referência presa ao atendimento; o
 * reenvio de uma falha o remonta da própria bolha (`respostaParticularReenvio`).
 */
type RespostaParticularDoEnvio = { envio: RespostaEmParticularEnvio, citacao: CitacaoNaBolha };

function respostaParticularDoEnvio(referencia: RespostaParticular): RespostaParticularDoEnvio {
    const citada = referencia.mensagem;
    // Mídia ainda no reparo do servidor: só há a miniatura, como no `dadosResposta`.
    const urlDaMidia = citada.midiaPendente ? undefined : citada.fileData?.url;
    return {
        envio: { idGrupo: referencia.idGrupo, idMensagem: citada.id },
        citacao: {
            replyToId     : citada.id,
            // O mesmo texto da prévia acima do campo: o atendente vê na bolha o que viu ao digitar.
            replyText     : GetReplySnippet(citada),
            replyOwner    : referencia.nomeRemetente,
            replyType     : citada.type,
            replyUrl      : urlDaMidia,
            replyMiniatura: urlDaMidia ? undefined : citada.miniatura,
            replyGrupoNome: referencia.nomeGrupo,
        },
    };
}

/** Reenvio: a citação de grupo que a bolha falha carregava. */
function respostaParticularDaBolha(message: Message): RespostaParticularDoEnvio | undefined {
    if (!message.respostaEmParticular) return undefined;
    return {
        envio: message.respostaEmParticular,
        citacao: {
            replyToId     : message.replyToId,
            replyText     : message.replyText,
            replyOwner    : message.replyOwner,
            replyType     : message.replyType,
            replyUrl      : message.replyUrl,
            replyMiniatura: message.replyMiniatura,
            replyGrupoNome: message.replyGrupoNome,
        },
    };
}

/**
 * A mensagem saiu sem a citação do grupo: o servidor não gravou `dadosResposta`, e a
 * bolha não pode mostrar uma citação que o contato não recebeu (sumiria ao recarregar).
 */
function descartarCitacaoParticular(message: Message) {
    message.replyToId = undefined;
    message.replyText = undefined;
    message.replyOwner = undefined;
    message.replyType = undefined;
    message.replyUrl = undefined;
    message.replyMiniatura = undefined;
    message.replyGrupoNome = undefined;
    message.respostaEmParticular = undefined;
}
// `idMensagemReenvio`: id da mensagem FALHOU sendo reenviada — o backend reaproveita a linha em vez de criar outra.
type SendTextMessagePayload     = SendMessagePayload & {text: string, mentioned: ChatParticipant[], substituirVariaveis?: boolean, idMensagemReenvio?: string}
type SendStickerMessagePayload  = SendMessagePayload & {fileurl: string, file?: File, trackStickerUsage: (url: string) => void|Promise<void>}
type SendAudioMessagePayload    = SendMessagePayload & ({audioUrl: string, audioBlob: undefined} | {audioUrl: undefined, audioBlob: Blob}) & {duration: string, waveform: number[], formatoAudio?: FormatoDeAudioCanal}
type SendPollMessagePayload     = SendMessagePayload & {question: string, options: string[], allowMultiple: boolean}
type SendFileMessagePayload     = SendMessagePayload & {file: File|string, caption: string, mentioned: ChatParticipant[], mimeType?: string, idMensagemReenvio?: string}
type SendContactMessagePayload  = SendMessagePayload & {contacts: { name: string, phone: string, initials: string, avatar?: string }[]}
type SendTemplateMessagePayload = SendMessagePayload & {template: ResponseTemplate}

/**
 * Cartão flutuante de uma conversão de mídia em `globalVideoConversionsState`.
 *
 * `mostrar` cria o cartão na primeira vez e, nas seguintes, só troca a etapa.
 * Trocar de etapa zera o progresso de propósito: cada passada mede o próprio
 * trabalho, e sem zerar o anel andaria para trás sozinho.
 *
 * Só é chamado de dentro da conversão, nunca antes dela: mídia que passa
 * intacta — o caso comum — não chega a piscar nada na tela.
 */
function cartaoDeConversao(arquivo: File, chatId: string | null | undefined, etapaPadrao: EtapaDeConversao | EtapaDeImagem) {
    const abortar = new AbortController();
    const idCartao = crypto.randomUUID();
    const encontrar = () => globalVideoConversionsState.ongoing.find((c) => c.id === idCartao);

    const mostrar = (etapa: EtapaDeConversao | EtapaDeImagem) => {
        const cartao = encontrar();
        if (cartao) {
            if (cartao.etapa !== etapa) { cartao.etapa = etapa; cartao.progress = 0; }
            return;
        }
        globalVideoConversionsState.ongoing.push({
            id: idCartao,
            chatId: chatId ?? null,
            fileName: arquivo.name,
            progress: 0,
            etapa,
            cancelar: () => abortar.abort(),
        });
    };

    return {
        signal: abortar.signal,
        mostrar,
        progresso: (fracao: number) => {
            // Defensivo: se algum caminho emitir progresso sem anunciar a
            // etapa, o cartão ainda assim aparece — progresso só existe
            // durante conversão de verdade.
            if (!encontrar()) mostrar(etapaPadrao);
            const cartao = encontrar();
            if (cartao) cartao.progress = Math.round(Math.min(1, Math.max(0, fracao)) * 100);
        },
        esconder: () => {
            globalVideoConversionsState.ongoing =
                globalVideoConversionsState.ongoing.filter((c) => c.id !== idCartao);
        },
    };
}

/**
 * Deixa o vídeo pronto para o canal Meta, ou explica por que não dá.
 *
 * Devolve o MESMO arquivo quando ele já serve — é o caso comum, e
 * reencodar ali só degradaria a imagem. Quando precisa converter, publica um
 * cartão em `globalVideoConversionsState` com progresso real e botão de
 * cancelar: um vídeo longo leva minutos no ffmpeg.wasm, e prender o atendente
 * atrás de um modal que trava a tela inteira não é opção — ele continua
 * atendendo enquanto o vídeo converte.
 *
 * Lança quando o vídeo não cabe de jeito nenhum (ou quando a conversão
 * falha), e o `handleSendMessage` que orquestra transforma isso em balão
 * vermelho — o operador vê QUAL mensagem falhou, e o Swal diz por quê.
 */
async function prepararVideoParaCanalMeta(
    arquivo: File,
    plataforma?: string | null,
    chatId?: string | null,
): Promise<File> {
    const cartao = cartaoDeConversao(arquivo, chatId, 'convertendo');

    try {
        const resultado = await converterVideoParaMeta(arquivo, plataforma, {
            signal: cartao.signal,
            onEtapa: cartao.mostrar,
            onProgress: cartao.progresso,
        });

        cartao.esconder();

        if (resultado.estado === 'impossivel') {
            void Swal.fire({
                icon: 'warning',
                title: 'Vídeo não pode ser enviado',
                text: resultado.motivo,
                confirmButtonText: 'Entendi',
            });
            throw new Error(resultado.motivo);
        }

        return resultado.arquivo;
    } catch (erro) {
        cartao.esconder();
        throw erro;
    }
}

/**
 * Deixa a imagem pronta para o WhatsApp oficial, ou explica por que não dá.
 *
 * A Cloud API só aceita JPEG/PNG de até 5 MB, e recusa depois do envio, por
 * webhook — a imagem "some". Aqui ela é convertida (webp, gif, bmp…) e/ou
 * comprimida o MÍNIMO necessário para caber, com o mesmo cartão de progresso e
 * cancelamento da conversão de vídeo. Imagem que já serve sai intacta.
 */
async function prepararImagemParaCanalMeta(
    arquivo: File,
    plataforma?: string | null,
    chatId?: string | null,
): Promise<File> {
    const cartao = cartaoDeConversao(arquivo, chatId, 'comprimindo-imagem');

    try {
        const resultado = await converterImagemParaMeta(arquivo, plataforma, {
            signal: cartao.signal,
            onEtapa: cartao.mostrar,
            onProgress: cartao.progresso,
        });

        cartao.esconder();

        if (resultado.estado === 'impossivel') {
            void Swal.fire({
                icon: 'warning',
                title: 'Imagem não pode ser enviada',
                text: resultado.motivo,
                confirmButtonText: 'Entendi',
            });
            throw new Error(resultado.motivo);
        }

        return resultado.arquivo;
    } catch (erro) {
        cartao.esconder();
        throw erro;
    }
}

type Methods = {
    handleSendEdit   : (thisCtx: ChatStore, payload: any) => Promise<boolean>
    hasContent       : (thisCtx: ChatStore, payload: any) => boolean
    getNewMessage    : (...payload: any) => Message
    getLastMessage   : (payload: any) => LastMessage
    handleSendMessage: (payload: any, idExternoResposta?: string, respostaEmParticular?: RespostaEmParticularEnvio) => Promise<RespostaEnvioMensagem & { fileUrl?: string }>
}

function getSendMessageStrategy(messageType: 'TEXT'|'STICKER'|'AUDIO'|'POLL'|'FILE'|'CONTACT'|'TEMPLATE') {
    // 0. handleSendEdit
    // 0.5 hasContent
    // 1.5 handleSignature & getNewMessage
    // 4. getLastMessage
    // 5. handleSendMessage
    const text: Methods & { handleSendSignatureMessage: (payload: SendTextMessagePayload) => Promise<{idMensagem: string}> } = {
        async handleSendEdit(thisCtx: ChatStore, payload: SendTextMessagePayload) {
            if (!thisCtx.messageToEdit) return true;
            const messageToEditId = thisCtx.messageToEdit.id;
            try {
                const message = thisCtx.messages.find(m => m.id === messageToEditId);
                if (!message) {
                    toast.warning('Mensagem não encontrada.');
                    return false;
                }

                const assinatura = TextContentUtil.extractSignature((message as Message & { _original: WhizMessageAtendimento })._original.conteudo);
                const assinaturaStr = assinatura && assinatura.length > 0 ? `*${assinatura}:*\n` : '';

                message.text = payload.text;
                message.isEdited = true;
                thisCtx.clearEditMessage();

                const tipoMensagemEditada = (message as typeof message & {_original?: {tipo: string|undefined}})._original?.tipo;
                const ehMidia = tipoMensagemEditada && ['IMAGEM', 'VIDEO', 'DOCUMENTO'].includes(tipoMensagemEditada);

                if (ehMidia) {
                    await thisCtx.messageService.editarLegendaImagemEVO(message.id, payload.text);
                } else {
                    await thisCtx.messageService.editarMensagemTexto(message.id, `${assinaturaStr}${payload.text}`);
                }
            } catch (e) {
                console.error(e)
                toast.error((e as Error).message || 'Não foi possível editar a mensagem.')
            } return false;
        },
        hasContent(thisCtx: ChatStore, payload: SendTextMessagePayload) {
            return payload.text == '<<assinatura>>' || (!!payload.text && payload.text.length > 0)
        },
        getNewMessage(payload: SendTextMessagePayload) {
            const rawSignature = `*${payload.currentUserName}:*`;
            // A bolha mostra o texto SEM a assinatura: ela já aparece no cabeçalho
            // (`agentName`), igual à mensagem que volta do servidor. Antes a otimista
            // exibia os dois — cabeçalho e `*Nome:*` cru — até o websocket chegar.
            const textoExibido = payload.text;
            if (payload.shouldSign) payload.text = `${rawSignature}\n` + payload.text;
            const newMessage: Message = {
                idExterno: '',
                id: makeDummyIdMensagem(),
                time: new Date().toISOString(),
                isSent: true,
                readStatus: 'pending',
                type: 'text',
                isBot: false,
                text: textoExibido,
                agentName: (payload.shouldSign) && payload.currentUserName ? payload.currentUserName : undefined
            };
            return newMessage;
        },
        getLastMessage(payload: SendTextMessagePayload) {
            const lastMessage: LastMessage = {
                sender: payload.currentUserName ?? null,
                text: payload.text,
                type: 'text',
                status: 'pending', isFromMe: true
            };
            return lastMessage;
        },
        async handleSendMessage(payload: SendTextMessagePayload, idExternoResposta?: string, respostaEmParticular?: RespostaEmParticularEnvio) {
            const { codigoDestino, idInboxOrigem } = payload;
            const res = await new MensagensService().enviarTexto({ codigo: codigoDestino, idInbox: idInboxOrigem, mensagem: payload.text, grupo: payload.isGroup, respostaPara: idExternoResposta, respostaEmParticular, mencoes: payload.mentioned.map(m => m.phone), substituirVariaveis: payload.substituirVariaveis, idMensagemReenvio: payload.idMensagemReenvio, ehEncaminhamento: payload.ehEncaminhamento });
            return res;
        },
        async handleSendSignatureMessage(payload: SendTextMessagePayload) {
            const { codigoDestino, idInboxOrigem } = payload;
            const res: { idMensagem: string } = await new MensagensService().enviarTexto({ codigo: codigoDestino, idInbox: idInboxOrigem, mensagem: payload.text, grupo: payload.isGroup, mencoes: [] });
            return res;
        }
    };
    const sticker: Methods = {
        async handleSendEdit(thisCtx: ChatStore,) {
            thisCtx.clearEditMessage();
            await delay(0);
            return true;
        },
        hasContent(thisCtx: ChatStore, payload: SendStickerMessagePayload) {
            return !!payload.file || payload.fileurl.length > 0;
        },
        getNewMessage(payload: SendStickerMessagePayload) {
            const newMessage: Message = {
                idExterno: '',
                id: makeDummyIdMensagem(),
                time: new Date().toISOString(),
                isSent: true,
                readStatus: 'pending',
                type: 'sticker',
                isBot: false,
                fileData: {
                    url: payload.fileurl,
                    name: payload.file ? payload.file.name : 'sticker.webp',
                    mimeType: payload.file ? payload.file.type : 'image/webp',
                    size: ''
                },
                agentName: payload.shouldSign ? payload.currentUserName : undefined
            };
            return newMessage;
        },
        getLastMessage(payload: SendStickerMessagePayload) {
            return {
                sender: payload.currentUserName ?? null,
                text: `Figurinha`,
                type: 'sticker',
                status: 'pending', isFromMe: true
            } as LastMessage;
        },
        async handleSendMessage(payload: SendStickerMessagePayload, idExternoResposta?: string, respostaEmParticular?: RespostaEmParticularEnvio) {
            let finalRemoteUrl = payload.fileurl;

            const uploadId = crypto.randomUUID();
            const chatId = payload.chatId;
            let fakeProgressInterval: number|undefined;

            if (payload.file) {
                if (chatId) {
                    globalUploadsState.ongoingUploads.push({ id: uploadId, chatId: chatId, fileName: 'Figurinha', progress: 0 });
                    fakeProgressInterval = setInterval(() => {
                        const upload = globalUploadsState.ongoingUploads.find((u) => u.id === uploadId);
                        if (upload && upload.progress < 90) {
                            upload.progress += Math.floor(Math.random() * 10) + 5;
                        }
                    }, 400);
                }

                try {
                    const uploadResponse = await new StorageService().uploadInboxFile({
                        file: payload.file,
                        idInbox: payload.idInboxOrigem
                    });
                    finalRemoteUrl = uploadResponse.path;
                } finally {
                    if (fakeProgressInterval) clearInterval(fakeProgressInterval);
                    const upload = globalUploadsState.ongoingUploads.find((u) => u.id === uploadId);
                    if (upload) upload.progress = 100;
                    setTimeout(() => {
                        globalUploadsState.ongoingUploads = globalUploadsState.ongoingUploads.filter((u) => u.id !== uploadId);
                    }, 600);
                }
            }

            void payload.trackStickerUsage(finalRemoteUrl);

            // Se queremos assinar...
            if (payload.shouldSign) {
                const rawSignature = `*${payload.currentUserName}:*`;
                await text.handleSendSignatureMessage( {text: rawSignature, mentioned: [], currentUserName: payload.currentUserName, isGroup: payload.isGroup, codigoDestino: payload.codigoDestino, idInboxOrigem: payload.idInboxOrigem } as SendTextMessagePayload)
            }

            const res = await new MensagensService().enviarFigurinha({
                figurinha   : finalRemoteUrl,
                codigo      : payload.codigoDestino,
                grupo       : payload.isGroup,
                idInbox     : payload.idInboxOrigem,
                respostaPara: idExternoResposta,
                respostaEmParticular,
                ehEncaminhamento: payload.ehEncaminhamento
            });
            return res;
        }
    };
    const audio: Methods = {
        async handleSendEdit(thisCtx: ChatStore,) {
            thisCtx.clearEditMessage();
            await delay(0);
            return true;
        },
        hasContent(thisCtx: ChatStore, payload: SendAudioMessagePayload) {
            return !!payload.audioUrl || (!!payload.audioBlob && payload.audioBlob.size > 0);
        },
        getNewMessage(payload: SendAudioMessagePayload) {
            const audioUrl = payload.audioBlob ? URL.createObjectURL(payload.audioBlob) : payload.audioUrl;
            const newMessage: Message = {
                idExterno: '',
                id: makeDummyIdMensagem(),
                time: new Date().toISOString(),
                isSent: true,
                readStatus: 'pending',
                type: 'audio',
                isBot: false,
                audioUrl: audioUrl,
                audioDuration: payload.duration,
                waveform: payload.waveform,
                agentName: (payload.shouldSign) && payload.currentUserName ? payload.currentUserName : undefined,
                senderNome: payload.currentUserName || undefined
            };
            return newMessage;
        },
        getLastMessage(payload: SendAudioMessagePayload) {
            return {
                sender: payload.currentUserName ?? null,
                text: `Áudio`,
                type: 'audio',
                status: 'pending', isFromMe: true
            } as LastMessage;
        },
        async handleSendMessage(payload: SendAudioMessagePayload, idExternoResposta?: string, respostaEmParticular?: RespostaEmParticularEnvio) {
            const { codigoDestino, idInboxOrigem } = payload;
            let audioRemoteUrl;

            const uploadId = crypto.randomUUID();
            const chatId = payload.chatId;
            let fakeProgressInterval: number|undefined;

            // Mime/extensão reais do blob gravado (antes era fixo .opus/audio/opus,
            // o que quebrava o contrato audio/mp4 das plataformas Meta).
            const EXT_BY_MIME: Record<string, string> = {
                'audio/mp4': 'm4a', 'audio/webm': 'webm', 'audio/ogg': 'ogg', 'audio/opus': 'opus',
            };
            const blobMime = (payload.audioBlob?.type || '').split(';')[0].trim() || 'audio/webm';
            const audioExt = EXT_BY_MIME[blobMime] ?? (blobMime.split('/')[1] || 'webm');
            let uploadedMime = blobMime;

            // Formatos que cada canal aceita. No WhatsApp Cloud a lista é maior
            // de propósito: Ogg/Opus e WebM/Opus são os que viram NOTA DE VOZ
            // (o WebM é remuxado para Ogg no servidor), e o audio/mp4 segue
            // aceito como degradação para navegador sem Opus — chega como anexo
            // de áudio, mas chega.
            const MIMES_POR_FORMATO: Record<FormatoDeAudioCanal, string[] | null> = {
                opus: ['audio/ogg', 'audio/webm', 'audio/mp4'],
                m4a: ['audio/mp4'],
                livre: null,
            };

            if (payload.audioBlob && payload.formatoAudio && payload.formatoAudio !== 'livre') {
                const aceitos = MIMES_POR_FORMATO[payload.formatoAudio]!;
                if (!aceitos.includes(blobMime)) {
                    toast.error(`Áudio para este canal deve estar em ${aceitos.join(', ')}. Grave novamente.`);
                    throw new Error(`[sendAudio] mime inválido para canal Meta: ${blobMime}`);
                }
                // Em MP4 o mimetype não basta: MP4 com faixa Opus dentro passa no
                // teste acima, é aceito pela Meta e chega MUDO no aplicativo do
                // celular. O codec real é lido dos bytes (o gravador já transcoda
                // quando detecta, então chegar aqui fora do padrão significa outra
                // origem de áudio).
                if (blobMime === 'audio/mp4') {
                    const codec = await codecDeAudioMp4(payload.audioBlob);
                    if (codec !== SAMPLE_ENTRY_AAC) {
                        toast.error('Áudio para este canal precisa ser AAC (.m4a). O arquivo gravado não é reproduzível no aplicativo — grave novamente.');
                        throw new Error(`[sendAudio] codec inválido para canal Meta: ${codec ?? 'desconhecido'}`);
                    }
                }
                if (payload.audioBlob.size > AUDIO_META_MAX_BYTES) {
                    toast.error('Áudio excede o limite de 16 MB. Grave uma mensagem mais curta.');
                    throw new Error(`[sendAudio] áudio acima de 16 MB: ${payload.audioBlob.size} bytes`);
                }
            }

            if (payload.audioBlob) {
                if (chatId) {
                    globalUploadsState.ongoingUploads.push({ id: uploadId, chatId: chatId, fileName: 'Áudio', progress: 0 });
                    fakeProgressInterval = setInterval(() => {
                        const upload = globalUploadsState.ongoingUploads.find((u) => u.id === uploadId);
                        if (upload && upload.progress < 90) {
                            upload.progress += Math.floor(Math.random() * 10) + 5;
                        }
                    }, 400);
                }

                try {
                    const file = new File([payload.audioBlob], `audio-${Date.now()}.${audioExt}`, { type: blobMime });
                    uploadedMime = file.type;
                    const uploadResponse = await new StorageService().uploadInboxFile({ file, idInbox: idInboxOrigem }, true);
                    audioRemoteUrl = uploadResponse.path;
                } finally {
                    if (fakeProgressInterval) clearInterval(fakeProgressInterval);
                    const upload = globalUploadsState.ongoingUploads.find((u) => u.id === uploadId);
                    if (upload) upload.progress = 100;
                    setTimeout(() => {
                        globalUploadsState.ongoingUploads = globalUploadsState.ongoingUploads.filter((u) => u.id !== uploadId);
                    }, 600);
                }
            } else {
                audioRemoteUrl = payload.audioUrl
            }

            // Se queremos assinar...
            if (payload.shouldSign) {
                const rawSignature = `*${payload.currentUserName}:*`;
                await text.handleSendSignatureMessage( {text: rawSignature, mentioned: [], currentUserName: payload.currentUserName, isGroup: payload.isGroup, codigoDestino: payload.codigoDestino, idInboxOrigem: payload.idInboxOrigem } as SendTextMessagePayload)
            }

            const res = await new MensagensService().enviarAudio({ audio: audioRemoteUrl, mimeType: uploadedMime, codigo: codigoDestino, grupo: payload.isGroup, idInbox: idInboxOrigem, respostaPara: idExternoResposta, respostaEmParticular, mencoes: [], ehEncaminhamento: payload.ehEncaminhamento })
            return res;
        },
    };
    const file: Methods = {
        async handleSendEdit(thisCtx: ChatStore,) {
            thisCtx.clearEditMessage();
            await delay(0);
            return true;
        },
        hasContent(thisCtx: ChatStore, payload: SendFileMessagePayload) {
            return !!payload.file;
        },
        getNewMessage(payload: SendFileMessagePayload) {
            let fileName;
            let fileUrl;
            let size;
            let mime;
            if (typeof payload.file == 'string') {
                fileName = payload.file;
                fileUrl = payload.file;
                size = 'unknown';
                mime = payload.mimeType || tryToGetMimeFromExtension(fileName) || 'application/octet-stream';
            } else {
                fileName = payload.file.name;
                fileUrl = URL.createObjectURL(payload.file);
                size = (payload.file.size / 1024 / 1024).toFixed(2) + ' MB';
                const rawMime = payload.file.type || 'application/octet-stream';
                const MIME_NORMALIZE: Record<string, string> = { 'image/vnd.dwg': 'application/acad', 'image/x-dwg': 'application/acad' };
                mime = MIME_NORMALIZE[rawMime] ?? rawMime;
            }

            const newMessage: Message = {
                idExterno: '',
                id: makeDummyIdMensagem(),
                time: new Date().toISOString(),
                isSent: true,
                readStatus: 'pending',
                type: 'file',
                isBot: false,
                text: undefined,
                uploadError: false, // <-- Preparado para o reenvio
                fileData: {
                    url: fileUrl,
                    name: fileName,
                    size: size,
                    mimeType: mime,
                    caption: payload.caption,
                    originalFile: typeof payload.file !== 'string' ? payload.file : undefined // <-- Salva o arquivo na RAM para reenvio sem perder
                },
                agentName: (payload.shouldSign) && payload.currentUserName ? payload.currentUserName : undefined
            };
            return newMessage;
        },
        getLastMessage(payload: SendFileMessagePayload) {
            const mime = (typeof payload.file == 'string') ? tryToGetMimeFromExtension(payload.file) || '' : payload.file.type;
            const filename = (typeof payload.file == 'string') ? payload.file : payload.file.name;

            const DOCUMENT_MIME_OVERRIDES = ['image/vnd.dwg', 'image/x-dwg', 'image/vnd.dxf'];
            const isAudio = mime.includes('audio');
            const isImage = mime.includes('image') && !DOCUMENT_MIME_OVERRIDES.includes(mime);
            const isVideo = mime.includes('video');

            const lastMessage: LastMessage = {
                sender: payload.currentUserName ?? null,
                text: isAudio ? `Áudio` : isImage ? `Imagem: ${filename}` : isVideo ? `Vídeo: ${filename}` : `Arquivo: ${filename}`,
                type: 'text',
                status: 'pending', isFromMe: true
            };
            return lastMessage;
        },
        async handleSendMessage(payload: SendFileMessagePayload, idExternoResposta?: string, respostaEmParticular?: RespostaEmParticularEnvio) {
            const filename = (typeof payload.file == 'string') ? payload.file : payload.file.name;
            // declaredMime = content-type REAL (override > File.type). Não inclui palpite por extensão.
            const declaredMime = payload.mimeType || (typeof payload.file == 'string' ? '' : payload.file.type) || '';
            // mime usado para classificar imagem/vídeo/doc: cai p/ extensão quando não há type real.
            const mime = declaredMime || tryToGetMimeFromExtension(filename) || '';

            // Allowlist de áudio: containers que o mime-db marca como vídeo (webm) ou genérico
            // (ogg/ogx/opus) neste app são áudios. Só decide quando o type REAL não afirma
            // vídeo/imagem — assim um .webm de vídeo COM content-type continua indo como vídeo.
            // O predicado é compartilhado com o modal de anexo: é ele que decide onde a caixa
            // de legenda aparece, e legenda de áudio é descartada logo abaixo.
            const DOCUMENT_MIME_OVERRIDES = ['image/vnd.dwg', 'image/x-dwg', 'image/vnd.dxf'];
            const isAudio = ehAnexoDeAudio(filename, declaredMime);
            const isImage = !isAudio && mime.includes('image') && !DOCUMENT_MIME_OVERRIDES.includes(mime);
            const isVideo = !isAudio && mime.includes('video');

            let fileRemoteUrl;
            const { codigoDestino, idInboxOrigem } = payload;

            // Vídeo em canal Meta tem teto de payload, e quem o aplica é a META,
            // não nós: o envio é por LINK (`{ video: { link } }`), o servidor
            // nunca toca nos bytes e a Graph responde 200 com um `wamid`. A
            // recusa chega segundos depois, por webhook:
            //
            //   131053 Media upload error — Video file has size 63787247 bytes
            //   but must be atmost 16777216 bytes and non-empty
            //
            // Converter AQUI, antes do upload, é o que faz o vídeo caber. Quem
            // já cabe e já é mp4 passa intacto — reencodar à toa degradaria a
            // imagem e gastaria minutos do atendente.
            if (isVideo && typeof payload.file !== 'string') {
                // `useChatStore()` e função de módulo, NÃO `this`: este método vive
                // no objeto de ESTRATÉGIA (`Methods`), e o orquestrador o chama
                // DESESTRUTURADO (`const { handleSendMessage: strategySend } =
                // getSendMessageStrategy(...)`). Em módulo ES isso é strict mode,
                // então `this` chega `undefined` e qualquer `this.x` lança
                // `TypeError: Cannot read properties of undefined`. Os demais
                // métodos da estratégia recebem `thisCtx` explicitamente — este
                // não recebe.
                payload.file = await prepararVideoParaCanalMeta(
                    payload.file,
                    useChatStore().inboxMap.get(idInboxOrigem)?.plataforma,
                    payload.chatId,
                );
            }

            // Imagem na Cloud API: mesmo problema do vídeo (envio por LINK,
            // recusa tardia por webhook), com regra própria — só JPEG/PNG, até
            // 5 MB. `useChatStore()` pelo mesmo motivo do bloco acima.
            if (isImage && typeof payload.file !== 'string') {
                payload.file = await prepararImagemParaCanalMeta(
                    payload.file,
                    useChatStore().inboxMap.get(idInboxOrigem)?.plataforma,
                    payload.chatId,
                );
            }

            // 1. Prepara o ID para o balão flutuante de loading global
            const uploadId = crypto.randomUUID();
            const chatId = payload.chatId;
            let fakeProgressInterval: number|undefined;

            // 2. Coloca o balão flutuante na tela para o usuário ver que começou
            if (typeof payload.file !== 'string' && chatId) {
                globalUploadsState.ongoingUploads.push({ id: uploadId, chatId: chatId, fileName: filename, progress: 0 });
                fakeProgressInterval = setInterval(() => {
                    const upload = globalUploadsState.ongoingUploads.find((u) => u.id === uploadId);
                    if (upload && upload.progress < 90) {
                        upload.progress += Math.floor(Math.random() * 10) + 5;
                    }
                }, 400);
            }

            try {
                // 3. Faz o upload do arquivo para o servidor da sua aplicação (Storage)
                if (typeof payload.file == 'string') {
                    fileRemoteUrl = payload.file;
                } else {
                    const uploadResponse = await new StorageService().uploadInboxFile({ file: payload.file, idInbox: idInboxOrigem });
                    fileRemoteUrl = uploadResponse.path;
                }

                // 4. O arquivo já subiu pro storage! Completamos a barrinha para 100%, MAS NÃO TIRAMOS ELA DA TELA AINDA.
                if (fakeProgressInterval) clearInterval(fakeProgressInterval);
                const upload = globalUploadsState.ongoingUploads.find((u) => u.id === uploadId);
                if (upload) upload.progress = 100;

                // 5. Envia a assinatura do atendente (se estiver ativado)
                if (payload.shouldSign) {
                    const rawSignature = `*${payload.currentUserName}:*`;
                    await new MensagensService().enviarTexto({ codigo: codigoDestino, idInbox: idInboxOrigem, mensagem: rawSignature, grupo: payload.isGroup, mencoes: [] });
                }

                // 6. Pede para a API enviar o arquivo de fato para o WhatsApp
                let res: RespostaEnvioMensagem;
                const msgService = new MensagensService();

                const mencoes = (payload.mentioned ?? []).map((m: ChatParticipant) => m.phone);
                if (isAudio)
                    // Envia o mime real (canais Meta exigem audio/mp4). Antes não ia,
                    // então uploads de .m4a eram rejeitados no backend. O backend ainda
                    // deduz da extensão da URL quando isto vier vazio.
                    res = await msgService.enviarAudio({ audio: fileRemoteUrl, mimeType: mime || undefined, codigo: codigoDestino, grupo: payload.isGroup, idInbox: idInboxOrigem, respostaPara: idExternoResposta, respostaEmParticular, mencoes, idMensagemReenvio: payload.idMensagemReenvio, ehEncaminhamento: payload.ehEncaminhamento });
                else if (isImage)
                    res = await msgService.enviarImagem({ imagem: fileRemoteUrl, legenda: payload.caption, codigo: codigoDestino, grupo: payload.isGroup, idInbox: idInboxOrigem, respostaPara: idExternoResposta, respostaEmParticular, mencoes, idMensagemReenvio: payload.idMensagemReenvio, ehEncaminhamento: payload.ehEncaminhamento });
                else if (isVideo)
                    res = await msgService.enviarVideo({ video: fileRemoteUrl, legenda: payload.caption, codigo: codigoDestino, grupo: payload.isGroup, idInbox: idInboxOrigem, respostaPara: idExternoResposta, respostaEmParticular, mencoes, idMensagemReenvio: payload.idMensagemReenvio, ehEncaminhamento: payload.ehEncaminhamento });
                else
                    res = await msgService.enviarDocumento({ documento: fileRemoteUrl, nome_documento: filename, legenda: payload.caption, codigo: codigoDestino, grupo: payload.isGroup, idInbox: idInboxOrigem, respostaPara: idExternoResposta, respostaEmParticular, mencoes, idMensagemReenvio: payload.idMensagemReenvio, ehEncaminhamento: payload.ehEncaminhamento });

                return { ...res, fileUrl: fileRemoteUrl };

            } finally {
                // 7. BLOCO FINALLY: O grande truque está aqui.
                // O 'finally' executa obrigatoriamente quando tudo acima termina, SEJA COM SUCESSO OU COM ERRO.
                // É só aqui, no final de tudo, que nós removemos o balãozinho de loading da tela.
                setTimeout(() => {
                    globalUploadsState.ongoingUploads = globalUploadsState.ongoingUploads.filter((u) => u.id !== uploadId);
                }, 600);
            }
        },
    };
    const poll: Methods = {
        async handleSendEdit(thisCtx: ChatStore,) {
            thisCtx.clearEditMessage();
            await delay(0);
            return true;
        },
        hasContent(thisCtx: ChatStore, payload: SendPollMessagePayload) {
            const validOptions = payload.options.filter(opt => opt.trim() !== '');
            const trimmedQuestion = payload.question.trim();
            if (!trimmedQuestion || validOptions.length < 2) return false;
            else {
                payload.options = validOptions;
                payload.question = trimmedQuestion;
                return true;
            }
        },
        getNewMessage(payload: SendPollMessagePayload) {
            const newMessage: Message = {
                idExterno: '',
                id: makeDummyIdMensagem(),
                time: new Date().toISOString(),
                isSent: true,
                readStatus: 'pending',
                type: 'poll',
                isBot: false,
                pollData: {
                    question: payload.question,
                    options: payload.options.map((text, index) => ({
                        id: index,
                        text: text,
                        votes: 0,
                        voters: []
                    })),
                    allowMultiple: payload.allowMultiple
                }
            };
            return newMessage;
        },
        getLastMessage(thisCtx: ChatStore,) {
            throw new Error(`[handleSendMessage] Sending polls is unsupported`);
            console.log(thisCtx);
            return {
                sender: null,
                text  : '',
                type  : 'text',
                ogType: null,
                status: 'pending', isFromMe: true
            }
        },
        async handleSendMessage(payload: SendPollMessagePayload, idExternoResposta?: string) {
            throw new Error(`[handleSendMessage] Sending polls is unsupported`)
            await delay(0);
            console.log({payload, idExternoResposta})
            return { idMensagem: '' };
        },
    };
    const contact: Methods = {
        async handleSendEdit(thisCtx: ChatStore,) {
            thisCtx.clearEditMessage();
            await delay(0);
            return true;
        },
        hasContent(thisCtx: ChatStore, payload: SendContactMessagePayload) {
            return (!!payload.contacts && payload.contacts.length > 0)
        },
        getNewMessage(payload: SendContactMessagePayload) {
            const isMulti = payload.contacts.length > 1;

            const mappedContacts = payload.contacts.map(c => ({
                name: c.name,
                phone: c.phone,
                avatar: c.avatar || '',
                initials: c.initials
            }));

            const newMessage: Message = {
                idExterno: '',
                id: makeDummyIdMensagem(),
                time: new Date().toISOString(),
                isSent: true,
                readStatus: 'pending',
                type: isMulti ? 'multi_contact' : 'contact',
                isBot: false,

                contactData: mappedContacts,
            };

            return newMessage;
        },
        getLastMessage(payload: SendContactMessagePayload) {
            const isMulti = payload.contacts.length > 1;
            return {
                time: new Date().toISOString(),
                sender: payload.currentUserName ?? null,
                text: '',
                type: isMulti ? 'multi_contacts' : 'contact',
                ogType: isMulti ? 'CONTATOS' : 'CONTATO',
                status: 'pending', isFromMe: true
            } as LastMessage;
        },
        async handleSendMessage(payload: SendContactMessagePayload, idExternoResposta?: string, respostaEmParticular?: RespostaEmParticularEnvio) {
            const { codigoDestino, idInboxOrigem } = payload;
            const res = new MensagensService().enviarContatos({
                codigo: codigoDestino,
                idInbox: idInboxOrigem,
                grupo: payload.isGroup,
                respostaPara: idExternoResposta,
                respostaEmParticular,
                ehEncaminhamento: payload.ehEncaminhamento,
                contatos: payload.contacts.map(c => ({
                    nomeCompleto: c.name,
                    telefone: c.phone.replace(/^(55\d{2})9(\d{8})$/, "$1$2"),
                    codigo: c.phone,
                })),
                mencoes: [],
            });
            return res;
        },
    };
    const template: Methods = {
        async handleSendEdit(thisCtx: ChatStore,) {
            thisCtx.clearEditMessage();
            await delay(0);
            return true;
        },
        hasContent(thisCtx: ChatStore, payload: SendTemplateMessagePayload) {
            return !!payload.template.id;
        },
        getNewMessage(payload: SendTemplateMessagePayload) {
            payload.shouldSign = false;

            let newMessage: Message;
            switch (payload.template.conteudo) {
                case 'TEXTO': newMessage = { // Igual ao de text
                    idExterno: '',
                    id: makeDummyIdMensagem(),
                    text: payload.template.mensagem,
                    time: new Date().toISOString(),
                    isSent: true,
                    readStatus: 'pending',
                    type: 'text',
                    isBot: false,
                    isEdited: false,
                    agentName: undefined
                }; break;
                case 'ANEXO': {
                    newMessage = { // Igual ao de file
                        idExterno: '',
                        id: makeDummyIdMensagem(),
                        time: new Date().toISOString(),
                        isSent: true,
                        readStatus: 'pending',
                        type: 'file',
                        isBot: false,
                        text: payload.template.legenda,
                        senderNome: payload.currentUserName || undefined,
                        fileData: {
                            url: payload.template.mensagem,
                            caption: payload.template.legenda,
                            name: '',
                            size: '',
                            mimeType: 'application/*',
                        }
                    }; break;
                }
                case 'SENHA_USO_UNICO': newMessage = {
                        idExterno: '',
                        id: makeDummyIdMensagem(),
                        time: new Date().toISOString(),
                        isSent: true,
                        readStatus: 'pending',
                        type: 'list',
                        isBot: false,
                        listHeader: `🔐 **${payload.template.nome}**\n${payload.template.mensagem}\n\nCódigo: *${payload.template.codigoSenha}*`,
                        listButtonText: payload.template.textoBotao
                }; break;
                case 'PIX': newMessage = {
                    idExterno: '',
                    id: makeDummyIdMensagem(),
                    time: new Date().toISOString(),
                    isSent: true,
                    readStatus: 'pending',
                    type: 'list', // Reutilizando 'list' ou criando um tipo 'pix' se quiser customizar visual
                    isBot: false,
                    text: 'Chave PIX enviada',
                    pollData: undefined, // limpando tipos conflitantes
                    contactData: undefined,

                    listHeader: `🔑 **Chave PIX**\n\nNome: ${payload.template.mensagem}\nChave: ${payload.template.chavePix}`,
                    listButtonText: 'Copiar Chave'
                }; break;
            } return newMessage;
        },
        getLastMessage(payload: SendTemplateMessagePayload) {
            const templateType = payload.template.conteudo;
            return {
                time: new Date().toISOString(),
                sender: payload.currentUserName ?? null,
                text: '',
                type: templateType == 'ANEXO' ? 'file' : templateType == 'PIX' ? 'list' : templateType == 'SENHA_USO_UNICO' ? 'list' : 'text',
                ogType: templateType == 'ANEXO' ? 'DOCUMENTO' : templateType == 'PIX' ? 'PIX' : templateType == 'SENHA_USO_UNICO' ? 'INTERATIVA' : 'TEXTO',
                status: 'pending', isFromMe: true
            }
        },
        async handleSendMessage(payload: SendTemplateMessagePayload, idExternoResposta?: string, respostaEmParticular?: RespostaEmParticularEnvio) {
            const { codigoDestino, idInboxOrigem } = payload;
            const chavePixToExpectedType = (chavePix: TipoChavePixTemplate) => {
                switch (chavePix) {
                    case 'TELEFONE'       : return 'phone';
                    case 'EMAIL'          : return 'email';
                    case 'CPF'            : return 'cpf';
                    case 'CHAVE_ALEATORIA': return 'random';
                    //case '': return 'cnpj';
                }
            }
            const msgService = new MensagensService();
            switch (payload.template.conteudo) {
                case 'TEXTO'          : return await msgService.enviarTexto({ codigo: codigoDestino, idInbox: idInboxOrigem, grupo: payload.isGroup, mensagem: payload.template.mensagem, respostaPara: idExternoResposta, respostaEmParticular, mencoes: [] });
                case 'SENHA_USO_UNICO': return await msgService.enviarSenhaUsoUnico({ codigo: codigoDestino, idInbox: idInboxOrigem, grupo: payload.isGroup, titulo: "Senha de uso único", descricao: payload.template.mensagem, botoes: [{tipo: 'copy', codigoCopia: payload.template.codigoSenha ?? 'codigo-senha', texto: payload.template.textoBotao || 'Clique para copiar' }], respostaPara: idExternoResposta, respostaEmParticular, mencoes: [] });
                case 'PIX'            : return await msgService.enviarPix({ codigo: codigoDestino, idInbox: idInboxOrigem, grupo: payload.isGroup, botoes: [{chave: payload.template.chavePix ?? 'chave-pix', nomeTitular: payload.template.nomeContaPix ?? 'nome-conta-pix', tipo: 'pix', tipoChave: chavePixToExpectedType(payload.template.tipoChavePix ?? 'CHAVE_ALEATORIA')}], respostaPara: idExternoResposta, respostaEmParticular, mencoes: [] });
                case 'ANEXO'          : {
                    const url = payload.template.mensagem;
                    const ext = url.split('.').pop()?.toLowerCase() ?? '';
                    // Extensões de áudio têm prioridade: containers ambíguos (webm/ogg) que o
                    // mime-db classifica como vídeo, neste app são áudios (vídeo usa mp4/mov).
                    const AUDIO_EXTS = ['opus', 'mp3', 'm4a', 'aac', 'wav', 'amr', 'flac', 'oga', 'ogg', 'weba', 'webm'];
                    const mime = tryToGetMimeFromExtension(url) || '';
                    const DOCUMENT_MIME_OVERRIDES = ['image/vnd.dwg', 'image/x-dwg', 'image/vnd.dxf'];
                    if (AUDIO_EXTS.includes(ext) || mime.includes('audio'))
                        return await msgService.enviarAudio({ codigo: codigoDestino, idInbox: idInboxOrigem, grupo: payload.isGroup, audio: url, respostaPara: idExternoResposta, respostaEmParticular, mencoes: [] });
                    if (mime.includes('image') && !DOCUMENT_MIME_OVERRIDES.includes(mime))
                        return await msgService.enviarImagem({ codigo: codigoDestino, idInbox: idInboxOrigem, grupo: payload.isGroup, imagem: url, legenda: payload.template.legenda, respostaPara: idExternoResposta, respostaEmParticular, mencoes: [] });
                    if (mime.includes('video'))
                        return await msgService.enviarVideo({ codigo: codigoDestino, idInbox: idInboxOrigem, grupo: payload.isGroup, video: url, legenda: payload.template.legenda, respostaPara: idExternoResposta, respostaEmParticular, mencoes: [] });
                    return await msgService.enviarDocumento({ codigo: codigoDestino, idInbox: idInboxOrigem, grupo: payload.isGroup, documento: url, legenda: payload.template.legenda, respostaPara: idExternoResposta, respostaEmParticular, mencoes: [] });
                }
            }
        },
    };

    const strategies: Record<typeof messageType, Methods> = {
        TEXT: text,
        STICKER: sticker,
        AUDIO: audio,
        FILE: file,
        POLL: poll,
        CONTACT: contact,
        TEMPLATE: template,
    }

    return strategies[messageType];
}

async function handleSendMessage(thisCtx: ChatStore, messageType: 'TEXT',    payload: SendTextMessagePayload): Promise<void>;
async function handleSendMessage(thisCtx: ChatStore, messageType: 'STICKER', payload: SendStickerMessagePayload): Promise<void>;
async function handleSendMessage(thisCtx: ChatStore, messageType: 'AUDIO',   payload: SendAudioMessagePayload): Promise<void>;
async function handleSendMessage(thisCtx: ChatStore, messageType: 'POLL',    payload: SendPollMessagePayload): Promise<void>;
async function handleSendMessage(thisCtx: ChatStore, messageType: 'FILE',    payload: SendFileMessagePayload): Promise<void>;
async function handleSendMessage(thisCtx: ChatStore, messageType: 'CONTACT', payload: SendContactMessagePayload): Promise<void>;
async function handleSendMessage(thisCtx: ChatStore, messageType: 'TEMPLATE',payload: SendTemplateMessagePayload): Promise<void>;
async function handleSendMessage(
    thisCtx: ChatStore,
    messageType: 'TEXT'|'STICKER'|'AUDIO'|'POLL'|'FILE'|'CONTACT'|'TEMPLATE',
    payload: SendTextMessagePayload | SendStickerMessagePayload | SendAudioMessagePayload | SendPollMessagePayload | SendFileMessagePayload | SendContactMessagePayload | SendTemplateMessagePayload
): Promise<void> {

    // Renomeamos para strategySend para não dar erro
    const { handleSendEdit, hasContent, getNewMessage, getLastMessage, handleSendMessage: strategySend } = getSendMessageStrategy(messageType);

    if (thisCtx.isForwarding) thisCtx.cancelForwarding();

    if (!await handleSendEdit(thisCtx, payload)) return;
    if (!hasContent(thisCtx, payload)) return;

    const shouldSign = !payload.skipSignature && thisCtx.isSignatureEnabled && thisCtx.chatContext !== 'team';
    payload.shouldSign = shouldSign;
    const newMessage: Message = getNewMessage(payload);

    // Reenvio: a bolha já nasce com o id REAL da mensagem no servidor. Se ela ficasse
    // com o id provisório, um reenvio que falha degradaria a bolha e o clique seguinte
    // voltaria a duplicar (e, em mídia, perderia a URL). Também fecha a janela em que
    // `MENSAGEM.FALHA_ENVIO` chegava sem encontrar bolha com esse id.
    const idMensagemReenvio = (payload as { idMensagemReenvio?: string }).idMensagemReenvio;
    if (idMensagemReenvio) newMessage.id = idMensagemReenvio;

    const { idExternoResposta, replyToId, replyText, replyOwner } = thisCtx.ifReplyGetProperData()
    newMessage.replyToId  = replyToId;
    newMessage.replyText  = replyText;
    newMessage.replyOwner = replyOwner;

    // "Responder em particular", lido ANTES de limpar a referência. Uma citação por
    // vez: `setReplyMessage` já descarta a do grupo, e o servidor recusa
    // `respostaPara` junto com `respostaEmParticular` — a da conversa prevalece.
    const referenciaDoGrupo = thisCtx.activeChatId ? thisCtx.respostasParticulares[thisCtx.activeChatId] : undefined;
    const respostaParticular = idExternoResposta || replyToId ? undefined
        : payload.respostaParticularReenvio ?? (referenciaDoGrupo ? respostaParticularDoEnvio(referenciaDoGrupo) : undefined);
    if (respostaParticular) {
        Object.assign(newMessage, respostaParticular.citacao);
        newMessage.respostaEmParticular = respostaParticular.envio;
    }

    thisCtx.clearReplyMessage();
    // A referência do grupo serviu a ESTA mensagem; a próxima não responde a nada.
    if (thisCtx.activeChatId) thisCtx.limparRespostaParticular(thisCtx.activeChatId);

    // A atualização da LastMessage e UnreadCount
    if (thisCtx.activeChat) {
        thisCtx.activeChat.time = new Date().toISOString();
        thisCtx.activeChat.lastMessage = getLastMessage(payload);
        thisCtx.activeChat.unreadCount = 0;
        thisCtx.initialUnreadCount = 0;
    }

    const activeChatId = thisCtx.activeChatId!;

    if (!thisCtx.chatMapToMsgQueue.has(activeChatId))
        thisCtx.chatMapToMsgQueue.set(activeChatId, new SimpleQueue());

    const foundMsgQueue = thisCtx.chatMapToMsgQueue.get(activeChatId)

    const { promise, resolve, reject } = getPromiseWithResolvers();

    // Orquestração do envio com a Fila Local
    const asyncSendMessage = async () => {
        // 1. Adiciona na fila local e já exibe o balãozinho na tela!
        thisCtx.addPendingMessage(activeChatId, newMessage);
        // URL local (blob:) do anexo otimista. Capturada antes do envio porque o eco do
        // socket faz Object.assign na mensagem e sobrescreve `fileData.url`.
        const blobLocal = newMessage.fileData?.url?.startsWith('blob:') ? newMessage.fileData.url : undefined;

        try {
            // 2. Chama a estratégia específica de upload/envio (Text, File, Audio...)
            const { idMensagem, fileUrl, respostaVinculada } = await strategySend(payload, idExternoResposta, respostaParticular?.envio);

            // 3. Sucesso! Tira da fila de pendentes local e marca como 'sent'
            thisCtx.removePendingMessage(activeChatId, newMessage.id);

            // Canal que não anexou a citação do grupo: a mensagem SAIU, só que solta.
            // `undefined` (servidor sem o campo) não é recusa.
            const saiuSemCitacao = !!respostaParticular && respostaVinculada === false;

            if (activeChatId == thisCtx.activeChatId) {
                const msgToUpdate = thisCtx.messages.find(m => m.id === newMessage.id);
                if (msgToUpdate) {
                    msgToUpdate.id = idMensagem;
                    msgToUpdate.readStatus = 'sent';
                    msgToUpdate.uploadError = false;
                    if (fileUrl && msgToUpdate.fileData) msgToUpdate.fileData.url = fileUrl;
                    if (saiuSemCitacao) descartarCitacaoParticular(msgToUpdate);
                }
            }
            if (saiuSemCitacao) toast.warning('A resposta foi enviada sem a citação do grupo.');
            // Sem revoke o blob segura o arquivo inteiro na RAM até a aba fechar. Adiado porque
            // cópias da URL sobrevivem à troca pela remota: o visualizador de mídia guarda um
            // snapshot (url/galeria) e o selectChat/resync pode reinserir a bolha pendente vinda
            // da RAM/IndexedDB. Se algo ainda aponta para o blob, vaza (como antes) em vez de quebrar.
            if (blobLocal) setTimeout(() => {
                const viewer = thisCtx.uiStore.mediaToView;
                const emUso = thisCtx.messages.some(m => m.fileData?.url === blobLocal)
                    || [...thisCtx.localPendingMessages.values()].some(lista => lista.some(m => m.fileData?.url === blobLocal))
                    || viewer?.url === blobLocal
                    || !!viewer?.gallery?.some(g => g.url === blobLocal)
                    || thisCtx.messageToReply?.fileData?.url === blobLocal
                    || thisCtx.messageToEdit?.fileData?.url === blobLocal;
                if (!emUso) URL.revokeObjectURL(blobLocal);
            }, 30_000);
            resolve();
        } catch (e) {
            console.error("Falha no envio da mensagem", e);
            // 4. Falhou! Marca como erro na fila local para não sumir o balão vermelho
            thisCtx.markPendingMessageAsError(activeChatId, newMessage.id);
            // O reenvio pode ser RECUSADO pelo servidor (já reenviada, sem acesso ao
            // inbox, conteúdo não remontável). Sem isto o motivo só existia no console e
            // a bolha só ficava vermelha de novo, sem explicação.
            if (idMensagemReenvio) toast.error(extrairMensagemDeErro(e) ?? 'Não foi possível reenviar a mensagem.');
            // Resposta em particular RECUSADA (grupo de outro inbox, autor só com LID,
            // canal sem suporte...): o servidor explica em português. Erro local (mime,
            // upload) já avisou na própria estratégia.
            else if (respostaParticular && ehErroDoServidor(e)) toast.error(extrairMensagemDeErro(e) ?? 'Não foi possível enviar a resposta em particular.');
            reject(e);
        }
    }

    if (shouldSign && (messageType == 'AUDIO' || messageType == 'STICKER' || messageType == 'FILE')) {
        foundMsgQueue!.push(asyncSendMessage);
        await promise;
    } else {
        // `asyncSendMessage` engole o erro e rejeita `promise`; sem handler aqui, toda
        // falha de envio virava unhandled rejection.
        void promise.catch(() => { /* já tratado em asyncSendMessage */ });
        await asyncSendMessage();
    }
}

/**
 * Tipos que `retryFailedMessage` sabe reenviar. O botão só aparece para eles
 * (ver `chatUtils.ts`), e esta lista é a guarda no clique.
 */
const TIPOS_REENVIAVEIS: Message['type'][] = ['text', 'file', 'imagem', 'video'];

/**
 * Pendente cujo id já veio da API não pode virar uma segunda bolha: desde que o reenvio
 * passou a nascer com o id REAL, o registro pendente (RAM/IndexedDB) e a linha do
 * servidor compartilham o mesmo id, e a lista renderiza por `:key="msg.id"`.
 */
function semDuplicataDaApi(pendentes: Message[], daApi: Message[]): Message[] {
    const idsDaApi = new Set(daApi.map(m => m.id));
    return pendentes.filter(m => {
        if (!idsDaApi.has(m.id)) return true;
        // O servidor já tem a linha: o registro local é órfão (reenvio recusado que
        // ninguém clicou de novo). Sem apagar, ele ficaria invisível no IndexedDB para
        // sempre, relido em toda abertura de chat.
        void deletePendingMessageFromDB(m.id);
        return false;
    });
}

/** Corpo de erro do backend (`throw await response.json()` no FetchUtil), não um `Error` local. */
function ehErroDoServidor(e: unknown): boolean {
    return typeof (e as { statusCode?: unknown })?.statusCode === 'number';
}

/** Mensagem de erro do backend (`throw await response.json()` no FetchUtil). */
function extrairMensagemDeErro(e: unknown): string | undefined {
    const mensagem = (e as { message?: string | string[] })?.message;
    if (Array.isArray(mensagem)) return mensagem.join('. ');
    return typeof mensagem === 'string' ? mensagem : undefined;
}


const MIME_OVERRIDES: Record<string, string> = {
    ofx: 'application/x-ofx',
    kml: 'application/vnd.google-earth.kml+xml',
    dwg: 'application/acad',
};

// Build a reverse map: extension -> mime type from mime-db (browser-safe, no path.extname)
const _extToMime: Record<string, string> = {};
for (const [mime, entry] of Object.entries(mimeDb)) {
    if ((entry as any).extensions) {
        for (const ext of (entry as any).extensions) {
            if (!_extToMime[ext]) _extToMime[ext] = mime;
        }
    }
}

function tryToGetMimeFromExtension(fullFileNameOrPath: string) {
    const ext = fullFileNameOrPath.split('.').pop()?.toLowerCase();
    if (!ext) return null;
    if (MIME_OVERRIDES[ext]) return MIME_OVERRIDES[ext];
    return _extToMime[ext] || null;
}
